import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  Plus,
  Save,
  Trash2,
  RefreshCw,
  X,
  CalendarClock,
  Users,
  Layers,
  BarChart3,
  Activity,
  ListChecks,
  Settings2,
  Table2,
  Undo2,
  Redo2,
  FileText,
  Flag,
} from "lucide-react";
import { apiService } from "../services/api";
import { getWorkingDaysDates } from "../utils/dateUtils";
import InteractiveTimelineView from "./InteractiveTimelineView";
import { isPlannedInvoiceStatus, isSentInvoiceStatus } from "./BillingBatchManagement/billingMath";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Customized,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const SCENARIO_BASE_ID = "base";
const SCENARIO_META_KEY = "__scenario_meta";
const DEFAULT_EMPLOYEE_WEEKLY_HOURS = 35;
const DEFAULT_SCENARIO_AVAILABILITY_PERCENT = 100;
const DEFAULT_SCENARIO_EFFICIENCY_PERCENT = 100;
const DEFAULT_CHARGE_HOURS_PER_KEUR = 6.4;
const NON_PRODUCTION_PROJECT_FALLBACK_ID = 1;
const NON_PRODUCTION_PROJECT_NAME = "hors production";
const NON_PRODUCTION_PROJECT_NUMBER = "00";
const CLIENT_TYPE_NORMAL = "normal";
const CLIENT_TYPE_INTERNAL_INVESTMENT = "investissement_interne";
const CLIENT_TYPE_SEGMENTS = [
  {
    id: CLIENT_TYPE_NORMAL,
    label: "Clients normaux",
    shortLabel: "Normal",
    color: "#2563eb",
    forecastColor: "#93c5fd",
  },
  {
    id: CLIENT_TYPE_INTERNAL_INVESTMENT,
    label: "Investissement interne",
    shortLabel: "Interne",
    color: "#d97706",
    forecastColor: "#fbbf24",
  },
];
const EDITOR_HISTORY_LIMIT = 100;
const PILOTAGE_TABS = [
  {
    id: "production",
    label: "Production",
    description: "Charge, capacite et CA produit",
    icon: BarChart3,
  },
  {
    id: "statistics",
    label: "Stats",
    description: "CA/h, saisie des heures et efficacite",
    icon: Activity,
  },
  {
    id: "projects",
    label: "Projets",
    description: "Roadmap et scenario planning",
    icon: ListChecks,
  },
  {
    id: "accounting",
    label: "Compta",
    description: "Tresorerie, encaissements, depenses",
    icon: Table2,
  },
  {
    id: "invoices",
    label: "Factures",
    description: "Timeline des encaissements",
    icon: FileText,
  },
  {
    id: "scenario",
    label: "Scenario",
    description: "Hypotheses, equipe et filtres",
    icon: Settings2,
  },
];
const PROJECT_LINE_PALETTE = [
  "#0f172a",
  "#2563eb",
  "#059669",
  "#f59e0b",
  "#db2777",
  "#7c3aed",
  "#0ea5e9",
  "#84cc16",
  "#f97316",
  "#14b8a6",
];

const ARRAY_FIELD_CONFIG = {
  employeeOverrides: {
    key: "employee_id",
  },
  employeeAdditions: {
    key: "local_id",
    fallbackKey: "id",
  },
  employeeTimeOffs: {
    key: "local_id",
    fallbackKey: "id",
  },
  projectOverrides: {
    key: "project_id",
  },
  projectAdditions: {
    key: "local_id",
    fallbackKey: "id",
  },
  iterationOverrides: {
    key: "iteration_id",
  },
  iterationAdditions: {
    key: "local_id",
    fallbackKey: "id",
  },
};

const SCENARIO_PRIMITIVE_FIELDS = [
  "efficiencyPercent",
  "hoursPerDay",
  "factoredInvoiceIds",
];

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const keyOf = (value) => (value === undefined || value === null ? "" : String(value));

const createScenarioMeta = (overrides = {}) => ({
  parent_id: SCENARIO_BASE_ID,
  inherit_parent_changes: true,
  parent_snapshot: null,
  ...overrides,
});

const createStandaloneScenarioMeta = () =>
  createScenarioMeta({
    parent_id: SCENARIO_BASE_ID,
    inherit_parent_changes: false,
    parent_snapshot: null,
  });

const sanitizeScenarioItem = (item) => {
  if (!isPlainObject(item)) return item;
  const next = { ...item };
  delete next.__deleted;
  return next;
};

const getScenarioArrayIdentity = (field, item) => {
  const config = ARRAY_FIELD_CONFIG[field];
  if (!config || !item) return null;
  const direct = item[config.key];
  if (direct !== undefined && direct !== null && direct !== "") {
    return `${config.key}:${String(direct)}`;
  }
  if (config.fallbackKey) {
    const fallback = item[config.fallbackKey];
    if (fallback !== undefined && fallback !== null && fallback !== "") {
      return `${config.fallbackKey}:${String(fallback)}`;
    }
  }
  return null;
};

const mergeScenarioArrayField = (field, baseItems = [], localItems = []) => {
  const orderedKeys = [];
  const mergedByKey = new Map();

  baseItems.forEach((item, index) => {
    const identity = getScenarioArrayIdentity(field, item) || `base:${index}`;
    orderedKeys.push(identity);
    mergedByKey.set(identity, sanitizeScenarioItem(item));
  });

  (localItems || []).forEach((item, index) => {
    const identity = getScenarioArrayIdentity(field, item) || `local:${index}`;
    if (item?.__deleted) {
      mergedByKey.delete(identity);
      return;
    }
    if (!orderedKeys.includes(identity)) orderedKeys.push(identity);
    const previous = mergedByKey.get(identity) || {};
    mergedByKey.set(identity, {
      ...previous,
      ...sanitizeScenarioItem(item),
    });
  });

  return orderedKeys
    .filter((identity) => mergedByKey.has(identity))
    .map((identity) => mergedByKey.get(identity));
};

const normalizeScenarioPayload = (payload) => {
  const rawPayload = isPlainObject(payload) ? payload : {};
  const meta = createScenarioMeta(rawPayload[SCENARIO_META_KEY] || {});
  const localConfig = Object.entries(rawPayload).reduce((acc, [key, value]) => {
    if (key === SCENARIO_META_KEY) return acc;
    acc[key] = value;
    return acc;
  }, {});
  return { meta, localConfig };
};

const mergeScenarioConfigs = (baseConfig = createDefaultScenarioConfig(), localConfig = {}) => {
  const merged = {
    ...createDefaultScenarioConfig(),
    ...baseConfig,
  };

  SCENARIO_PRIMITIVE_FIELDS.forEach((field) => {
    if (localConfig[field] !== undefined) {
      merged[field] = localConfig[field];
    }
  });

  Object.keys(ARRAY_FIELD_CONFIG).forEach((field) => {
    merged[field] = mergeScenarioArrayField(field, baseConfig[field] || [], localConfig[field] || []);
  });

  return merged;
};

const buildScenarioPayload = (localConfig, meta) => {
  const payload = { ...localConfig };
  payload[SCENARIO_META_KEY] = meta;
  return payload;
};

const createDefaultScenarioConfig = () => ({
  efficiencyPercent: DEFAULT_SCENARIO_EFFICIENCY_PERCENT,
  hoursPerDay: 8,
  factoredInvoiceIds: [],
  employeeOverrides: [],
  employeeAdditions: [],
  employeeTimeOffs: [],
  projectOverrides: [],
  projectAdditions: [],
  iterationOverrides: [],
  iterationAdditions: [],
});

const getScenarioEfficiencyMultiplier = (config = {}) => {
  const value = Number(config?.efficiencyPercent);
  const percent = Number.isFinite(value) && value >= 0 ? value : DEFAULT_SCENARIO_EFFICIENCY_PERCENT;
  return percent / 100;
};

const cloneJson = (value) => JSON.parse(JSON.stringify(value));

const normalizeScenarioConfig = (config = {}) => {
  const source = isPlainObject(config) ? config : {};
  const base = createDefaultScenarioConfig();
  const sanitizedSource = { ...source };
  delete sanitizedSource.caPerHourEur;
  delete sanitizedSource.globalAvailabilityPercent;
  return {
    ...base,
    ...sanitizedSource,
    factoredInvoiceIds: Array.isArray(source.factoredInvoiceIds) ? source.factoredInvoiceIds : [],
    employeeOverrides: Array.isArray(source.employeeOverrides) ? source.employeeOverrides : [],
    employeeAdditions: Array.isArray(source.employeeAdditions) ? source.employeeAdditions : [],
    employeeTimeOffs: Array.isArray(source.employeeTimeOffs) ? source.employeeTimeOffs : [],
    projectOverrides: Array.isArray(source.projectOverrides) ? source.projectOverrides : [],
    projectAdditions: Array.isArray(source.projectAdditions) ? source.projectAdditions : [],
    iterationOverrides: Array.isArray(source.iterationOverrides) ? source.iterationOverrides : [],
    iterationAdditions: Array.isArray(source.iterationAdditions) ? source.iterationAdditions : [],
  };
};

const buildEditorSnapshot = (snapshot = {}, fallbackYear) => {
  const parsedYear = Number(snapshot.year);
  return {
    year: Number.isFinite(parsedYear) ? parsedYear : fallbackYear,
    scenarioName: String(snapshot.scenarioName || ""),
    scenarioConfig: cloneJson(normalizeScenarioConfig(snapshot.scenarioConfig)),
  };
};

const serializeEditorSnapshot = (snapshot, fallbackYear) =>
  JSON.stringify(buildEditorSnapshot(snapshot, fallbackYear));

const isEditableElement = (element) => {
  if (!element) return false;
  const tagName = String(element.tagName || "").toUpperCase();
  return (
    element.isContentEditable ||
    tagName === "INPUT" ||
    tagName === "TEXTAREA" ||
    tagName === "SELECT"
  );
};

const buildRoadmapDraft = (year, overrides = {}) => ({
  id: null,
  source: "addition",
  item_type: "iteration",
  objective_id: null,
  project_id: "",
  project_mode: "existing",
  new_project_name: "",
  new_project_number: "",
  name: "",
  description: "",
  start_date: `${year}-01-01`,
  end_date: `${year}-01-15`,
  forecast_ca_eur: "",
  forecast_purchase_eur: "",
  forecast_time_hours: "",
  ...overrides,
});

const isRoadmapMilestoneItem = (item) =>
  item?.__roadmapType === "milestone" || item?.is_milestone === true;

const formatProjectOptionLabel = (project) => {
  if (!project) return "";
  const numberLabel = project.project_number ? ` (${project.project_number})` : "";
  const sourceLabel = project.__source === "scenario_addition" ? " [scenario]" : "";
  return `${project.name || "Projet"}${numberLabel}${sourceLabel}`;
};

const getProjectClientKey = (project) => {
  const clientId = project?.client_id ?? project?.client?.id ?? project?.clientId;
  if (clientId !== undefined && clientId !== null && clientId !== "") {
    return String(clientId);
  }
  const clientName = String(project?.client_name || project?.client?.name || "").trim();
  return clientName ? `name:${clientName.toLowerCase()}` : "unassigned";
};

const getProjectClientName = (project) => {
  const clientName = String(project?.client_name || project?.client?.name || "").trim();
  return clientName || "Sans client";
};

const normalizeClientType = (value) =>
  value === CLIENT_TYPE_INTERNAL_INVESTMENT ? CLIENT_TYPE_INTERNAL_INVESTMENT : CLIENT_TYPE_NORMAL;

const getProjectClientType = (project) =>
  normalizeClientType(project?.client_type ?? project?.client?.client_type);

const createClientTypeBuckets = () =>
  CLIENT_TYPE_SEGMENTS.reduce((acc, segment) => {
    acc[segment.id] = 0;
    return acc;
  }, {});

const isNonProductionProject = (project) => {
  const projectName = String(project?.name || "").trim().toLowerCase();
  const projectNumber = String(project?.project_number ?? project?.number ?? "").trim();
  return (
    projectName === NON_PRODUCTION_PROJECT_NAME ||
    projectNumber === NON_PRODUCTION_PROJECT_NUMBER
  );
};

const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parts = String(value).split("-");
  if (parts.length < 3) return null;
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const toOptionalNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNumberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getMonthKeyForDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const toDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const formatDateShort = (value) => {
  const date = parseDate(value);
  if (!date) return "-";
  return new Intl.DateTimeFormat("fr-FR").format(date);
};

const formatInvoiceStatus = (status) => (status === "Prevu" ? "Prévu" : "Envoyée");

const getDateDelayDays = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  return Math.round((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
};

const isInvoiceFactorable = (issueDate, clientPaymentDate) => {
  const delayDays = getDateDelayDays(issueDate, clientPaymentDate);
  return Number.isFinite(delayDays) && delayDays > 0;
};

const isPaymentAtReceiptTerms = (paymentTerms) => {
  const normalized = String(paymentTerms || "").toLowerCase();
  return normalized.includes("ception") || normalized.includes("rception");
};

const estimateIssueDateFromPaymentTerms = (paymentDateValue, paymentTerms) => {
  const paymentDate = parseDate(paymentDateValue);
  if (!paymentDate) return null;
  const normalized = String(paymentTerms || "").toLowerCase();
  if (isPaymentAtReceiptTerms(normalized)) return new Date(paymentDate);

  const delayDays = normalized.includes("30 jours nets") ? 30 : 45;
  const estimatedIssueDate = new Date(paymentDate);
  estimatedIssueDate.setDate(estimatedIssueDate.getDate() - delayDays);
  return estimatedIssueDate;
};

const getInvoiceIssueDateInfo = (event, paymentDateValue) => {
  const explicitIssueDate = parseDate(event?.issue_date);
  if (explicitIssueDate) {
    return { date: explicitIssueDate, isEstimated: false };
  }
  const estimatedIssueDate = estimateIssueDateFromPaymentTerms(paymentDateValue, event?.payment_terms);
  return { date: estimatedIssueDate, isEstimated: Boolean(estimatedIssueDate) };
};

const getExplicitIterationChargeHours = (iteration) => {
  const explicit = iteration?.forecast_time_hours;
  if (explicit !== null && explicit !== undefined && explicit !== "") {
    const parsed = Number(explicit);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
};

const computeChargeHours = (iteration, caActualTotal, caForecastTotal, expenseTotal) => {
  // Budget heures saisi sur l'iteration (modele decouple : plus de budget issu des lignes)
  const explicit = getExplicitIterationChargeHours(iteration);
  if (explicit !== null) return explicit;
  const baseCa = caForecastTotal;
  const net = baseCa - expenseTotal;
  if (!Number.isFinite(net) || net <= 0) return 0;
  return (net / 1000) * DEFAULT_CHARGE_HOURS_PER_KEUR;
};

const getIterationProductionTarget = (iteration) => {
  return Math.max(0, Number(iteration?.forecast_ca_eur || 0));
};

const getIterationLayoutKey = (iteration) =>
  `${iteration.__source || "base"}:${iteration.local_id || iteration.id}`;

const buildProjectRoadmapLayout = (projects, iterations, rowHeight, laneGap) => {
  const iterationsByProject = new Map();
  iterations.forEach((iteration) => {
    if (iteration?.project_id === undefined || iteration?.project_id === null) return;
    const key = String(iteration.project_id);
    if (!iterationsByProject.has(key)) iterationsByProject.set(key, []);
    iterationsByProject.get(key).push(iteration);
  });

  const rows = [];
  const laneIndexByIteration = new Map();
  let nextGlobalLaneIndex = 0;

  projects.forEach((project) => {
    const projectIterations = (iterationsByProject.get(String(project.id)) || [])
      .slice()
      .sort((left, right) => {
        const leftStart = parseDate(left.start_date);
        const rightStart = parseDate(right.start_date);
        if (leftStart && rightStart && leftStart.getTime() !== rightStart.getTime()) {
          return leftStart - rightStart;
        }
        const leftEnd = parseDate(left.end_date);
        const rightEnd = parseDate(right.end_date);
        if (leftEnd && rightEnd && leftEnd.getTime() !== rightEnd.getTime()) {
          return leftEnd - rightEnd;
        }
        return String(left.name || "").localeCompare(String(right.name || ""));
      });

    const subLaneEndDates = [];
    projectIterations.forEach((iteration) => {
      const start = parseDate(iteration.start_date);
      const end = parseDate(iteration.end_date);
      if (!start || !end) return;
      let subLaneIndex = subLaneEndDates.findIndex((laneEndDate) => laneEndDate < start);
      if (subLaneIndex === -1) {
        subLaneIndex = subLaneEndDates.length;
        subLaneEndDates.push(end);
      } else {
        subLaneEndDates[subLaneIndex] = end;
      }
      laneIndexByIteration.set(getIterationLayoutKey(iteration), nextGlobalLaneIndex + subLaneIndex);
    });

    const laneCount = Math.max(1, subLaneEndDates.length);
    const milestoneCount = projectIterations.filter(isRoadmapMilestoneItem).length;
    rows.push({
      ...project,
      laneIndex: nextGlobalLaneIndex,
      laneCount,
      iterationCount: projectIterations.length - milestoneCount,
      milestoneCount,
      visualHeight: rowHeight * laneCount + laneGap * Math.max(0, laneCount - 1),
    });
    nextGlobalLaneIndex += laneCount;
  });

  return {
    rows,
    laneIndexByIteration,
  };
};

const formatMonthLabel = (date) =>
  date.toLocaleDateString("fr-FR", { month: "short" }).replace(".", "");

const buildMonthRanges = (year) =>
  Array.from({ length: 12 }, (_, idx) => {
    const start = new Date(year, idx, 1);
    const end = new Date(year, idx + 1, 0);
    return {
      key: `${year}-${String(idx + 1).padStart(2, "0")}`,
      label: formatMonthLabel(start),
      start,
      end,
    };
  });

const CSV_SEPARATOR = ";";

const formatCsvNumber = (value) => {
  const numeric = Number(value);
  return new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(Number.isFinite(numeric) ? numeric : 0);
};

const formatCsvCell = (value) =>
  typeof value === "number" ? formatCsvNumber(value) : value ?? "";

const escapeCsvCell = (value) => {
  const text = String(formatCsvCell(value));
  if (!/[;"\n\r]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
};

const buildCsvRow = (values) => values.map(escapeCsvCell).join(CSV_SEPARATOR);

const downloadTextFile = (filename, content, type = "text/csv;charset=utf-8") => {
  const blob = new Blob(["\ufeff", content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const sanitizeFilenamePart = (value) => {
  const sanitized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "");
  return sanitized || "scenario";
};

const buildPointedHoursByMonth = (hoursById = {}, monthRanges = [], year) => {
  const totalsByMonth = monthRanges.reduce((acc, month) => {
    acc[month.key] = 0;
    return acc;
  }, {});

  Object.values(hoursById || {}).forEach((response) => {
    Object.entries(response?.hours_by_day || {}).forEach(([dateKey, projectsById]) => {
      const day = parseDate(dateKey);
      if (!day || day.getFullYear() !== year) return;
      const monthKey = getMonthKeyForDate(day);
      if (!(monthKey in totalsByMonth)) return;

      Object.values(projectsById || {}).forEach((rawHours) => {
        const hours = Number(rawHours || 0);
        if (Number.isFinite(hours) && hours > 0) {
          totalsByMonth[monthKey] += hours;
        }
      });
    });
  });

  return totalsByMonth;
};

const isDateInRange = (day, start, end) => {
  if (!start || !end) return false;
  return day >= start && day <= end;
};

const getEmployeeWeekdayIndex = (day) => {
  const jsDay = day.getDay();
  return jsDay === 0 ? 6 : jsDay - 1;
};

const getEmployeeConfiguredWorkDays = (employee) =>
  Array.isArray(employee?.weekly_work_days) && employee.weekly_work_days.length
    ? employee.weekly_work_days.map(Number)
    : [0, 1, 2, 3, 4];

const employeeWorksOnDay = (employee, day) => {
  const configuredDays = getEmployeeConfiguredWorkDays(employee);
  return configuredDays.includes(getEmployeeWeekdayIndex(day));
};

const getEmployeeWeeklyHours = (employee, fallbackDailyHours = 8) => {
  const configuredHours = Number(employee?.weekly_hours);
  if (Number.isFinite(configuredHours) && configuredHours > 0) {
    return configuredHours;
  }
  return getEmployeeConfiguredWorkDays(employee).length * Number(fallbackDailyHours || 8);
};

const getEmployeeDailyCapacityHours = (employee, fallbackDailyHours = 8) => {
  const workDayCount = getEmployeeConfiguredWorkDays(employee).length || 5;
  return getEmployeeWeeklyHours(employee, fallbackDailyHours) / workDayCount;
};

const getEmployeeProductionRatePercent = (employee) => {
  const productionRate = Number(employee?.production_rate ?? 100);
  if (!Number.isFinite(productionRate)) return 100;
  return Math.min(100, Math.max(0, productionRate));
};

const getEmployeeProductionRateMultiplier = (employee) =>
  getEmployeeProductionRatePercent(employee) / 100;

const MonthlyCapacityTicks = ({ data, xAxisMap, yAxisMap }) => {
  const xAxis = xAxisMap?.[Object.keys(xAxisMap || {})[0]];
  const yAxis = yAxisMap?.[Object.keys(yAxisMap || {})[0]];
  if (!xAxis || !yAxis || !xAxis.scale || !yAxis.scale) return null;
  const band = typeof xAxis.scale.bandwidth === "function" ? xAxis.scale.bandwidth() : 0;
  return (
    <g>
      {data.map((entry) => {
        const x = xAxis.scale(entry.label);
        if (x === undefined || x === null) return null;
        const y = yAxis.scale(entry.capacity);
        if (y === undefined || y === null) return null;
        const lineWidth = band > 0 ? band : 0;
        const x1 = x;
        const x2 = x + lineWidth;
        return (
          <line
            key={entry.key}
            x1={x1}
            x2={x2}
            y1={y}
            y2={y}
            stroke="#111827"
            strokeWidth={2}
            strokeDasharray="4 3"
          />
        );
      })}
    </g>
  );
};

const eventTypeMeta = {
  employee_arrival: {
    color: "#16a34a",
    label: "Arrivee employe",
    shortLabel: "AE",
  },
  employee_departure: {
    color: "#ef4444",
    label: "Depart employe",
    shortLabel: "DE",
  },
  reinforcement_arrival: {
    color: "#16a34a",
    label: "Arrivee renfort",
    shortLabel: "AR",
  },
  reinforcement_departure: {
    color: "#f59e0b",
    label: "Depart renfort",
    shortLabel: "DR",
  },
};

const splitAxisLabel = (value, maxChars = 24, maxLines = 3) => {
  const words = String(value || "").split(/\s+/).filter(Boolean);
  if (!words.length) return [""];
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length <= maxChars) {
      current = next;
      return;
    }
    if (current) lines.push(current);
    current = word;
  });
  if (current) lines.push(current);
  if (lines.length <= maxLines) return lines;
  const visible = lines.slice(0, maxLines);
  visible[maxLines - 1] = `${visible[maxLines - 1].replace(/\.+$/, "")}...`;
  return visible;
};

const ProjectAxisTick = ({ x, y, payload }) => {
  const label = String(payload?.value || "");
  const lines = splitAxisLabel(label);
  const lineHeight = 12;
  const firstLineOffset = -((lines.length - 1) * lineHeight) / 2;
  return (
    <g transform={`translate(${x},${y})`}>
      <title>{label}</title>
      <text
        x={0}
        y={firstLineOffset}
        textAnchor="end"
        fill="#475569"
        fontSize={11}
      >
        {lines.map((line, index) => (
          <tspan key={`${line}-${index}`} x={0} dy={index === 0 ? 0 : lineHeight}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const MonthlyEventMarkers = ({ data, xAxisMap, yAxisMap }) => {
  const xAxis = xAxisMap?.[Object.keys(xAxisMap || {})[0]];
  const yAxis = yAxisMap?.[Object.keys(yAxisMap || {})[0]];
  if (!xAxis || !yAxis || !xAxis.scale || !yAxis.scale) return null;
  const band = typeof xAxis.scale.bandwidth === "function" ? xAxis.scale.bandwidth() : 0;
  const range = typeof yAxis.scale.range === "function" ? yAxis.scale.range() : [0, 0];
  const top = Math.min(...range);
  const baseY = top + 10;
  return (
    <g>
      {data.map((entry) => {
        if (!entry?.events?.length) return null;
        const x = xAxis.scale(entry.label);
        if (x === undefined || x === null) return null;
        const centerX = x + (band > 0 ? band / 2 : 0);
        return entry.events.map((event, index) => {
          const meta = eventTypeMeta[event.type] || {
            color: "#0f172a",
            label: "Evenement",
            shortLabel: "EV",
          };
          const y = baseY + index * 10;
          const title = `${meta.label}: ${event.name || "Sans nom"} (${event.dateLabel || ""})`;
          return (
            <g key={`${entry.key}-${event.id || index}`}>
              <title>{title}</title>
              <circle cx={centerX} cy={y} r={3} fill={meta.color} />
            </g>
          );
        });
      })}
    </g>
  );
};

const Modal = ({ open, title, onClose, children, maxWidth = "max-w-4xl" }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
        role="button"
        tabIndex={-1}
        aria-label="Fermer la fenetre"
      />
      <div className={`relative w-full ${maxWidth} max-h-[85vh] overflow-hidden rounded-xl bg-white shadow-xl`}>
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[calc(85vh-90px)] overflow-y-auto px-5 py-4">
          {children}
        </div>
      </div>
    </div>
  );
};

const PilotagePage = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYearRaw] = useState(currentYear);
  const [projects, setProjects] = useState([]);
  const [billingCashEvents, setBillingCashEvents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeeHoursById, setEmployeeHoursById] = useState({});
  const [employeeHoursRangeKey, setEmployeeHoursRangeKey] = useState(null);
  const [employeeHoursLoading, setEmployeeHoursLoading] = useState(false);
  const [employeeHoursError, setEmployeeHoursError] = useState(null);
  const [iterations, setIterations] = useState([]);
  const [milestoneObjectives, setMilestoneObjectives] = useState([]);
  const [slots, setSlots] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [activeScenarioId, setActiveScenarioId] = useState(SCENARIO_BASE_ID);
  const [scenarioName, setScenarioNameRaw] = useState("");
  const [scenarioConfig, setScenarioConfigRaw] = useState(createDefaultScenarioConfig);
  const [scenarioPast, setScenarioPast] = useState([]);
  const [scenarioFuture, setScenarioFuture] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [selectedMonthKey, setSelectedMonthKey] = useState(null);
  const [roadmapIterations, setRoadmapIterations] = useState([]);
  const [roadmapEditorOpen, setRoadmapEditorOpen] = useState(false);
  const [roadmapDraftError, setRoadmapDraftError] = useState(null);
  const [roadmapDraft, setRoadmapDraft] = useState(() => buildRoadmapDraft(currentYear));
  const [showExpenseChart, setShowExpenseChart] = useState(false);
  const [showAcquiredForecastSplit, setShowAcquiredForecastSplit] = useState(true);
  const [showDetailedImpact, setShowDetailedImpact] = useState(false);
  const [separateInternalInvestment, setSeparateInternalInvestment] = useState(false);
  const showCapacityRevenueProjection = true;
  const [exportingCsv, setExportingCsv] = useState(false);
  const [activePilotageTab, setActivePilotageTab] = useState("production");
  const [hoveredInvoiceTimelineItem, setHoveredInvoiceTimelineItem] = useState(null);
  const roadmapRowHeight = 56;
  const roadmapLaneGap = 12;
  const roadmapTopOffset = 24;
  const roadmapHeaderOffset = 60;
  const yearRef = useRef(year);
  const scenarioNameRef = useRef(scenarioName);
  const scenarioConfigRef = useRef(scenarioConfig);
  const scenarioEfficiencyMultiplier = useMemo(
    () => getScenarioEfficiencyMultiplier(scenarioConfig),
    [scenarioConfig]
  );

  useEffect(() => {
    yearRef.current = year;
  }, [year]);

  useEffect(() => {
    scenarioNameRef.current = scenarioName;
  }, [scenarioName]);

  useEffect(() => {
    scenarioConfigRef.current = scenarioConfig;
  }, [scenarioConfig]);

  const applyEditorSnapshot = useCallback(
    (snapshot) => {
      const normalized = buildEditorSnapshot(snapshot, currentYear);
      setYearRaw(normalized.year);
      setScenarioNameRaw(normalized.scenarioName);
      setScenarioConfigRaw(normalized.scenarioConfig);
    },
    [currentYear]
  );

  const getCurrentEditorSnapshot = useCallback(
    () =>
      buildEditorSnapshot(
        {
          year: yearRef.current,
          scenarioName: scenarioNameRef.current,
          scenarioConfig: scenarioConfigRef.current,
        },
        currentYear
      ),
    [currentYear]
  );

  const resetEditorHistory = useCallback(
    (snapshot) => {
      setScenarioPast([]);
      setScenarioFuture([]);
      applyEditorSnapshot(snapshot);
    },
    [applyEditorSnapshot]
  );

  const pushEditorSnapshot = useCallback(
    (updater) => {
      const currentSnapshot = getCurrentEditorSnapshot();
      const nextSnapshot = buildEditorSnapshot(updater(currentSnapshot), currentYear);
      if (
        serializeEditorSnapshot(currentSnapshot, currentYear) ===
        serializeEditorSnapshot(nextSnapshot, currentYear)
      ) {
        return;
      }
      setScenarioPast((prev) => [
        ...prev.slice(-(EDITOR_HISTORY_LIMIT - 1)),
        currentSnapshot,
      ]);
      setScenarioFuture([]);
      applyEditorSnapshot(nextSnapshot);
    },
    [applyEditorSnapshot, currentYear, getCurrentEditorSnapshot]
  );

  const setScenarioConfig = useCallback(
    (valueOrUpdater, options = {}) => {
      const { trackHistory = true } = options;
      if (!trackHistory) {
        setScenarioConfigRaw((prev) =>
          normalizeScenarioConfig(
            typeof valueOrUpdater === "function" ? valueOrUpdater(prev) : valueOrUpdater
          )
        );
        return;
      }
      pushEditorSnapshot((currentSnapshot) => ({
        ...currentSnapshot,
        scenarioConfig: normalizeScenarioConfig(
          typeof valueOrUpdater === "function"
            ? valueOrUpdater(currentSnapshot.scenarioConfig)
            : valueOrUpdater
        ),
      }));
    },
    [pushEditorSnapshot]
  );

  const setScenarioName = useCallback(
    (valueOrUpdater, options = {}) => {
      const { trackHistory = true } = options;
      if (!trackHistory) {
        setScenarioNameRaw((prev) =>
          typeof valueOrUpdater === "function" ? valueOrUpdater(prev) : valueOrUpdater
        );
        return;
      }
      pushEditorSnapshot((currentSnapshot) => ({
        ...currentSnapshot,
        scenarioName:
          typeof valueOrUpdater === "function"
            ? valueOrUpdater(currentSnapshot.scenarioName)
            : valueOrUpdater,
      }));
    },
    [pushEditorSnapshot]
  );

  const setYear = useCallback(
    (valueOrUpdater, options = {}) => {
      const { trackHistory = true } = options;
      if (!trackHistory) {
        setYearRaw((prev) => (typeof valueOrUpdater === "function" ? valueOrUpdater(prev) : valueOrUpdater));
        return;
      }
      pushEditorSnapshot((currentSnapshot) => ({
        ...currentSnapshot,
        year:
          typeof valueOrUpdater === "function"
            ? valueOrUpdater(currentSnapshot.year)
            : valueOrUpdater,
      }));
    },
    [pushEditorSnapshot]
  );

  const monthRanges = useMemo(() => buildMonthRanges(year), [year]);
  const employeeIds = useMemo(
    () =>
      employees
        .filter((employee) => employee?.id !== undefined && employee?.id !== null)
        .map((employee) => employee.id),
    [employees]
  );
  const employeeHoursCurrentRangeKey = `${year}-01-01:${year}-12-31`;
  const fetchEmployeeHoursForCurrentYear = useCallback(async () => {
    const startDate = `${year}-01-01`;
    const endDate = `${year}-12-31`;
    const responses = await Promise.all(
      employeeIds.map((employeeId) =>
        apiService
          .get(`/hours/employee/${employeeId}?start_date=${startDate}&end_date=${endDate}`)
          .then((data) => ({ employeeId, data, failed: false }))
          .catch((error) => ({ employeeId, error, failed: true }))
      )
    );
    const nextHours = {};
    const failures = [];
    responses.forEach((response) => {
      if (response.failed) {
        failures.push(response.employeeId);
        return;
      }
      nextHours[String(response.employeeId)] = response.data;
    });
    return { nextHours, failures };
  }, [employeeIds, year]);

  useEffect(() => {
    const loadBaseData = async () => {
      try {
        setLoading(true);
        const [projectsData, employeesData, scenarioData, cashEventsData, slotsData] = await Promise.all([
          apiService.get("/projects"),
          apiService.get("/employees"),
          apiService.get("/pilotage/scenarios"),
          apiService.get("/billing-batches/cash-events").catch(() => []),
          apiService.get("/slots").catch(() => []),
        ]);
        setProjects(Array.isArray(projectsData) ? projectsData : []);
        setEmployees(Array.isArray(employeesData) ? employeesData : []);
        setScenarios(Array.isArray(scenarioData) ? scenarioData : []);
        setBillingCashEvents(Array.isArray(cashEventsData) ? cashEventsData : []);
        setSlots(
          Array.isArray(slotsData)
            ? [...slotsData].sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
            : []
        );
      } catch (err) {
        console.error("Erreur chargement pilotage:", err);
      } finally {
        setLoading(false);
      }
    };
    loadBaseData();
  }, []);

  useEffect(() => {
    const loadIterations = async () => {
      if (!projects.length) return;
      try {
        const responses = await Promise.all(
          projects.map((project) =>
            apiService.get(`/projects/${project.id}/iterations`).catch(() => [])
          )
        );
        setIterations(responses.flat());
      } catch (err) {
        console.error("Erreur chargement iterations:", err);
      }
    };
    loadIterations();
  }, [projects]);

  useEffect(() => {
    const loadMilestoneObjectives = async () => {
      if (!projects.length) {
        setMilestoneObjectives([]);
        return;
      }
      try {
        const responses = await Promise.all(
          projects.map((project) =>
            apiService.get(`/projects/${project.id}/objectives`).catch(() => [])
          )
        );
        const milestones = responses
          .flat()
          .filter((objective) => objective?.is_milestone && objective?.milestone_date);
        setMilestoneObjectives(milestones);
      } catch (err) {
        console.error("Erreur chargement jalons:", err);
      }
    };
    loadMilestoneObjectives();
  }, [projects]);

  useEffect(() => {
    if (activePilotageTab !== "statistics") return;
    if (!employeeIds.length) {
      setEmployeeHoursById({});
      setEmployeeHoursRangeKey(null);
      setEmployeeHoursError(null);
      setEmployeeHoursLoading(false);
      return;
    }

    let cancelled = false;
    const loadEmployeeHours = async () => {
      setEmployeeHoursLoading(true);
      setEmployeeHoursError(null);
      setEmployeeHoursById({});
      setEmployeeHoursRangeKey(null);
      try {
        const { nextHours, failures } = await fetchEmployeeHoursForCurrentYear();
        if (cancelled) return;
        setEmployeeHoursById(nextHours);
        setEmployeeHoursRangeKey(employeeHoursCurrentRangeKey);
        setEmployeeHoursError(
          failures.length
            ? `Heures indisponibles pour ${failures.length} employe(s).`
            : null
        );
      } catch (err) {
        if (!cancelled) {
          console.error("Erreur chargement heures employees:", err);
          setEmployeeHoursById({});
          setEmployeeHoursError("Impossible de charger les heures saisies.");
        }
      } finally {
        if (!cancelled) {
          setEmployeeHoursLoading(false);
        }
      }
    };

    loadEmployeeHours();
    return () => {
      cancelled = true;
    };
  }, [
    activePilotageTab,
    employeeHoursCurrentRangeKey,
    employeeIds,
    fetchEmployeeHoursForCurrentYear,
  ]);

  const getEmployeeHoursForExport = useCallback(async () => {
    if (!employeeIds.length) return { hoursById: {}, failures: [] };
    const hasCurrentCache =
      employeeHoursRangeKey === employeeHoursCurrentRangeKey &&
      employeeIds.every((employeeId) => employeeHoursById[String(employeeId)]);
    if (hasCurrentCache) return { hoursById: employeeHoursById, failures: [] };

    setEmployeeHoursLoading(true);
    setEmployeeHoursError(null);
    try {
      const { nextHours, failures } = await fetchEmployeeHoursForCurrentYear();
      setEmployeeHoursById(nextHours);
      setEmployeeHoursRangeKey(employeeHoursCurrentRangeKey);
      setEmployeeHoursError(
        failures.length
          ? `Heures indisponibles pour ${failures.length} employe(s).`
          : null
      );
      return { hoursById: nextHours, failures };
    } catch (err) {
      console.error("Erreur chargement heures export CSV:", err);
      setEmployeeHoursError("Impossible de charger les heures saisies.");
      throw err;
    } finally {
      setEmployeeHoursLoading(false);
    }
  }, [
    employeeHoursById,
    employeeHoursCurrentRangeKey,
    employeeHoursRangeKey,
    employeeIds,
    fetchEmployeeHoursForCurrentYear,
  ]);

  useEffect(() => {
    if (!monthRanges.length) return;
    const hasSelected = monthRanges.some((month) => month.key === selectedMonthKey);
    if (!hasSelected) {
      setSelectedMonthKey(monthRanges[0].key);
    }
  }, [monthRanges, selectedMonthKey]);

  const projectExclusions = useMemo(() => {
    const exclusions = new Set();
    (scenarioConfig.projectOverrides || []).forEach((override) => {
      if (override && override.project_id && override.include === false) {
        exclusions.add(keyOf(override.project_id));
      }
    });
    return exclusions;
  }, [scenarioConfig.projectOverrides]);

  const scenarioProjectAdditions = useMemo(() => {
    return (scenarioConfig.projectAdditions || []).map((project, index) => {
      const localId = project?.local_id || project?.id || `scenario-project-${index}`;
      return {
        ...project,
        id: localId,
        local_id: localId,
        status: project?.status || "proposition",
        __source: "scenario_addition",
      };
    });
  }, [scenarioConfig.projectAdditions]);

  const availableProjects = useMemo(() => {
    const byId = new Map();
    projects.forEach((project) => {
      if (project?.id === undefined || project?.id === null) return;
      byId.set(String(project.id), project);
    });
    scenarioProjectAdditions.forEach((project) => {
      if (project?.id === undefined || project?.id === null) return;
      byId.set(String(project.id), project);
    });
    return Array.from(byId.values());
  }, [projects, scenarioProjectAdditions]);

  const scenarioResolutionMap = useMemo(() => {
    const byId = new Map(scenarios.map((scenario) => [String(scenario.id), scenario]));
    const cache = new Map();

    const resolveBaseConfig = (meta, trail) => {
      if (meta.inherit_parent_changes === false && isPlainObject(meta.parent_snapshot)) {
        return mergeScenarioConfigs(createDefaultScenarioConfig(), meta.parent_snapshot);
      }
      if (!meta.parent_id || String(meta.parent_id) === SCENARIO_BASE_ID) {
        return createDefaultScenarioConfig();
      }
      const parentId = String(meta.parent_id);
      if (trail.has(parentId)) {
        return createDefaultScenarioConfig();
      }
      const parentResolution = resolveScenario(parentId, new Set([...trail, parentId]));
      return parentResolution?.effectiveConfig || createDefaultScenarioConfig();
    };

    const resolveScenario = (scenarioId, trail = new Set([scenarioId])) => {
      if (cache.has(String(scenarioId))) return cache.get(String(scenarioId));
      const scenario = byId.get(String(scenarioId));
      if (!scenario) return null;
      const { meta, localConfig } = normalizeScenarioPayload(scenario.payload);
      const baseConfig = resolveBaseConfig(meta, trail);
      const effectiveConfig = mergeScenarioConfigs(baseConfig, localConfig);
      const resolution = { scenario, meta, localConfig, baseConfig, effectiveConfig };
      cache.set(String(scenarioId), resolution);
      return resolution;
    };

    scenarios.forEach((scenario) => resolveScenario(String(scenario.id)));
    return cache;
  }, [scenarios]);

  const scenarioSelectOptions = useMemo(() => {
    const items = [{ id: SCENARIO_BASE_ID, label: "Base (non enregistre)" }];
    const sortedScenarios = [...scenarios].sort((left, right) => {
      const nameCompare = String(left.name || "").localeCompare(String(right.name || ""));
      if (nameCompare !== 0) return nameCompare;
      return Number(left.year || 0) - Number(right.year || 0);
    });
    sortedScenarios.forEach((scenario) => {
      items.push({
        id: scenario.id,
        label: `${scenario.name} (${scenario.year})`,
      });
    });
    return items;
  }, [scenarios]);

  useEffect(() => {
    if (activeScenarioId === SCENARIO_BASE_ID) {
      resetEditorHistory({
        year: currentYear,
        scenarioName: "",
        scenarioConfig: createDefaultScenarioConfig(),
      });
      return;
    }
    const resolution = scenarioResolutionMap.get(String(activeScenarioId));
    if (resolution) {
      const { scenario, effectiveConfig } = resolution;
      resetEditorHistory({
        year: Number(scenario.year || currentYear),
        scenarioName: scenario.name || "",
        scenarioConfig: {
          ...createDefaultScenarioConfig(),
          ...effectiveConfig,
        },
      });
    }
  }, [activeScenarioId, currentYear, resetEditorHistory, scenarioResolutionMap]);

  const includedProjectIds = useMemo(() => {
    return availableProjects
      .filter((project) => !projectExclusions.has(keyOf(project.id)))
      .map((project) => project.id);
  }, [availableProjects, projectExclusions]);

  const projectColorMap = useMemo(() => {
    const sorted = [...availableProjects].sort((a, b) => {
      if (a.project_number && b.project_number) {
        return String(a.project_number).localeCompare(String(b.project_number));
      }
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
    return new Map(sorted.map((project, index) => [project.id, index]));
  }, [availableProjects]);

  const projectLaneMap = useMemo(() => {
    const includedProjectKeys = new Set(includedProjectIds.map(keyOf));
    const included = availableProjects.filter((project) => includedProjectKeys.has(keyOf(project.id)));
    const sorted = [...included].sort((a, b) => {
      const colorA = projectColorMap.get(a.id) ?? 0;
      const colorB = projectColorMap.get(b.id) ?? 0;
      if (colorA !== colorB) return colorA - colorB;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
    return new Map(sorted.map((project, index) => [project.id, index]));
  }, [availableProjects, includedProjectIds, projectColorMap]);

  const scenarioIterationOverrides = useMemo(() => {
    const overrides = scenarioConfig.iterationOverrides || [];
    return new Map(
      overrides
        .filter((item) => item && item.iteration_id !== undefined && item.iteration_id !== null)
        .map((item) => [String(item.iteration_id), item])
    );
  }, [scenarioConfig.iterationOverrides]);

  const { scenarioIterations, scenarioIterationsAll } = useMemo(() => {
    const includedProjects = new Set(includedProjectIds.map(keyOf));
    const baseIterations = iterations
      .map((iteration) => {
        const override = scenarioIterationOverrides.get(String(iteration.id));
        const merged = override ? { ...iteration, ...override } : { ...iteration };
        const baseStartDate = iteration.start_date;
        const baseEndDate = iteration.end_date;
        const hasOverrideDates =
          Boolean(override?.start_date) ||
          Boolean(override?.end_date);
        const hasDifferentDates =
          hasOverrideDates &&
          (String(merged.start_date) !== String(baseStartDate) ||
            String(merged.end_date) !== String(baseEndDate));
        const project =
          availableProjects.find((item) => String(item.id) === String(merged.project_id)) || null;
        const projectLabel = formatProjectOptionLabel(project);
        const descriptionParts = [];
        if (merged.description) descriptionParts.push(merged.description);
        return {
          ...merged,
          name: merged.name || `Iteration ${merged.id}`,
          description: descriptionParts.join(" - "),
          project_label: projectLabel,
          iteration_label: merged.name || `Iteration ${merged.id}`,
          base_start_date: hasDifferentDates ? baseStartDate : null,
          base_end_date: hasDifferentDates ? baseEndDate : null,
          sprints: [],
          colorIndex: projectColorMap.get(merged.project_id) ?? 0,
          laneIndex: projectLaneMap.get(merged.project_id) ?? 0,
          __source: "base",
          // Acquis = donnees de l'iteration projet ; Prevu = modifie par le scenario (override)
          __isForecast: Boolean(override),
        };
      });

    const additions = (scenarioConfig.iterationAdditions || [])
      .map((item, index) => {
        const localId = item.local_id || item.id || `iter-${index}`;
        const project =
          availableProjects.find((entry) => String(entry.id) === String(item.project_id)) || null;
        const projectLabel = formatProjectOptionLabel(project);
        const descriptionParts = [];
        if (item.description) descriptionParts.push(item.description);
        return {
          id: localId,
          local_id: localId,
          project_id: item.project_id,
          name: item.name || "Nouvelle iteration",
          start_date: item.start_date,
          end_date: item.end_date || item.start_date,
          forecast_ca_eur: toNumberOrZero(item.forecast_ca_eur),
          forecast_purchase_eur: toNumberOrZero(item.forecast_purchase_eur),
          forecast_time_hours: toNumberOrZero(item.forecast_time_hours),
          budget_time_hours: item.budget_time_hours ?? item.forecast_time_hours ?? null,
          description: descriptionParts.join(" - "),
          project_label: projectLabel,
          iteration_label: item.name || "Nouvelle iteration",
          sprints: [],
          colorIndex: projectColorMap.get(item.project_id) ?? 0,
          laneIndex: projectLaneMap.get(item.project_id) ?? 0,
          __source: "addition",
          // Ajout de scenario = toujours previsionnel
          __isForecast: true,
        };
      });

    const combined = [...baseIterations, ...additions];
    return {
      scenarioIterationsAll: combined,
      scenarioIterations: combined.filter((iteration) => includedProjects.has(keyOf(iteration.project_id))),
    };
  }, [
    includedProjectIds,
    iterations,
    availableProjects,
    scenarioConfig.iterationAdditions,
    scenarioIterationOverrides,
    projectColorMap,
    projectLaneMap,
  ]);

  const { roadmapMilestones, roadmapMilestonesAll } = useMemo(() => {
    const allMilestones = (milestoneObjectives || [])
      .filter((objective) => objective?.is_milestone && objective?.milestone_date)
      .map((objective) => {
        const project =
          availableProjects.find((entry) => String(entry.id) === String(objective.project_id)) || null;
        const projectLabel = formatProjectOptionLabel(project);
        return {
          id: `milestone-${objective.id}`,
          objective_id: objective.id,
          project_id: objective.project_id,
          name: objective.name || "Jalon",
          description: objective.description || "",
          start_date: objective.milestone_date,
          end_date: objective.milestone_date,
          milestone_date: objective.milestone_date,
          project_label: projectLabel,
          iteration_label: objective.name || "Jalon",
          sprints: [],
          colorIndex: projectColorMap.get(objective.project_id) ?? 0,
          laneIndex: projectLaneMap.get(objective.project_id) ?? 0,
          __source: "milestone",
          __roadmapType: "milestone",
          __isForecast: false,
          is_milestone: true,
          status: objective.status,
        };
      });

    return {
      roadmapMilestonesAll: allMilestones,
      roadmapMilestones: allMilestones.filter(
        (milestone) => !projectExclusions.has(keyOf(milestone.project_id))
      ),
    };
  }, [availableProjects, milestoneObjectives, projectColorMap, projectExclusions, projectLaneMap]);

  const roadmapScenarioItems = useMemo(
    () => [...scenarioIterations, ...roadmapMilestones],
    [roadmapMilestones, scenarioIterations]
  );

  const roadmapScenarioItemsAll = useMemo(
    () => [...scenarioIterationsAll, ...roadmapMilestonesAll],
    [roadmapMilestonesAll, scenarioIterationsAll]
  );

  const roadmapIterationsForYear = useMemo(() => {
    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    return roadmapScenarioItems.filter((iteration) => {
      const start = parseDate(iteration.start_date);
      const end = parseDate(iteration.end_date);
      if (!start || !end) return false;
      return end >= yearStart && start <= yearEnd;
    });
  }, [roadmapScenarioItems, year]);

  const roadmapAllIterationsForYear = useMemo(() => {
    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    return roadmapScenarioItemsAll.filter((iteration) => {
      const start = parseDate(iteration.start_date);
      const end = parseDate(iteration.end_date);
      if (!start || !end) return false;
      return end >= yearStart && start <= yearEnd;
    });
  }, [roadmapScenarioItemsAll, year]);

  const roadmapProjectBaseRows = useMemo(() => {
    const projectIds = new Set(roadmapAllIterationsForYear.map((iteration) => keyOf(iteration.project_id)));
    return availableProjects
      .filter((project) => projectIds.has(keyOf(project.id)))
      .sort((a, b) => {
        if (a.project_number && b.project_number) {
          return String(a.project_number).localeCompare(String(b.project_number));
        }
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
  }, [availableProjects, roadmapAllIterationsForYear]);

  const { roadmapIterationsAligned, roadmapProjectRows } = useMemo(() => {
    const layout = buildProjectRoadmapLayout(
      roadmapProjectBaseRows,
      roadmapAllIterationsForYear,
      roadmapRowHeight,
      roadmapLaneGap
    );
    return {
      roadmapProjectRows: layout.rows,
      roadmapIterationsAligned: roadmapIterationsForYear.map((iteration) => ({
        ...iteration,
        laneIndex:
          layout.laneIndexByIteration.get(getIterationLayoutKey(iteration)) ??
          iteration.laneIndex ??
          0,
      })),
    };
  }, [
    roadmapAllIterationsForYear,
    roadmapLaneGap,
    roadmapProjectBaseRows,
    roadmapIterationsForYear,
    roadmapRowHeight,
  ]);

  useEffect(() => {
    setRoadmapIterations(roadmapIterationsAligned);
  }, [roadmapIterationsAligned]);

  const roadmapTimelineBounds = useMemo(
    () => ({
      startDate: `${year}-01-01`,
      endDate: `${year}-12-31`,
    }),
    [year]
  );

  const roadmapStats = useMemo(() => {
    const yearStart = parseDate(roadmapTimelineBounds.startDate);
    const yearEnd = parseDate(roadmapTimelineBounds.endDate);
    return roadmapIterationsForYear.reduce(
      (acc, iteration) => {
        const start = parseDate(iteration.start_date);
        const end = parseDate(iteration.end_date);
        const isMilestone = isRoadmapMilestoneItem(iteration);
        if (isMilestone) {
          acc.milestones += 1;
        } else {
          acc.iterations += 1;
        }
        if (!isMilestone && iteration.__source === "addition") acc.scenarioAdditions += 1;
        if (!isMilestone && Number(iteration.forecast_ca_eur || 0) > 0) acc.forecastOnly += 1;
        if ((start && yearStart && start < yearStart) || (end && yearEnd && end > yearEnd)) {
          acc.outsideWindow += 1;
        }
        return acc;
      },
      {
        projects: roadmapProjectRows.filter((project) => !projectExclusions.has(keyOf(project.id))).length,
        iterations: 0,
        milestones: 0,
        scenarioAdditions: 0,
        forecastOnly: 0,
        outsideWindow: 0,
      }
    );
  }, [projectExclusions, roadmapIterationsForYear, roadmapProjectRows, roadmapTimelineBounds]);

  const timelineEvents = useMemo(() => {
    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    const events = [];
    const pushEvent = (dateValue, type, name) => {
      const date = parseDate(dateValue);
      if (!date) return;
      if (date < yearStart || date > yearEnd) return;
      events.push({
        id: `${type}-${name}-${date.toISOString()}`,
        date,
        dateLabel: date.toLocaleDateString("fr-FR"),
        type,
        name,
      });
    };

    employees.forEach((employee) => {
      if (employee?.arrival_date) {
        pushEvent(employee.arrival_date, "employee_arrival", employee.name || "Employe");
      }
      if (employee?.departure_date) {
        pushEvent(employee.departure_date, "employee_departure", employee.name || "Employe");
      }
    });

    (scenarioConfig.employeeAdditions || []).forEach((item, index) => {
      const label = item?.name || `Renfort ${index + 1}`;
      if (item?.start_date) {
        pushEvent(item.start_date, "reinforcement_arrival", label);
      }
      if (item?.end_date) {
        pushEvent(item.end_date, "reinforcement_departure", label);
      }
    });

    return events.sort((a, b) => a.date - b.date);
  }, [employees, scenarioConfig.employeeAdditions, year]);

  const eventByMonthKey = useMemo(() => {
    const map = new Map(monthRanges.map((month) => [month.key, []]));
    timelineEvents.forEach((event) => {
      const match = monthRanges.find((month) =>
        isDateInRange(event.date, month.start, month.end)
      );
      if (match) {
        map.get(match.key).push(event);
      }
    });
    return map;
  }, [timelineEvents, monthRanges]);

  const metricsByMonth = useMemo(() => {
    const data = monthRanges.map((month) => ({
      ...month,
      capacityHours: 0,
      chargeHours: 0,
      expensesForecastEur: 0,
      caActualEur: 0,
      caForecastEur: 0,
    }));

    if (scenarioIterations.length) {
      scenarioIterations.forEach((iteration) => {
        const start = parseDate(iteration.start_date);
        const end = parseDate(iteration.end_date);
        if (!start || !end || start > end) return;

        const totalWorkingDays = getWorkingDaysDates(start, end).length;
        if (totalWorkingDays <= 0) return;

        const caActualTotal = Number(iteration.valeur || 0);
        const caForecastTotal = Number(iteration.forecast_ca_eur || 0);
        const expenseTotal = Number(iteration.forecast_purchase_eur || 0);
        const chargeTotal = computeChargeHours(
          iteration,
          caActualTotal,
          caForecastTotal,
          expenseTotal
        );

        const isForecast = Boolean(iteration.__isForecast);
        const caActualShare = isForecast ? 0 : caForecastTotal;
        const caForecastShare = isForecast ? caForecastTotal : 0;
        data.forEach((month) => {
          const overlapStart = start > month.start ? start : month.start;
          const overlapEnd = end < month.end ? end : month.end;
          if (overlapStart > overlapEnd) return;
          const overlapWorkingDays = getWorkingDaysDates(overlapStart, overlapEnd).length;
          if (overlapWorkingDays <= 0) return;
          const ratio = overlapWorkingDays / totalWorkingDays;
          month.caActualEur += caActualShare * ratio;
          month.caForecastEur += caForecastShare * ratio;
          month.chargeHours += chargeTotal * ratio;
          month.expensesForecastEur += expenseTotal * ratio;
        });
      });
    }

    const timeOffs = employees.reduce((acc, employee) => {
      if (!employee?.id) return acc;
      const list = [...(acc.get(employee.id) || [])];
      (employee.time_offs || []).forEach((item) => {
        list.push({
          start: parseDate(item.start_date),
          end: parseDate(item.end_date),
        });
      });
      acc.set(employee.id, list);
      return acc;
    }, new Map());

    (scenarioConfig.employeeTimeOffs || []).forEach((item) => {
      if (!item || !item.employee_id) return;
      const list = timeOffs.get(item.employee_id) || [];
      list.push({
        start: parseDate(item.start_date),
        end: parseDate(item.end_date),
      });
      timeOffs.set(item.employee_id, list);
    });

    const overrides = (scenarioConfig.employeeOverrides || []).reduce((acc, item) => {
      if (!item || !item.employee_id) return acc;
      acc.set(item.employee_id, {
        availability_percent: Number(item.availability_percent ?? 0),
        start: parseDate(item.start_date),
        end: parseDate(item.end_date),
      });
      return acc;
    }, new Map());

    const scenarioEmployees = employees
      .filter((employee) => employee && employee.is_active !== false)
      .map((employee) => ({
        ...employee,
        source: "existing",
      }));

    const additions = (scenarioConfig.employeeAdditions || []).map((item, index) => ({
      id: item.local_id || `extra-${index}`,
      name: item.name || "Renfort",
      arrival_date: item.start_date,
      departure_date: item.end_date,
      production_rate: item.production_rate ?? 100,
      weekly_hours: item.weekly_hours ?? DEFAULT_EMPLOYEE_WEEKLY_HOURS,
      availability_percent:
        item.availability_percent ?? DEFAULT_SCENARIO_AVAILABILITY_PERCENT,
      source: "addition",
    }));

    const allEmployees = [...scenarioEmployees, ...additions];
    const hoursPerDayFallback = Number(scenarioConfig.hoursPerDay || 8);

    data.forEach((month) => {
      const workingDays = getWorkingDaysDates(month.start, month.end);
      let capacity = 0;
      workingDays.forEach((day) => {
        allEmployees.forEach((employee) => {
          const arrival = parseDate(employee.arrival_date);
          const departure = parseDate(employee.departure_date);
          if (arrival && day < arrival) return;
          if (departure && day > departure) return;

          if (employee.source === "existing") {
            if (!employeeWorksOnDay(employee, day)) {
              return;
            }
            const employeeTimeOffs = timeOffs.get(employee.id) || [];
            if (employeeTimeOffs.some((off) => isDateInRange(day, off.start, off.end))) {
              return;
            }
          }

          let availabilityPercent = DEFAULT_SCENARIO_AVAILABILITY_PERCENT;
          if (employee.source === "addition") {
            availabilityPercent = employee.availability_percent ?? availabilityPercent;
          } else {
            const override = overrides.get(employee.id);
            if (override) {
              const applyStart = override.start ? day >= override.start : true;
              const applyEnd = override.end ? day <= override.end : true;
              if (applyStart && applyEnd) {
                availabilityPercent = override.availability_percent ?? availabilityPercent;
              }
            }
          }

          const productionRateMultiplier = getEmployeeProductionRateMultiplier(employee);
          const dailyCapacityHours = getEmployeeDailyCapacityHours(employee, hoursPerDayFallback);
          capacity += (dailyCapacityHours * productionRateMultiplier * availabilityPercent) / 100;
        });
      });
      month.capacityHours = capacity * scenarioEfficiencyMultiplier;
    });

    return data;
  }, [
    monthRanges,
    scenarioIterations,
    scenarioConfig,
    employees,
    scenarioEfficiencyMultiplier,
  ]);

  const totals = useMemo(() => {
    return metricsByMonth.reduce(
      (acc, month) => {
        acc.capacityHours += month.capacityHours;
        acc.chargeHours += month.chargeHours;
        acc.expensesForecastEur += month.expensesForecastEur;
        acc.caActualEur += month.caActualEur;
        acc.caForecastEur += month.caForecastEur;
        return acc;
      },
      {
        capacityHours: 0,
        chargeHours: 0,
        expensesForecastEur: 0,
        caActualEur: 0,
        caForecastEur: 0,
      }
    );
  }, [metricsByMonth]);

  const formatHours = (value) =>
    new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(value || 0);
  const formatCurrency = (value) =>
    new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value || 0);
  const formatCurrencyShort = (value) =>
    new Intl.NumberFormat("fr-FR", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value || 0);
  const formatPercent = (value) =>
    `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(value || 0)}%`;
  const formatOptionalPercent = (value) =>
    Number.isFinite(value)
      ? `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(value)}%`
      : "-";
  const formatCurrencyPerHour = (value) =>
    Number.isFinite(value) ? `${formatCurrency(value)}/h` : "-";
  const formatSignedHours = (value) => {
    const numeric = Number(value || 0);
    const prefix = numeric > 0 ? "+" : numeric < 0 ? "-" : "";
    return `${prefix}${formatHours(Math.abs(numeric))} h`;
  };

  const projectSeries = useMemo(() => {
    const includedProjectKeys = new Set(includedProjectIds.map(keyOf));
    const included = availableProjects.filter((project) => includedProjectKeys.has(keyOf(project.id)));
    return included.map((project) => {
      const colorIndex = projectColorMap.get(project.id) ?? 0;
      return {
        id: project.id,
        name: formatProjectOptionLabel(project),
        clientKey: getProjectClientKey(project),
        clientName: getProjectClientName(project),
        clientType: getProjectClientType(project),
        color: PROJECT_LINE_PALETTE[colorIndex % PROJECT_LINE_PALETTE.length] || "#64748b",
      };
    });
  }, [availableProjects, includedProjectIds, projectColorMap]);

  const projectMonthlyMetrics = useMemo(() => {
    const base = monthRanges.map((month) => ({
      key: month.key,
      label: month.label,
      totalCharge: 0,
      totalChargeActual: 0,
      totalChargeForecast: 0,
      totalExpenses: 0,
      totalCa: 0,
      totalCaActual: 0,
      totalCaForecast: 0,
      projectCharge: {},
      projectChargeActual: {},
      projectChargeForecast: {},
      projectExpenses: {},
      projectCa: {},
      projectCaActual: {},
      projectCaForecast: {},
      clientTypeCharge: createClientTypeBuckets(),
      clientTypeChargeActual: createClientTypeBuckets(),
      clientTypeChargeForecast: createClientTypeBuckets(),
      clientTypeExpenses: createClientTypeBuckets(),
      clientTypeCa: createClientTypeBuckets(),
      clientTypeCaActual: createClientTypeBuckets(),
      clientTypeCaForecast: createClientTypeBuckets(),
    }));

    if (!scenarioIterations.length) return base;
    const includedProjects = new Set(includedProjectIds.map(keyOf));
    const projectClientTypeById = new Map(
      projectSeries.map((project) => [String(project.id), normalizeClientType(project.clientType)])
    );

    scenarioIterations.forEach((iteration) => {
      if (!includedProjects.has(keyOf(iteration.project_id))) return;
      const start = parseDate(iteration.start_date);
      const end = parseDate(iteration.end_date);
      if (!start || !end || start > end) return;
      const totalWorkingDays = getWorkingDaysDates(start, end).length;
      if (totalWorkingDays <= 0) return;

      const caActualTotal = Number(iteration.valeur || 0);
      const caForecastTotal = Number(iteration.forecast_ca_eur || 0);
      const isForecast = Boolean(iteration.__isForecast);
      const caActualShare = isForecast ? 0 : caForecastTotal;
      const caForecastShare = isForecast ? caForecastTotal : 0;
      const expenseTotal = Number(
        iteration.forecast_purchase_eur || 0
      );
      const chargeTotal = computeChargeHours(
        iteration,
        caActualTotal,
        caForecastTotal,
        expenseTotal
      );
      const chargeActualShare = isForecast ? 0 : chargeTotal;
      const chargeForecastShare = isForecast ? chargeTotal : 0;

      base.forEach((month, index) => {
        const monthStart = monthRanges[index].start;
        const monthEnd = monthRanges[index].end;
        const overlapStart = start > monthStart ? start : monthStart;
        const overlapEnd = end < monthEnd ? end : monthEnd;
        if (overlapStart > overlapEnd) return;
        const overlapWorkingDays = getWorkingDaysDates(overlapStart, overlapEnd).length;
        if (overlapWorkingDays <= 0) return;
        const ratio = overlapWorkingDays / totalWorkingDays;
        const projectKey = String(iteration.project_id);
        const monthChargeActual = chargeActualShare * ratio;
        const monthChargeForecast = chargeForecastShare * ratio;
        const monthCharge = monthChargeActual + monthChargeForecast;
        const monthExpense = expenseTotal * ratio;
        const monthCaActual = caActualShare * ratio;
        const monthCaForecast = caForecastShare * ratio;
        const monthCa = monthCaActual + monthCaForecast;
        const clientType = projectClientTypeById.get(projectKey) || CLIENT_TYPE_NORMAL;
        month.totalCharge += monthCharge;
        month.totalChargeActual += monthChargeActual;
        month.totalChargeForecast += monthChargeForecast;
        month.totalExpenses += monthExpense;
        month.totalCa += monthCa;
        month.totalCaActual += monthCaActual;
        month.totalCaForecast += monthCaForecast;
        month.projectCharge[projectKey] = (month.projectCharge[projectKey] || 0) + monthCharge;
        month.projectChargeActual[projectKey] =
          (month.projectChargeActual[projectKey] || 0) + monthChargeActual;
        month.projectChargeForecast[projectKey] =
          (month.projectChargeForecast[projectKey] || 0) + monthChargeForecast;
        month.projectExpenses[projectKey] = (month.projectExpenses[projectKey] || 0) + monthExpense;
        month.projectCa[projectKey] = (month.projectCa[projectKey] || 0) + monthCa;
        month.projectCaActual[projectKey] =
          (month.projectCaActual[projectKey] || 0) + monthCaActual;
        month.projectCaForecast[projectKey] =
          (month.projectCaForecast[projectKey] || 0) + monthCaForecast;
        month.clientTypeCharge[clientType] =
          (month.clientTypeCharge[clientType] || 0) + monthCharge;
        month.clientTypeChargeActual[clientType] =
          (month.clientTypeChargeActual[clientType] || 0) + monthChargeActual;
        month.clientTypeChargeForecast[clientType] =
          (month.clientTypeChargeForecast[clientType] || 0) + monthChargeForecast;
        month.clientTypeExpenses[clientType] =
          (month.clientTypeExpenses[clientType] || 0) + monthExpense;
        month.clientTypeCa[clientType] =
          (month.clientTypeCa[clientType] || 0) + monthCa;
        month.clientTypeCaActual[clientType] =
          (month.clientTypeCaActual[clientType] || 0) + monthCaActual;
        month.clientTypeCaForecast[clientType] =
          (month.clientTypeCaForecast[clientType] || 0) + monthCaForecast;
      });
    });

    return base;
  }, [monthRanges, scenarioIterations, includedProjectIds, projectSeries]);

  const productionProgress = useMemo(() => {
    const monthlyPlan = monthRanges.map((month) => ({
      key: month.key,
      label: month.label,
      done: 0,
      remaining: 0,
      doneByClientType: createClientTypeBuckets(),
      remainingByClientType: createClientTypeBuckets(),
    }));
    const today = parseDate(toDateKey(new Date()));
    const includedProjects = new Set(includedProjectIds.map(keyOf));
    const projectByKey = new Map(
      projectSeries.map((project) => [
        String(project.id),
        {
          id: project.id,
          name: project.name,
          color: project.color,
          clientType: normalizeClientType(project.clientType),
          target: 0,
          done: 0,
        },
      ])
    );

    const ensureProject = (projectId) => {
      const projectKey = String(projectId);
      if (!projectByKey.has(projectKey)) {
        projectByKey.set(projectKey, {
          id: projectId,
          name: `Projet ${projectKey}`,
          color: "#64748b",
          clientType: CLIENT_TYPE_NORMAL,
          target: 0,
          done: 0,
        });
      }
      return projectByKey.get(projectKey);
    };

    scenarioIterations.forEach((iteration) => {
      if (!includedProjects.has(keyOf(iteration.project_id))) return;
      const start = parseDate(iteration.start_date);
      const end = parseDate(iteration.end_date);
      if (!start || !end || start > end) return;

      const project = ensureProject(iteration.project_id);
      const clientType = normalizeClientType(project.clientType);
      const target = getIterationProductionTarget(iteration);
      if (target <= 0) return;
      const totalWorkingDays = getWorkingDaysDates(start, end).length;
      if (totalWorkingDays <= 0) return;

      monthRanges.forEach((month, index) => {
        const overlapStart = start > month.start ? start : month.start;
        const overlapEnd = end < month.end ? end : month.end;
        if (overlapStart > overlapEnd) return;
        const overlapWorkingDays = getWorkingDaysDates(overlapStart, overlapEnd).length;
        if (overlapWorkingDays <= 0) return;

        const plannedShare = target * (overlapWorkingDays / totalWorkingDays);
        const doneEnd = today && today < overlapEnd ? today : overlapEnd;
        const doneWorkingDays =
          today && doneEnd >= overlapStart
            ? getWorkingDaysDates(overlapStart, doneEnd).length
            : 0;
        const doneShare = Math.min(
          plannedShare,
          target * (doneWorkingDays / totalWorkingDays)
        );
        const remainingShare = Math.max(0, plannedShare - doneShare);

        monthlyPlan[index].done += doneShare;
        monthlyPlan[index].remaining += remainingShare;
        monthlyPlan[index].doneByClientType[clientType] =
          (monthlyPlan[index].doneByClientType[clientType] || 0) + doneShare;
        monthlyPlan[index].remainingByClientType[clientType] =
          (monthlyPlan[index].remainingByClientType[clientType] || 0) + remainingShare;
        project.target += plannedShare;
        project.done += doneShare;
      });
    });

    const totalTarget = Array.from(projectByKey.values()).reduce(
      (sum, project) => sum + project.target,
      0
    );
    let runningDone = 0;
    let runningRemaining = 0;
    const runningByClientType = CLIENT_TYPE_SEGMENTS.reduce((acc, segment) => {
      acc[segment.id] = { done: 0, remaining: 0 };
      return acc;
    }, {});
    const cumulativeData = monthlyPlan.map((month) => {
      runningDone += month.done;
      runningRemaining += month.remaining;
      const entry = {
        key: month.key,
        label: month.label,
        done: runningDone,
        remaining: runningRemaining,
        total: runningDone + runningRemaining,
      };
      CLIENT_TYPE_SEGMENTS.forEach((segment) => {
        runningByClientType[segment.id].done += Number(month.doneByClientType[segment.id] || 0);
        runningByClientType[segment.id].remaining += Number(
          month.remainingByClientType[segment.id] || 0
        );
        entry[`${segment.id}Done`] =
          runningByClientType[segment.id].done;
        entry[`${segment.id}Remaining`] =
          runningByClientType[segment.id].remaining;
      });
      return entry;
    });

    const monthlyData = monthlyPlan.map((month) => ({
      ...month,
      total: month.done + month.remaining,
    }));

    const totalDone = monthlyPlan.reduce((sum, month) => sum + month.done, 0);
    const totalsByClientType = CLIENT_TYPE_SEGMENTS.reduce((acc, segment) => {
      acc[segment.id] = monthlyPlan.reduce(
        (segmentAcc, month) => ({
          done: segmentAcc.done + Number(month.doneByClientType[segment.id] || 0),
          remaining:
            segmentAcc.remaining + Number(month.remainingByClientType[segment.id] || 0),
        }),
        { done: 0, remaining: 0 }
      );
      acc[segment.id].target = acc[segment.id].done + acc[segment.id].remaining;
      return acc;
    }, {});
    const projects = Array.from(projectByKey.values())
      .map((project) => ({
        ...project,
        remaining: Math.max(0, project.target - project.done),
        clientType: normalizeClientType(project.clientType),
      }))
      .filter((project) => project.target > 0 || project.done > 0)
      .sort((a, b) => b.target - a.target);

    return {
      cumulativeData,
      monthlyData,
      projects,
      totals: {
        target: totalTarget,
        done: totalDone,
        remaining: Math.max(0, totalTarget - totalDone),
        progressPct: totalTarget > 0 ? (totalDone / totalTarget) * 100 : 0,
        byClientType: totalsByClientType,
      },
    };
  }, [monthRanges, scenarioIterations, includedProjectIds, projectSeries]);

  const productionCapacityProjection = useMemo(() => {
    const monthlyData = (productionProgress.monthlyData || []).map((month, index) => {
      const metrics = metricsByMonth[index] || {};
      const capacity = Number(metrics.capacityHours || 0);
      const charge = Number(metrics.chargeHours || 0);
      const produced = Number(month.done || 0);
      const plannedFuture = Number(month.remaining || 0);
      const plannedTotal = produced + plannedFuture;
      const capacityRatio = charge > 0 ? Math.min(1, Math.max(0, capacity / charge)) : 1;
      const capacityLimitedTotal = plannedTotal * capacityRatio;
      const lostCa = Math.min(
        plannedFuture,
        Math.max(0, plannedTotal - capacityLimitedTotal)
      );
      const futureProductible = Math.max(0, plannedFuture - lostCa);
      const entry = {
        key: month.key,
        label: month.label,
        produced,
        plannedFuture,
        futureProductible,
        lostCa,
        finalCa: produced + futureProductible,
        plannedTotal,
        capacity,
        charge,
        overloadHours: Math.max(0, charge - capacity),
        capacityRatio,
      };
      CLIENT_TYPE_SEGMENTS.forEach((segment) => {
        const segmentProduced = Number(month.doneByClientType?.[segment.id] || 0);
        const segmentPlannedFuture = Number(month.remainingByClientType?.[segment.id] || 0);
        const segmentLost =
          plannedFuture > 0 ? lostCa * (segmentPlannedFuture / plannedFuture) : 0;
        entry[`${segment.id}Produced`] = segmentProduced;
        entry[`${segment.id}PlannedFuture`] = segmentPlannedFuture;
        entry[`${segment.id}FutureProductible`] = Math.max(
          0,
          segmentPlannedFuture - segmentLost
        );
        entry[`${segment.id}LostCa`] = segmentLost;
      });
      return entry;
    });

    let runningProduced = 0;
    let runningFutureProductible = 0;
    let runningLostCa = 0;
    let runningPlannedFuture = 0;
    const runningCapacityByClientType = CLIENT_TYPE_SEGMENTS.reduce((acc, segment) => {
      acc[segment.id] = {
        produced: 0,
        futureProductible: 0,
        lostCa: 0,
        plannedFuture: 0,
      };
      return acc;
    }, {});
    const cumulativeData = monthlyData.map((month) => {
      runningProduced += month.produced;
      runningFutureProductible += month.futureProductible;
      runningLostCa += month.lostCa;
      runningPlannedFuture += month.plannedFuture;
      const entry = {
        key: month.key,
        label: month.label,
        produced: runningProduced,
        futureProductible: runningFutureProductible,
        lostCa: runningLostCa,
        plannedFuture: runningPlannedFuture,
        plannedTotal: runningProduced + runningPlannedFuture,
        finalCa: runningProduced + runningFutureProductible,
      };
      CLIENT_TYPE_SEGMENTS.forEach((segment) => {
        const running = runningCapacityByClientType[segment.id];
        running.produced += Number(month[`${segment.id}Produced`] || 0);
        running.futureProductible += Number(month[`${segment.id}FutureProductible`] || 0);
        running.lostCa += Number(month[`${segment.id}LostCa`] || 0);
        running.plannedFuture += Number(month[`${segment.id}PlannedFuture`] || 0);
        entry[`${segment.id}Produced`] = running.produced;
        entry[`${segment.id}FutureProductible`] = running.futureProductible;
        entry[`${segment.id}LostCa`] = running.lostCa;
        entry[`${segment.id}PlannedFuture`] = running.plannedFuture;
      });
      return entry;
    });

    const totals = monthlyData.reduce(
      (acc, month) => {
        acc.producedCa += month.produced;
        acc.plannedFutureCa += month.plannedFuture;
        acc.productibleFutureCa += month.futureProductible;
        acc.lostCa += month.lostCa;
        acc.capacityHours += month.capacity;
        acc.chargeHours += month.charge;
        acc.overloadHours += month.overloadHours;
        return acc;
      },
      {
        producedCa: 0,
        plannedFutureCa: 0,
        productibleFutureCa: 0,
        lostCa: 0,
        plannedEndOfYearCa: 0,
        endOfYearCa: 0,
        capacityHours: 0,
        chargeHours: 0,
        overloadHours: 0,
        lossPct: 0,
      }
    );

    totals.byClientType = CLIENT_TYPE_SEGMENTS.reduce((acc, segment) => {
      const segmentTotals = monthlyData.reduce(
        (segmentAcc, month) => {
          segmentAcc.producedCa += Number(month[`${segment.id}Produced`] || 0);
          segmentAcc.plannedFutureCa += Number(month[`${segment.id}PlannedFuture`] || 0);
          segmentAcc.productibleFutureCa += Number(
            month[`${segment.id}FutureProductible`] || 0
          );
          segmentAcc.lostCa += Number(month[`${segment.id}LostCa`] || 0);
          return segmentAcc;
        },
        { producedCa: 0, plannedFutureCa: 0, productibleFutureCa: 0, lostCa: 0 }
      );
      segmentTotals.endOfYearCa =
        segmentTotals.producedCa + segmentTotals.productibleFutureCa;
      acc[segment.id] = segmentTotals;
      return acc;
    }, {});

    totals.plannedEndOfYearCa = totals.producedCa + totals.plannedFutureCa;
    totals.endOfYearCa = totals.producedCa + totals.productibleFutureCa;
    totals.lossPct =
      totals.plannedFutureCa > 0 ? (totals.lostCa / totals.plannedFutureCa) * 100 : 0;

    return {
      monthlyData,
      cumulativeData,
      totals,
    };
  }, [metricsByMonth, productionProgress.monthlyData]);

  const projectAnnualContribution = useMemo(() => {
    const contributionByProject = new Map(
      projectSeries.map((project) => [
        String(project.id),
        {
          id: project.id,
          name: project.name,
          clientKey: project.clientKey || "unassigned",
          clientName: project.clientName || "Sans client",
          clientType: normalizeClientType(project.clientType),
          color: project.color,
          charge: 0,
          ca: 0,
        },
      ])
    );
    const ensureProject = (projectId) => {
      const key = String(projectId);
      if (!contributionByProject.has(key)) {
        contributionByProject.set(key, {
          id: projectId,
          name: `Projet ${key}`,
          clientKey: "unassigned",
          clientName: "Sans client",
          clientType: CLIENT_TYPE_NORMAL,
          color: "#64748b",
          charge: 0,
          ca: 0,
        });
      }
      return contributionByProject.get(key);
    };

    projectMonthlyMetrics.forEach((month) => {
      projectSeries.forEach((project) => {
        const projectKey = String(project.id);
        const row = ensureProject(project.id);
        row.charge += Number(month.projectCharge[projectKey] || 0);
      });
    });
    productionProgress.projects.forEach((project) => {
      const row = ensureProject(project.id);
      row.ca = Number(project.target || 0);
    });

    const chargeTotal = Array.from(contributionByProject.values()).reduce(
      (sum, project) => sum + project.charge,
      0
    );
    const caTotal = Array.from(contributionByProject.values()).reduce(
      (sum, project) => sum + project.ca,
      0
    );
    const projects = Array.from(contributionByProject.values())
      .filter((project) => project.charge > 0 || project.ca > 0)
      .map((project) => ({
        ...project,
        chargePct: chargeTotal > 0 ? (project.charge / chargeTotal) * 100 : 0,
        caPct: caTotal > 0 ? (project.ca / caTotal) * 100 : 0,
      }))
      .sort((a, b) => b.ca + b.charge - (a.ca + a.charge));

    const contributionByClient = new Map();
    const ensureClient = (project) => {
      const clientKey = project.clientKey || "unassigned";
      if (!contributionByClient.has(clientKey)) {
        contributionByClient.set(clientKey, {
          id: clientKey,
          name: project.clientName || "Sans client",
          clientType: normalizeClientType(project.clientType),
          charge: 0,
          ca: 0,
          projectCount: 0,
        });
      }
      return contributionByClient.get(clientKey);
    };

    projects.forEach((project) => {
      const client = ensureClient(project);
      client.charge += project.charge;
      client.ca += project.ca;
      client.projectCount += 1;
    });

    const clients = Array.from(contributionByClient.values())
      .filter((client) => client.charge > 0 || client.ca > 0)
      .sort((a, b) => b.ca + b.charge - (a.ca + a.charge))
      .map((client, index) => ({
        ...client,
        color: PROJECT_LINE_PALETTE[index % PROJECT_LINE_PALETTE.length] || "#64748b",
        chargePct: chargeTotal > 0 ? (client.charge / chargeTotal) * 100 : 0,
        caPct: caTotal > 0 ? (client.ca / caTotal) * 100 : 0,
      }));

    return {
      projects,
      chargeData: projects.filter((project) => project.charge > 0),
      caData: projects.filter((project) => project.ca > 0),
      clients,
      clientChargeData: clients.filter((client) => client.charge > 0),
      clientCaData: clients.filter((client) => client.ca > 0),
      totals: {
        charge: chargeTotal,
        ca: caTotal,
      },
    };
  }, [projectMonthlyMetrics, projectSeries, productionProgress.projects]);

  const statisticsData = useMemo(() => {
    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    const today = parseDate(toDateKey(new Date()));
    const elapsedEnd =
      today && today >= yearStart ? (today > yearEnd ? yearEnd : today) : null;
    const hasElapsed = Boolean(elapsedEnd && elapsedEnd >= yearStart);
    const futureStart =
      !today || today < yearStart
        ? yearStart
        : today < yearEnd
        ? addDays(today, 1)
        : null;

    const monthStats = monthRanges.map((month) => ({
      key: month.key,
      label: month.label,
      expectedHours: 0,
      productiveHours: 0,
      actualHours: 0,
      nonProductionHours: 0,
      futureProductiveHours: 0,
    }));

    const nonProductionProjects = availableProjects.filter(isNonProductionProject);
    const nonProductionProjectIds = new Set(
      nonProductionProjects
        .filter((project) => project?.id !== undefined && project?.id !== null)
        .map((project) => String(project.id))
    );
    if (!nonProductionProjectIds.size) {
      nonProductionProjectIds.add(String(NON_PRODUCTION_PROJECT_FALLBACK_ID));
    }
    const nonProductionProject = nonProductionProjects[0] || null;

    const makeMonthCells = () =>
      monthRanges.map((month) => ({
        key: month.key,
        label: month.label,
        expectedHours: 0,
        productiveHours: 0,
        actualHours: 0,
        nonProductionHours: 0,
      }));

    const rowsByKey = new Map();
    const employeeById = new Map(
      employees
        .filter((employee) => employee?.id !== undefined && employee?.id !== null)
        .map((employee) => [String(employee.id), employee])
    );

    const ensureRow = (employee, source = "existing") => {
      const rowKey = `${source}:${String(employee.id)}`;
      if (!rowsByKey.has(rowKey)) {
        rowsByKey.set(rowKey, {
          key: rowKey,
          id: employee.id,
          name: employee.name || `Employe ${employee.id}`,
          source,
          expectedToDate: 0,
          productiveToDate: 0,
          productiveFuture: 0,
          actualToDate: 0,
          nonProductionHours: 0,
          rawActualToDate: 0,
          rawNonProductionHours: 0,
          months: makeMonthCells(),
        });
      }
      return rowsByKey.get(rowKey);
    };

    const timeOffs = employees.reduce((acc, employee) => {
      if (!employee?.id) return acc;
      const list = [...(acc.get(String(employee.id)) || [])];
      (employee.time_offs || []).forEach((item) => {
        list.push({
          start: parseDate(item.start_date),
          end: parseDate(item.end_date),
        });
      });
      acc.set(String(employee.id), list);
      return acc;
    }, new Map());

    (scenarioConfig.employeeTimeOffs || []).forEach((item) => {
      if (!item || !item.employee_id) return;
      const employeeKey = String(item.employee_id);
      const list = timeOffs.get(employeeKey) || [];
      list.push({
        start: parseDate(item.start_date),
        end: parseDate(item.end_date),
      });
      timeOffs.set(employeeKey, list);
    });

    const overrides = (scenarioConfig.employeeOverrides || []).reduce((acc, item) => {
      if (!item || !item.employee_id) return acc;
      acc.set(String(item.employee_id), {
        availability_percent: Number(item.availability_percent ?? 0),
        start: parseDate(item.start_date),
        end: parseDate(item.end_date),
      });
      return acc;
    }, new Map());

    const scenarioEmployees = employees
      .filter((employee) => employee && employee.is_active !== false)
      .map((employee) => ({
        ...employee,
        source: "existing",
      }));

    const additions = (scenarioConfig.employeeAdditions || []).map((item, index) => ({
      id: item.local_id || `extra-${index}`,
      name: item.name || "Renfort",
      arrival_date: item.start_date,
      departure_date: item.end_date,
      production_rate: item.production_rate ?? 100,
      weekly_hours: item.weekly_hours ?? DEFAULT_EMPLOYEE_WEEKLY_HOURS,
      weekly_work_days: item.weekly_work_days || [0, 1, 2, 3, 4],
      availability_percent:
        item.availability_percent ?? DEFAULT_SCENARIO_AVAILABILITY_PERCENT,
      source: "addition",
    }));

    const allEmployees = [...scenarioEmployees, ...additions];
    const hoursPerDayFallback = Number(scenarioConfig.hoursPerDay || 8);
    const totalsAcc = {
      expectedToDate: 0,
      trackedExpectedToDate: 0,
      productiveToDate: 0,
      productiveFuture: 0,
      nominalProductiveToDate: 0,
      nominalProductiveFuture: 0,
      actualToDate: 0,
      nonProductionHours: 0,
      rawActualToDate: 0,
      rawNonProductionHours: 0,
    };

    const getEmployeeDayCapacity = (employee, day) => {
      const arrival = parseDate(employee.arrival_date);
      const departure = parseDate(employee.departure_date);
      if (arrival && day < arrival) return null;
      if (departure && day > departure) return null;
      if (!employeeWorksOnDay(employee, day)) return null;

      if (employee.source === "existing") {
        const employeeTimeOffs = timeOffs.get(String(employee.id)) || [];
        if (employeeTimeOffs.some((off) => isDateInRange(day, off.start, off.end))) {
          return null;
        }
      }

      let availabilityPercent = DEFAULT_SCENARIO_AVAILABILITY_PERCENT;
      if (employee.source === "addition") {
        availabilityPercent = Number(employee.availability_percent ?? availabilityPercent);
      } else {
        const override = overrides.get(String(employee.id));
        if (override) {
          const applyStart = override.start ? day >= override.start : true;
          const applyEnd = override.end ? day <= override.end : true;
          if (applyStart && applyEnd) {
            availabilityPercent = Number(override.availability_percent ?? availabilityPercent);
          }
        }
      }
      if (!Number.isFinite(availabilityPercent)) availabilityPercent = 0;

      const productionRateMultiplier = getEmployeeProductionRateMultiplier(employee);
      const dailyCapacityHours = getEmployeeDailyCapacityHours(employee, hoursPerDayFallback);
      const availableHours = (dailyCapacityHours * Math.max(0, availabilityPercent)) / 100;
      const nominalProductiveHours = availableHours * productionRateMultiplier;
      const productiveHours = nominalProductiveHours * scenarioEfficiencyMultiplier;
      const expectedHours = productiveHours;
      return { expectedHours, productiveHours, nominalProductiveHours };
    };

    const addCapacityForRange = (employee, rangeStart, rangeEnd, mode) => {
      if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) return;
      const row = ensureRow(employee, employee.source);
      getWorkingDaysDates(rangeStart, rangeEnd).forEach((day) => {
        const capacity = getEmployeeDayCapacity(employee, day);
        if (!capacity) return;
        const monthIndex = day.getMonth();
        const month = monthStats[monthIndex];
        const rowMonth = row.months[monthIndex];
        if (!month || !rowMonth || day.getFullYear() !== year) return;

        if (mode === "elapsed") {
          month.expectedHours += capacity.expectedHours;
          month.productiveHours += capacity.productiveHours;
          rowMonth.expectedHours += capacity.expectedHours;
          rowMonth.productiveHours += capacity.productiveHours;
          row.expectedToDate += capacity.expectedHours;
          row.productiveToDate += capacity.productiveHours;
          totalsAcc.expectedToDate += capacity.expectedHours;
          totalsAcc.productiveToDate += capacity.productiveHours;
          totalsAcc.nominalProductiveToDate += capacity.nominalProductiveHours;
          if (employee.source === "existing") {
            totalsAcc.trackedExpectedToDate += capacity.expectedHours;
          }
          return;
        }

        month.futureProductiveHours += capacity.productiveHours;
        row.productiveFuture += capacity.productiveHours;
        totalsAcc.productiveFuture += capacity.productiveHours;
        totalsAcc.nominalProductiveFuture += capacity.nominalProductiveHours;
      });
    };

    allEmployees.forEach((employee) => {
      ensureRow(employee, employee.source);
      monthRanges.forEach((month) => {
        if (hasElapsed) {
          const rangeEnd = month.end < elapsedEnd ? month.end : elapsedEnd;
          if (rangeEnd >= month.start) {
            addCapacityForRange(employee, month.start, rangeEnd, "elapsed");
          }
        }
        if (futureStart && month.end >= futureStart) {
          const rangeStart = month.start > futureStart ? month.start : futureStart;
          if (rangeStart <= month.end) {
            addCapacityForRange(employee, rangeStart, month.end, "future");
          }
        }
      });
    });

    Object.entries(employeeHoursById || {}).forEach(([employeeId, response]) => {
      const employee =
        employeeById.get(String(employeeId)) || {
          id: employeeId,
          name: `Employe ${employeeId}`,
        };
      const row = ensureRow(employee, "existing");
      Object.entries(response?.hours_by_day || {}).forEach(([dateKey, projectsById]) => {
        const day = parseDate(dateKey);
        if (!day || day.getFullYear() !== year) return;
        if (!hasElapsed || day < yearStart || day > elapsedEnd) return;
        const monthIndex = day.getMonth();
        const month = monthStats[monthIndex];
        const rowMonth = row.months[monthIndex];
        if (!month || !rowMonth) return;

        Object.entries(projectsById || {}).forEach(([projectId, rawHours]) => {
          const hours = Number(rawHours || 0);
          if (!Number.isFinite(hours) || hours <= 0) return;
          const isNonProduction = nonProductionProjectIds.has(String(projectId));
          row.rawActualToDate += hours;
          totalsAcc.rawActualToDate += hours;
          if (isNonProduction) {
            row.rawNonProductionHours += hours;
            totalsAcc.rawNonProductionHours += hours;
          }

          const weightedHours = hours * getEmployeeProductionRateMultiplier(employee);
          if (Number.isFinite(weightedHours) && weightedHours > 0) {
            row.actualToDate += weightedHours;
            rowMonth.actualHours += weightedHours;
            month.actualHours += weightedHours;
            totalsAcc.actualToDate += weightedHours;
            if (isNonProduction) {
              row.nonProductionHours += weightedHours;
              rowMonth.nonProductionHours += weightedHours;
              month.nonProductionHours += weightedHours;
              totalsAcc.nonProductionHours += weightedHours;
            }
          }
        });
      });
    });

    const sumIterationChargeBetween = (rangeStart, rangeEnd) => {
      if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) return 0;
      return scenarioIterations.reduce((sum, iteration) => {
        const start = parseDate(iteration.start_date);
        const end = parseDate(iteration.end_date);
        if (!start || !end || start > end) return sum;
        const totalWorkingDays = getWorkingDaysDates(start, end).length;
        if (totalWorkingDays <= 0) return sum;
        const overlapStart = start > rangeStart ? start : rangeStart;
        const overlapEnd = end < rangeEnd ? end : rangeEnd;
        if (overlapStart > overlapEnd) return sum;
        const overlapWorkingDays = getWorkingDaysDates(overlapStart, overlapEnd).length;
        if (overlapWorkingDays <= 0) return sum;
        const chargeTotal = computeChargeHours(
          iteration,
          Number(iteration.valeur || 0),
          Number(iteration.forecast_ca_eur || 0),
          Number(iteration.forecast_purchase_eur || 0)
        );
        return sum + chargeTotal * (overlapWorkingDays / totalWorkingDays);
      }, 0);
    };

    const elapsedPlannedCharge = hasElapsed
      ? sumIterationChargeBetween(yearStart, elapsedEnd)
      : 0;
    const futurePlannedCharge = futureStart
      ? sumIterationChargeBetween(futureStart, yearEnd)
      : 0;
    const observedEfficiencyPct =
      totalsAcc.nominalProductiveToDate > 0
        ? (elapsedPlannedCharge / totalsAcc.nominalProductiveToDate) * 100
        : NaN;
    const producedCaToDate = Number(productionProgress.totals.done || 0);
    const remainingCa = Number(productionProgress.totals.remaining || 0);
    const actualProductionHours = Math.max(
      0,
      totalsAcc.actualToDate - totalsAcc.nonProductionHours
    );
    const rawProductionProjectHours = Math.max(
      0,
      totalsAcc.rawActualToDate - totalsAcc.rawNonProductionHours
    );
    const realNonProductionPct =
      rawProductionProjectHours > 0
        ? (totalsAcc.rawNonProductionHours / rawProductionProjectHours) * 100
        : NaN;
    const currentCaPerProductiveHour =
      totalsAcc.productiveToDate > 0 ? producedCaToDate / totalsAcc.productiveToDate : NaN;
    const currentCaPerEnteredProductionHour =
      actualProductionHours > 0 ? producedCaToDate / actualProductionHours : NaN;
    const requiredFutureCaPerHour =
      totalsAcc.productiveFuture > 0 ? remainingCa / totalsAcc.productiveFuture : NaN;

    const employeeRows = Array.from(rowsByKey.values())
      .map((row) => {
        const rawProductionProjectHours = Math.max(
          0,
          row.rawActualToDate - row.rawNonProductionHours
        );
        const realNonProductionPct =
          rawProductionProjectHours > 0
            ? (row.rawNonProductionHours / rawProductionProjectHours) * 100
            : NaN;
        const months = row.months.map((month) => ({
          ...month,
          gapHours: month.actualHours - month.expectedHours,
          enteredPct:
            month.expectedHours > 0
              ? (month.actualHours / month.expectedHours) * 100
              : month.actualHours > 0
              ? 100
              : 0,
        }));
        return {
          ...row,
          months,
          gapHours: row.actualToDate - row.expectedToDate,
          missingHours: Math.max(0, row.expectedToDate - row.actualToDate),
          enteredPct:
            row.expectedToDate > 0
              ? (row.actualToDate / row.expectedToDate) * 100
              : row.actualToDate > 0
              ? 100
              : 0,
          nonProductionPct:
            row.actualToDate > 0 ? (row.nonProductionHours / row.actualToDate) * 100 : 0,
          rawProductionProjectHours,
          realNonProductionPct,
        };
      })
      .filter(
        (row) =>
          row.expectedToDate > 0 ||
          row.actualToDate > 0 ||
          row.rawActualToDate > 0 ||
          row.productiveFuture > 0
      )
      .sort((a, b) => {
        const missingDiff = b.missingHours - a.missingHours;
        if (Math.abs(missingDiff) > 0.01) return missingDiff;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });

    const monthlyData = monthStats.map((month) => ({
      ...month,
      productionActualHours: Math.max(0, month.actualHours - month.nonProductionHours),
      gapHours: month.actualHours - month.expectedHours,
      enteredPct:
        month.expectedHours > 0
          ? (month.actualHours / month.expectedHours) * 100
          : month.actualHours > 0
          ? 100
          : 0,
    }));

    return {
      hasElapsed,
      yearStart,
      yearEnd,
      elapsedEnd,
      futureStart,
      nonProductionProjectName: nonProductionProject
        ? formatProjectOptionLabel(nonProductionProject)
        : `Projet ${NON_PRODUCTION_PROJECT_FALLBACK_ID}`,
      monthlyData,
      employeeRows,
      efficiencyBars: [
        {
          label: "CA/h actuel",
          value: Number.isFinite(currentCaPerProductiveHour)
            ? currentCaPerProductiveHour
            : 0,
        },
        {
          label: "CA/h requis futur",
          value: Number.isFinite(requiredFutureCaPerHour)
            ? requiredFutureCaPerHour
            : 0,
        },
      ],
      totals: {
        ...totalsAcc,
        producedCaToDate,
        remainingCa,
        actualProductionHours,
        rawProductionProjectHours,
        realNonProductionPct,
        observedEfficiencyPct,
        scenarioEfficiencyPct: scenarioEfficiencyMultiplier * 100,
        currentCaPerProductiveHour,
        currentCaPerEnteredProductionHour,
        requiredFutureCaPerHour,
        caPerHourGap:
          Number.isFinite(requiredFutureCaPerHour) &&
          Number.isFinite(currentCaPerProductiveHour)
            ? requiredFutureCaPerHour - currentCaPerProductiveHour
            : NaN,
        entryRate:
          totalsAcc.trackedExpectedToDate > 0
            ? (totalsAcc.actualToDate / totalsAcc.trackedExpectedToDate) * 100
            : 0,
        nonProductionPct:
          totalsAcc.actualToDate > 0
            ? (totalsAcc.nonProductionHours / totalsAcc.actualToDate) * 100
            : 0,
        elapsedPlannedCharge,
        futurePlannedCharge,
        elapsedLoadPct:
          totalsAcc.productiveToDate > 0
            ? (elapsedPlannedCharge / totalsAcc.productiveToDate) * 100
            : 0,
        futureLoadPct:
          totalsAcc.productiveFuture > 0
            ? (futurePlannedCharge / totalsAcc.productiveFuture) * 100
            : 0,
      },
    };
  }, [
    availableProjects,
    employeeHoursById,
    employees,
    monthRanges,
    productionProgress.totals.done,
    productionProgress.totals.remaining,
    scenarioConfig,
    scenarioEfficiencyMultiplier,
    scenarioIterations,
    year,
  ]);

  const factoredInvoiceIdSet = useMemo(
    () => new Set((scenarioConfig.factoredInvoiceIds || []).map(keyOf)),
    [scenarioConfig.factoredInvoiceIds]
  );

  const cashFlowEntriesByMonth = useMemo(() => {
    const entriesByMonth = new Map(monthRanges.map((month) => [month.key, []]));

    const pushEntry = (dateValue, entry) => {
      const eventDate = parseDate(dateValue);
      if (!eventDate) return;
      const monthKey = getMonthKeyForDate(eventDate);
      if (!entriesByMonth.has(monthKey)) return;
      entriesByMonth.get(monthKey).push({
        ...entry,
        date: toDateKey(eventDate),
        monthKey,
      });
    };

    billingCashEvents
      .filter((event) => event.status !== "Annule")
      .forEach((event) => {
        const amount = Number(event.amount_eur || 0);
        if (amount <= 0) return;
        const direction = event.direction === "outflow" ? "outflow" : "inflow";
        const originalPaymentDate = parseDate(event.effective_event_date || event.event_date || event.issue_date);
        const { date: issueDate, isEstimated: issueDateIsEstimated } =
          direction !== "outflow"
            ? getInvoiceIssueDateInfo(event, originalPaymentDate)
            : { date: parseDate(event.issue_date), isEstimated: false };
        const useFactoringForEvent =
          direction !== "outflow" &&
          factoredInvoiceIdSet.has(keyOf(event.id)) &&
          isInvoiceFactorable(issueDate, originalPaymentDate);
        const eventDate = useFactoringForEvent ? issueDate : originalPaymentDate;
        const allocations = (event.line_allocations || []).length
          ? (event.line_allocations || []).map((allocation) => ({
              label: allocation.line_name || `Ligne ${allocation.line_id}`,
              amount: Number(allocation.allocated_amount_eur || 0),
            }))
          : event.line_name
            ? [{ label: event.line_name, amount }]
            : [];
        pushEntry(eventDate, {
          id: `cash-${event.id}`,
          source: "cash_event",
          direction,
          amount,
          signedAmount: direction === "outflow" ? -amount : amount,
          title: event.invoice_reference || event.label || "Facture",
          clientType: normalizeClientType(event.client_type),
          subtitle: [
            event.batch_reference,
            event.client_name,
            issueDate ? `Emission ${formatDateShort(issueDate)}${issueDateIsEstimated ? " estimee" : ""}` : null,
            useFactoringForEvent
              ? `Factor: paiement a l'emission`
              : event.payment_terms,
          ].filter(Boolean).join(" - "),
          status: formatInvoiceStatus(event.status),
          eventType: event.event_type,
          invoiceReference: event.invoice_reference,
          issueDate,
          issueDateIsEstimated,
          originalPaymentDate,
          paymentDate: eventDate,
          isFactored: useFactoringForEvent,
          isEstimatedDate: !useFactoringForEvent && !event.event_date && Boolean(event.effective_event_date),
          allocations,
        });
      });

    scenarioIterations.forEach((iteration) => {
      const project =
        availableProjects.find((item) => String(item.id) === String(iteration.project_id)) || null;
      const iterationStart = parseDate(iteration.start_date);
      const outflowAmount = Number(
        iteration.forecast_purchase_eur || 0
      );
      if (iterationStart && outflowAmount > 0) {
        pushEntry(iterationStart, {
          id: `iteration-outflow-${iteration.id}`,
          source: "iteration_expense",
          direction: "outflow",
          amount: outflowAmount,
          signedAmount: -outflowAmount,
          title: `Depenses prev. - ${iteration.name || `Iteration ${iteration.id}`}`,
          clientType: getProjectClientType(project),
          subtitle: [project?.project_number, project?.name].filter(Boolean).join(" - "),
          status: "Prevu",
          eventType: "Achat",
          isEstimatedDate: true,
          allocations: [],
        });
      }
    });

    entriesByMonth.forEach((entries) => {
      entries.sort((left, right) => {
        const dateCompare = String(left.date || "").localeCompare(String(right.date || ""));
        if (dateCompare !== 0) return dateCompare;
        return Math.abs(right.amount || 0) - Math.abs(left.amount || 0);
      });
    });

    return entriesByMonth;
  }, [monthRanges, scenarioIterations, availableProjects, billingCashEvents, factoredInvoiceIdSet]);

  const cashFlowByMonth = useMemo(() => {
    const data = monthRanges.map((month) => {
      const entries = cashFlowEntriesByMonth.get(month.key) || [];
      const inflow = entries
        .filter((entry) => entry.direction !== "outflow")
        .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
      const outflow = entries
        .filter((entry) => entry.direction === "outflow")
        .reduce((sum, entry) => sum - Number(entry.amount || 0), 0);
      const byClientType = CLIENT_TYPE_SEGMENTS.reduce((acc, segment) => {
        acc[segment.id] = { inflow: 0, outflow: 0, net: 0 };
        return acc;
      }, {});
      entries.forEach((entry) => {
        const clientType = normalizeClientType(entry.clientType);
        if (entry.direction === "outflow") {
          byClientType[clientType].outflow -= Number(entry.amount || 0);
        } else {
          byClientType[clientType].inflow += Number(entry.amount || 0);
        }
        byClientType[clientType].net =
          byClientType[clientType].inflow + byClientType[clientType].outflow;
      });
      const row = {
        key: month.key,
        label: month.label,
        inflow,
        outflow,
        net: inflow + outflow,
        byClientType,
      };
      CLIENT_TYPE_SEGMENTS.forEach((segment) => {
        row[`${segment.id}Inflow`] = byClientType[segment.id].inflow;
        row[`${segment.id}Outflow`] = byClientType[segment.id].outflow;
        row[`${segment.id}Net`] = byClientType[segment.id].net;
      });
      return row;
    });
    return data;
  }, [monthRanges, cashFlowEntriesByMonth]);

  const cumulativeCashFlowData = useMemo(() => {
    let runningNet = 0;
    let runningInflow = 0;
    let runningOutflow = 0;
    const runningByClientType = CLIENT_TYPE_SEGMENTS.reduce((acc, segment) => {
      acc[segment.id] = { inflow: 0, outflow: 0, net: 0 };
      return acc;
    }, {});
    return cashFlowByMonth.map((month) => {
      const entry = {
        key: month.key,
        label: month.label,
        inflowCumulative: runningInflow + month.inflow,
        outflowCumulative: runningOutflow + month.outflow,
        netCumulative: runningNet + month.net,
      };
      CLIENT_TYPE_SEGMENTS.forEach((segment) => {
        const segmentMonth = month.byClientType?.[segment.id] || {};
        runningByClientType[segment.id].inflow += Number(segmentMonth.inflow || 0);
        runningByClientType[segment.id].outflow += Number(segmentMonth.outflow || 0);
        runningByClientType[segment.id].net += Number(segmentMonth.net || 0);
        entry[`${segment.id}InflowCumulative`] = runningByClientType[segment.id].inflow;
        entry[`${segment.id}OutflowCumulative`] = runningByClientType[segment.id].outflow;
        entry[`${segment.id}NetCumulative`] = runningByClientType[segment.id].net;
      });
      runningInflow = entry.inflowCumulative;
      runningOutflow = entry.outflowCumulative;
      runningNet = entry.netCumulative;
      return entry;
    });
  }, [cashFlowByMonth]);

  const cashFlowTotals = useMemo(() => {
    const totalsByClientType = CLIENT_TYPE_SEGMENTS.reduce((segmentAcc, segment) => {
      segmentAcc[segment.id] = { inflow: 0, outflow: 0, net: 0 };
      return segmentAcc;
    }, {});
    const totalsValue = cashFlowByMonth.reduce(
      (acc, month) => {
        acc.inflow += month.inflow;
        acc.outflow += month.outflow;
        acc.net += month.net;
        CLIENT_TYPE_SEGMENTS.forEach((segment) => {
          const segmentMonth = month.byClientType?.[segment.id] || {};
          totalsByClientType[segment.id].inflow += Number(segmentMonth.inflow || 0);
          totalsByClientType[segment.id].outflow += Number(segmentMonth.outflow || 0);
          totalsByClientType[segment.id].net += Number(segmentMonth.net || 0);
        });
        return acc;
      },
      {
        inflow: 0,
        outflow: 0,
        net: 0,
      }
    );
    totalsValue.byClientType = totalsByClientType;
    return totalsValue;
  }, [cashFlowByMonth]);

  const cumulativeChargeData = useMemo(() => {
    let runningTotal = 0;
    let runningCapacity = 0;
    return projectMonthlyMetrics.map((month, index) => {
      const entry = {
        key: month.key,
        label: month.label,
        total: runningTotal + month.totalCharge,
        capacityTotal: runningCapacity + (metricsByMonth[index]?.capacityHours || 0),
      };
      runningTotal = entry.total;
      runningCapacity = entry.capacityTotal;
      return entry;
    });
  }, [projectMonthlyMetrics, metricsByMonth]);

  const cumulativeChargeSplitData = useMemo(() => {
    let runningActual = 0;
    let runningForecast = 0;
    let runningCapacity = 0;
    return projectMonthlyMetrics.map((month, index) => {
      const entry = {
        key: month.key,
        label: month.label,
        actual: runningActual + month.totalChargeActual,
        forecast: runningForecast + month.totalChargeForecast,
        capacityTotal: runningCapacity + (metricsByMonth[index]?.capacityHours || 0),
      };
      runningActual = entry.actual;
      runningForecast = entry.forecast;
      runningCapacity = entry.capacityTotal;
      return entry;
    });
  }, [projectMonthlyMetrics, metricsByMonth]);

  const cumulativeChargeDetailData = useMemo(() => {
    const runningByProjectActual = {};
    const runningByProjectForecast = {};
    let runningCapacity = 0;
    return projectMonthlyMetrics.map((month, index) => {
      const entry = {
        key: month.key,
        label: month.label,
        capacityTotal: runningCapacity + (metricsByMonth[index]?.capacityHours || 0),
      };
      runningCapacity = entry.capacityTotal;
      projectSeries.forEach((project) => {
        const projectKey = String(project.id);
        const nextActual =
          (runningByProjectActual[projectKey] || 0) + (month.projectChargeActual[projectKey] || 0);
        const nextForecast =
          (runningByProjectForecast[projectKey] || 0) +
          (month.projectChargeForecast[projectKey] || 0);
        runningByProjectActual[projectKey] = nextActual;
        runningByProjectForecast[projectKey] = nextForecast;
        entry[`project_${projectKey}_actual`] = nextActual;
        entry[`project_${projectKey}_forecast`] = nextForecast;
      });
      return entry;
    });
  }, [projectMonthlyMetrics, projectSeries, metricsByMonth]);

  const cumulativeChargeClientTypeData = useMemo(() => {
    const runningByClientType = CLIENT_TYPE_SEGMENTS.reduce((acc, segment) => {
      acc[segment.id] = { actual: 0, forecast: 0 };
      return acc;
    }, {});
    let runningCapacity = 0;
    return projectMonthlyMetrics.map((month, index) => {
      const entry = {
        key: month.key,
        label: month.label,
        capacityTotal: runningCapacity + (metricsByMonth[index]?.capacityHours || 0),
      };
      runningCapacity = entry.capacityTotal;
      CLIENT_TYPE_SEGMENTS.forEach((segment) => {
        runningByClientType[segment.id].actual += Number(
          month.clientTypeChargeActual[segment.id] || 0
        );
        runningByClientType[segment.id].forecast += Number(
          month.clientTypeChargeForecast[segment.id] || 0
        );
        entry[`${segment.id}Actual`] = runningByClientType[segment.id].actual;
        entry[`${segment.id}Forecast`] = runningByClientType[segment.id].forecast;
      });
      return entry;
    });
  }, [projectMonthlyMetrics, metricsByMonth]);

  const cumulativeExpenseData = useMemo(() => {
    const runningByProject = {};
    let runningTotal = 0;
    return projectMonthlyMetrics.map((month) => {
      const entry = {
        key: month.key,
        label: month.label,
        total: runningTotal + month.totalExpenses,
      };
      runningTotal = entry.total;
      projectSeries.forEach((project) => {
        const projectKey = String(project.id);
        const nextValue = (runningByProject[projectKey] || 0) + (month.projectExpenses[projectKey] || 0);
        runningByProject[projectKey] = nextValue;
        entry[`project_${projectKey}`] = nextValue;
      });
      return entry;
    });
  }, [projectMonthlyMetrics, projectSeries]);

  const monthlyChargeData = useMemo(() => {
    return projectMonthlyMetrics.map((month, index) => ({
      key: month.key,
      label: month.label,
      total: month.totalCharge,
      actual: month.totalChargeActual,
      forecast: month.totalChargeForecast,
      capacity: metricsByMonth[index]?.capacityHours || 0,
      events: eventByMonthKey.get(month.key) || [],
    }));
  }, [projectMonthlyMetrics, metricsByMonth, eventByMonthKey]);

  const monthlyChargeDetailData = useMemo(() => {
    return projectMonthlyMetrics.map((month, index) => {
      const entry = {
        key: month.key,
        label: month.label,
        capacity: metricsByMonth[index]?.capacityHours || 0,
        events: eventByMonthKey.get(month.key) || [],
      };
      projectSeries.forEach((project) => {
        const projectKey = String(project.id);
        entry[`project_${projectKey}_actual`] = month.projectChargeActual[projectKey] || 0;
        entry[`project_${projectKey}_forecast`] = month.projectChargeForecast[projectKey] || 0;
      });
      return entry;
    });
  }, [projectMonthlyMetrics, projectSeries, metricsByMonth, eventByMonthKey]);

  const monthlyChargeClientTypeData = useMemo(() => {
    return projectMonthlyMetrics.map((month, index) => {
      const entry = {
        key: month.key,
        label: month.label,
        total: month.totalCharge,
        actual: month.totalChargeActual,
        forecast: month.totalChargeForecast,
        capacity: metricsByMonth[index]?.capacityHours || 0,
        events: eventByMonthKey.get(month.key) || [],
      };
      CLIENT_TYPE_SEGMENTS.forEach((segment) => {
        entry[`${segment.id}Actual`] = month.clientTypeChargeActual[segment.id] || 0;
        entry[`${segment.id}Forecast`] = month.clientTypeChargeForecast[segment.id] || 0;
      });
      return entry;
    });
  }, [projectMonthlyMetrics, metricsByMonth, eventByMonthKey]);

  const selectedMonth = useMemo(() => {
    if (!metricsByMonth.length) return null;
    return metricsByMonth.find((month) => month.key === selectedMonthKey) || metricsByMonth[0];
  }, [metricsByMonth, selectedMonthKey]);

  const selectedCashFlowMonth = useMemo(() => {
    if (!cashFlowByMonth.length) return null;
    return (
      cashFlowByMonth.find((month) => month.key === selectedMonthKey) || cashFlowByMonth[0]
    );
  }, [cashFlowByMonth, selectedMonthKey]);

  const selectedCashFlowEntries = useMemo(() => {
    const monthKey = selectedMonthKey || selectedCashFlowMonth?.key;
    if (!monthKey) return [];
    return cashFlowEntriesByMonth.get(monthKey) || [];
  }, [cashFlowEntriesByMonth, selectedCashFlowMonth?.key, selectedMonthKey]);

  const selectedProductionProgressMonth = useMemo(() => {
    if (!productionProgress.cumulativeData.length) return null;
    return (
      productionProgress.cumulativeData.find((month) => month.key === selectedMonthKey) ||
      productionProgress.cumulativeData[0]
    );
  }, [productionProgress.cumulativeData, selectedMonthKey]);

  const selectedCapacityProjectionMonth = useMemo(() => {
    if (!productionCapacityProjection.monthlyData.length) return null;
    return (
      productionCapacityProjection.monthlyData.find((month) => month.key === selectedMonthKey) ||
      productionCapacityProjection.monthlyData[0]
    );
  }, [productionCapacityProjection.monthlyData, selectedMonthKey]);

  const invoiceTimelineData = useMemo(() => {
    const rangeStart = monthRanges[0]?.start;
    const rangeEnd = monthRanges[monthRanges.length - 1]?.end;
    const emptyTotals = {
      amount: 0,
      planned: 0,
      invoiced: 0,
      count: 0,
      acceleratedDays: 0,
      factorable: 0,
      factored: 0,
      factorableAmount: 0,
      factoredAmount: 0,
    };
    if (!rangeStart || !rangeEnd) {
      return { entries: [], totals: emptyTotals, laneCount: 0, timelineHeight: 220 };
    }

    const rangeMs = Math.max(1, rangeEnd.getTime() - rangeStart.getTime());
    const clampDate = (date) => {
      if (!date) return null;
      if (date < rangeStart) return rangeStart;
      if (date > rangeEnd) return rangeEnd;
      return date;
    };
    const datePercent = (date) => {
      const clamped = clampDate(date);
      if (!clamped) return 0;
      return Math.max(0, Math.min(100, ((clamped.getTime() - rangeStart.getTime()) / rangeMs) * 100));
    };
    const overlapsRange = (left, right) => {
      if (!left && !right) return false;
      const start = left && right ? (left < right ? left : right) : left || right;
      const end = left && right ? (left > right ? left : right) : left || right;
      return start <= rangeEnd && end >= rangeStart;
    };

    const rawEntries = billingCashEvents
      .filter((event) => event.direction !== "outflow" && event.status !== "Annule")
      .map((event) => {
        const originalPaymentDate = parseDate(event.effective_event_date || event.event_date || event.issue_date);
        const { date: issueDate, isEstimated: issueDateIsEstimated } =
          getInvoiceIssueDateInfo(event, originalPaymentDate);
        if (!overlapsRange(issueDate, originalPaymentDate)) return null;

        const amount = Number(event.amount_eur || 0);
        if (amount <= 0) return null;
        const delayDays = getDateDelayDays(issueDate, originalPaymentDate);
        const canFactor = isInvoiceFactorable(issueDate, originalPaymentDate);
        const isFactored = canFactor && factoredInvoiceIdSet.has(keyOf(event.id));
        const scenarioPaymentDate =
          isFactored && issueDate ? issueDate : originalPaymentDate;
        const issuePct = datePercent(issueDate);
        const originalPaymentPct = datePercent(originalPaymentDate);
        const scenarioPaymentPct = datePercent(scenarioPaymentDate);
        const acceleratedDays =
          isFactored && Number.isFinite(delayDays)
            ? Math.max(0, delayDays)
            : 0;
        const allocations = (event.line_allocations || []).length
          ? event.line_allocations.map((allocation) => ({
              label: allocation.line_name || `Ligne ${allocation.line_id}`,
              amount: Number(allocation.allocated_amount_eur || 0),
            }))
          : event.line_name
            ? [{ label: event.line_name, amount }]
            : [];
        return {
          id: event.id,
          title: event.invoice_reference || event.label || `Facture ${event.id}`,
          invoiceReference: event.invoice_reference || "",
          batchReference: event.batch_reference || "",
          batchName: event.batch_name || "",
          clientName: event.client_name || "",
          clientType: normalizeClientType(event.client_type),
          subtitle: [event.batch_reference, event.batch_name, event.client_name].filter(Boolean).join(" - "),
          projectLabel: [
            event.issue_project_number,
            event.issue_project_name,
            event.issue_iteration_name,
          ].filter(Boolean).join(" / "),
          amount,
          status: formatInvoiceStatus(event.status),
          rawStatus: event.status,
          paymentTerms: event.payment_terms,
          issueDate,
          issueDateIsEstimated,
          originalPaymentDate,
          scenarioPaymentDate,
          issueDateKey: toDateKey(issueDate),
          originalPaymentDateKey: toDateKey(originalPaymentDate),
          scenarioPaymentDateKey: toDateKey(scenarioPaymentDate),
          issuePct,
          originalPaymentPct,
          scenarioPaymentPct,
          cashLineLeftPct: Math.min(issuePct, scenarioPaymentPct),
          cashLineWidthPct: Math.max(0.3, Math.abs(scenarioPaymentPct - issuePct)),
          delayDays,
          acceleratedDays,
          canFactor,
          isFactored: Boolean(isFactored),
          isPlanned: isPlannedInvoiceStatus(event.status),
          isSent: isSentInvoiceStatus(event.status),
          allocations,
        };
      })
      .filter(Boolean)
      .sort((left, right) => {
        const leftDate = left.scenarioPaymentDate || left.originalPaymentDate || left.issueDate;
        const rightDate = right.scenarioPaymentDate || right.originalPaymentDate || right.issueDate;
        const dateCompare = toDateKey(leftDate).localeCompare(toDateKey(rightDate));
        if (dateCompare !== 0) return dateCompare;
        return right.amount - left.amount;
      });

    const laneEnds = [];
    const minimumGapPct = 1.8;
    const entries = rawEntries.map((entry) => {
      const markerPct = Number.isFinite(entry.scenarioPaymentPct)
        ? entry.scenarioPaymentPct
        : 0;
      let laneIndex = laneEnds.findIndex((lastPct) => markerPct - lastPct >= minimumGapPct);
      if (laneIndex === -1) {
        laneIndex = laneEnds.length;
        laneEnds.push(-Infinity);
      }
      laneEnds[laneIndex] = markerPct;
      return {
        ...entry,
        laneIndex,
        laneTop: 62 + laneIndex * 30,
      };
    });

    const totals = entries.reduce(
      (acc, entry) => {
        acc.amount += entry.amount;
        acc.count += 1;
        acc.acceleratedDays += entry.acceleratedDays || 0;
        if (entry.canFactor) {
          acc.factorable += 1;
          acc.factorableAmount += entry.amount;
        }
        if (entry.isFactored) {
          acc.factored += 1;
          acc.factoredAmount += entry.amount;
        }
        if (entry.isPlanned) {
          acc.planned += entry.amount;
        } else if (entry.isSent) {
          acc.invoiced += entry.amount;
        }
        return acc;
      },
      { ...emptyTotals }
    );

    const laneCount = Math.max(1, laneEnds.length);
    return {
      entries,
      totals,
      laneCount,
      timelineHeight: Math.max(220, 112 + laneCount * 30),
    };
  }, [billingCashEvents, factoredInvoiceIdSet, monthRanges]);

  const invoiceTimelineEntriesByMonth = useMemo(() => {
    const groups = monthRanges.map((month) => ({
      ...month,
      entries: [],
      amount: 0,
      planned: 0,
      invoiced: 0,
      factored: 0,
      factorable: 0,
    }));
    const groupByKey = new Map(groups.map((group) => [group.key, group]));

    invoiceTimelineData.entries.forEach((invoice) => {
      const scenarioDate = parseDate(
        invoice.scenarioPaymentDateKey || invoice.originalPaymentDateKey || invoice.issueDateKey
      );
      if (!scenarioDate) return;
      const group = groupByKey.get(getMonthKeyForDate(scenarioDate));
      if (!group) return;
      group.entries.push(invoice);
      group.amount += invoice.amount;
      if (invoice.isPlanned) group.planned += invoice.amount;
      if (invoice.isSent) group.invoiced += invoice.amount;
      if (invoice.isFactored) group.factored += 1;
      if (invoice.canFactor) group.factorable += 1;
    });

    return groups.filter((group) => group.entries.length > 0);
  }, [invoiceTimelineData.entries, monthRanges]);

  const accountingCoverage = useMemo(() => {
    const rangeStart = monthRanges[0]?.start;
    const rangeEnd = monthRanges[monthRanges.length - 1]?.end;
    const empty = {
      rows: [],
      unlinkedEntries: [],
      totals: {
        production: projectAnnualContribution.totals.ca,
        invoices: 0,
        linkedInvoices: 0,
        unlinkedInvoices: 0,
        planned: 0,
        invoiced: 0,
        gapVsInvoices: projectAnnualContribution.totals.ca,
        gapVsLinkedInvoices: projectAnnualContribution.totals.ca,
        linkedPct: 0,
        coveragePct: 0,
      },
    };
    if (!rangeStart || !rangeEnd) return empty;

    const overlapsYear = (left, right) => {
      if (!left && !right) return false;
      const start = left && right ? (left < right ? left : right) : left || right;
      const end = left && right ? (left > right ? left : right) : left || right;
      return start <= rangeEnd && end >= rangeStart;
    };

    const projectRows = new Map(
      projectAnnualContribution.projects.map((project) => [
        String(project.id),
        {
          id: project.id,
          name: project.name,
          color: project.color,
          production: Number(project.ca || 0),
          invoices: 0,
          planned: 0,
          invoiced: 0,
        },
      ])
    );

    const ensureProjectRow = (allocation) => {
      const projectId = allocation.project_id;
      const projectKey = String(projectId);
      if (!projectRows.has(projectKey)) {
        const label = [
          allocation.project_number,
          allocation.project_name || `Projet ${projectKey}`,
        ]
          .filter(Boolean)
          .join(" - ");
        projectRows.set(projectKey, {
          id: projectId,
          name: label,
          color: "#64748b",
          production: 0,
          invoices: 0,
          planned: 0,
          invoiced: 0,
        });
      }
      return projectRows.get(projectKey);
    };

    const totals = {
      production: projectAnnualContribution.totals.ca,
      invoices: 0,
      linkedInvoices: 0,
      unlinkedInvoices: 0,
      planned: 0,
      invoiced: 0,
    };
    const unlinkedEntries = [];

    billingCashEvents
      .filter((event) => event.direction !== "outflow" && event.status !== "Annule")
      .forEach((event) => {
        const amount = Number(event.amount_eur || 0);
        if (amount <= 0) return;
        const paymentDate = parseDate(event.effective_event_date || event.event_date || event.issue_date);
        const { date: issueDate } = getInvoiceIssueDateInfo(event, paymentDate);
        if (!overlapsYear(issueDate, paymentDate)) return;

        totals.invoices += amount;
        if (isSentInvoiceStatus(event.status)) {
          totals.invoiced += amount;
        } else if (isPlannedInvoiceStatus(event.status)) {
          totals.planned += amount;
        }

        const projectAllocations = Array.isArray(event.project_allocations)
          ? event.project_allocations
          : [];
        const fallbackAllocations =
          projectAllocations.length || !event.issue_project_id
            ? projectAllocations
            : [
                {
                  project_id: event.issue_project_id,
                  project_name: event.issue_project_name,
                  project_number: event.issue_project_number,
                  iteration_id: event.issue_iteration_id,
                  iteration_name: event.issue_iteration_name,
                  allocated_amount_eur: amount,
                },
              ];
        const rawLinkedTotal = fallbackAllocations.reduce(
          (sum, allocation) => sum + Math.max(0, Number(allocation.allocated_amount_eur || 0)),
          0
        );
        const scale = rawLinkedTotal > amount && rawLinkedTotal > 0 ? amount / rawLinkedTotal : 1;
        let linkedForEvent = 0;

        fallbackAllocations.forEach((allocation) => {
          if (!allocation?.project_id) return;
          const linkedAmount = Math.max(0, Number(allocation.allocated_amount_eur || 0)) * scale;
          if (linkedAmount <= 0) return;
          const row = ensureProjectRow(allocation);
          row.invoices += linkedAmount;
          linkedForEvent += linkedAmount;
          if (isSentInvoiceStatus(event.status)) {
            row.invoiced += linkedAmount;
          } else if (isPlannedInvoiceStatus(event.status)) {
            row.planned += linkedAmount;
          }
        });

        const unlinkedAmount = Math.max(0, amount - linkedForEvent);
        totals.linkedInvoices += linkedForEvent;
        totals.unlinkedInvoices += unlinkedAmount;
        if (unlinkedAmount > 0.5) {
          unlinkedEntries.push({
            id: event.id,
            label: event.invoice_reference || event.label || `Facture ${event.id}`,
            subtitle: [event.batch_reference, event.batch_name, event.client_name].filter(Boolean).join(" - "),
            amount: unlinkedAmount,
            status: formatInvoiceStatus(event.status),
            date: toDateKey(paymentDate || issueDate),
          });
        }
      });

    const rows = Array.from(projectRows.values())
      .map((row) => ({
        ...row,
        gap: row.production - row.invoices,
        coveragePct: row.production > 0 ? (row.invoices / row.production) * 100 : 0,
      }))
      .filter((row) => row.production > 0 || row.invoices > 0)
      .sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap));

    return {
      rows,
      unlinkedEntries: unlinkedEntries.sort((a, b) => b.amount - a.amount),
      totals: {
        ...totals,
        gapVsInvoices: totals.production - totals.invoices,
        gapVsLinkedInvoices: totals.production - totals.linkedInvoices,
        linkedPct: totals.invoices > 0 ? (totals.linkedInvoices / totals.invoices) * 100 : 0,
        coveragePct: totals.production > 0 ? (totals.linkedInvoices / totals.production) * 100 : 0,
      },
    };
  }, [billingCashEvents, monthRanges, projectAnnualContribution]);

  const selectedMonthIndex = useMemo(() => {
    if (!metricsByMonth.length) return -1;
    return metricsByMonth.findIndex((month) => month.key === selectedMonthKey);
  }, [metricsByMonth, selectedMonthKey]);

  const selectedMonthProjects = useMemo(() => {
    if (selectedMonthIndex < 0) return [];
    const monthData = projectMonthlyMetrics[selectedMonthIndex];
    if (!monthData) return [];
    return projectSeries
      .map((project) => {
        const projectKey = String(project.id);
        const chargeActual = monthData.projectChargeActual[projectKey] || 0;
        const chargeForecast = monthData.projectChargeForecast[projectKey] || 0;
        const caActual = monthData.projectCaActual[projectKey] || 0;
        const caForecast = monthData.projectCaForecast[projectKey] || 0;
        const expenses = monthData.projectExpenses[projectKey] || 0;
        const chargeTotal = chargeActual + chargeForecast;
        return {
          id: project.id,
          name: project.name,
          color: project.color,
          clientType: normalizeClientType(project.clientType),
          chargeActual,
          chargeForecast,
          chargeTotal,
          caActual,
          caForecast,
          caTotal: caActual + caForecast,
          expenses,
        };
      })
      .filter((item) => item.chargeTotal > 0 || item.caTotal > 0 || item.expenses > 0)
      .sort((a, b) => b.chargeTotal - a.chargeTotal);
  }, [projectMonthlyMetrics, projectSeries, selectedMonthIndex]);

  const selectedMonthProjectChartHeight = Math.max(
    256,
    selectedMonthProjects.length * 58
  );

  const selectedRatio =
    selectedMonth && selectedMonth.capacityHours > 0
      ? (selectedMonth.chargeHours / selectedMonth.capacityHours) * 100
      : 0;

  const scenarioStats = {
    overrides: (scenarioConfig.employeeOverrides || []).length,
    additions: (scenarioConfig.employeeAdditions || []).length,
    timeOffs: (scenarioConfig.employeeTimeOffs || []).length,
    excludedProjects: projectExclusions.size,
  };

  const exportPilotageCsv = useCallback(async () => {
    setExportingCsv(true);
    try {
      const { hoursById, failures } = await getEmployeeHoursForExport();
      const pointedHoursByMonth = buildPointedHoursByMonth(hoursById, monthRanges, year);
      const progressByKey = new Map(
        (productionProgress.monthlyData || []).map((month) => [month.key, month])
      );
      const capacityByKey = new Map(
        (productionCapacityProjection.monthlyData || []).map((month) => [month.key, month])
      );
      const cashByKey = new Map(cashFlowByMonth.map((month) => [month.key, month]));
      const invoiceIssuedAmountByMonth = monthRanges.reduce((acc, month) => {
        acc[month.key] = 0;
        return acc;
      }, {});
      const invoiceIssuedAmountByClientTypeMonth = CLIENT_TYPE_SEGMENTS.reduce((acc, segment) => {
        acc[segment.id] = monthRanges.reduce((monthAcc, month) => {
          monthAcc[month.key] = 0;
          return monthAcc;
        }, {});
        return acc;
      }, {});
      const invoicesForCsv = [...(invoiceTimelineData.entries || [])].sort((left, right) => {
        const leftDate = left.issueDateKey || left.scenarioPaymentDateKey || left.originalPaymentDateKey || "";
        const rightDate = right.issueDateKey || right.scenarioPaymentDateKey || right.originalPaymentDateKey || "";
        const dateCompare = String(leftDate).localeCompare(String(rightDate));
        if (dateCompare !== 0) return dateCompare;
        return String(left.title || "").localeCompare(String(right.title || ""));
      });
      invoicesForCsv.forEach((invoice) => {
        const issueDate = parseDate(invoice.issueDateKey);
        if (!issueDate) return;
        const monthKey = getMonthKeyForDate(issueDate);
        if (!(monthKey in invoiceIssuedAmountByMonth)) return;
        const amount = Number(invoice.amount || 0);
        const clientType = normalizeClientType(invoice.clientType);
        invoiceIssuedAmountByMonth[monthKey] += amount;
        invoiceIssuedAmountByClientTypeMonth[clientType][monthKey] += amount;
      });
      const monthLabels = monthRanges.map((month) =>
        month.start.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
      );
      const scenarioLabel =
        scenarioName.trim() ||
        (activeScenarioId === SCENARIO_BASE_ID ? "Base" : `Scenario ${activeScenarioId}`);
      const rows = [
        ["Scenario", scenarioLabel],
        ["Annee", year],
        ["Export", new Date().toLocaleString("fr-FR")],
        ["Detail normal / investissement interne", "oui"],
        [],
        ["Indicateur", ...monthLabels],
      ];
      const addMonthlyRow = (label, getValue) => {
        rows.push([
          label,
          ...monthRanges.map((month, index) => Number(getValue(month, index) || 0)),
        ]);
      };

      addMonthlyRow("CA produit (EUR)", (month) => progressByKey.get(month.key)?.done);
      addMonthlyRow("CA a produire (EUR)", (month) => progressByKey.get(month.key)?.remaining);
      addMonthlyRow(
        "CA futur productible (EUR)",
        (month) => capacityByKey.get(month.key)?.futureProductible
      );
      addMonthlyRow("CA perdu capacite (EUR)", (month) => capacityByKey.get(month.key)?.lostCa);
      addMonthlyRow("CA final projete (EUR)", (month) => capacityByKey.get(month.key)?.finalCa);
      addMonthlyRow(
        "CA total planifie (EUR)",
        (month) => capacityByKey.get(month.key)?.plannedTotal
      );
      addMonthlyRow("Heures budgetees (h)", (_, index) => metricsByMonth[index]?.chargeHours);
      addMonthlyRow(
        "Heures budgetees acquises (h)",
        (_, index) => projectMonthlyMetrics[index]?.totalChargeActual
      );
      addMonthlyRow(
        "Heures budgetees previsionnelles (h)",
        (_, index) => projectMonthlyMetrics[index]?.totalChargeForecast
      );
      addMonthlyRow("Capacite disponible (h)", (_, index) => metricsByMonth[index]?.capacityHours);
      addMonthlyRow(
        "Surcharge capacite (h)",
        (month) => capacityByKey.get(month.key)?.overloadHours
      );
      addMonthlyRow("Heures pointees (h)", (month) => pointedHoursByMonth[month.key]);
      addMonthlyRow(
        "Achats / depenses prevues (EUR)",
        (_, index) => projectMonthlyMetrics[index]?.totalExpenses
      );
      addMonthlyRow("Factures emises (EUR)", (month) => invoiceIssuedAmountByMonth[month.key]);
      addMonthlyRow("Encaissements (EUR)", (month) => cashByKey.get(month.key)?.inflow);
      addMonthlyRow("Decaissements (EUR)", (month) => cashByKey.get(month.key)?.outflow);
      addMonthlyRow("Tresorerie nette (EUR)", (month) => cashByKey.get(month.key)?.net);

      rows.push([]);
      rows.push(["Detail par type client", ...monthLabels]);
      CLIENT_TYPE_SEGMENTS.forEach((segment) => {
        addMonthlyRow(
          `CA produit - ${segment.label} (EUR)`,
          (month) => progressByKey.get(month.key)?.doneByClientType?.[segment.id]
        );
        addMonthlyRow(
          `CA a produire - ${segment.label} (EUR)`,
          (month) => progressByKey.get(month.key)?.remainingByClientType?.[segment.id]
        );
        addMonthlyRow(
          `CA futur productible - ${segment.label} (EUR)`,
          (month) => capacityByKey.get(month.key)?.[`${segment.id}FutureProductible`]
        );
        addMonthlyRow(
          `CA perdu capacite - ${segment.label} (EUR)`,
          (month) => capacityByKey.get(month.key)?.[`${segment.id}LostCa`]
        );
        addMonthlyRow(
          `Heures budgetees - ${segment.label} (h)`,
          (_, index) => projectMonthlyMetrics[index]?.clientTypeCharge?.[segment.id]
        );
        addMonthlyRow(
          `Achats / depenses - ${segment.label} (EUR)`,
          (_, index) => projectMonthlyMetrics[index]?.clientTypeExpenses?.[segment.id]
        );
        addMonthlyRow(
          `Factures emises - ${segment.label} (EUR)`,
          (month) => invoiceIssuedAmountByClientTypeMonth[segment.id]?.[month.key]
        );
        addMonthlyRow(
          `Encaissements - ${segment.label} (EUR)`,
          (month) => cashByKey.get(month.key)?.byClientType?.[segment.id]?.inflow
        );
        addMonthlyRow(
          `Decaissements - ${segment.label} (EUR)`,
          (month) => cashByKey.get(month.key)?.byClientType?.[segment.id]?.outflow
        );
        addMonthlyRow(
          `Tresorerie nette - ${segment.label} (EUR)`,
          (month) => cashByKey.get(month.key)?.byClientType?.[segment.id]?.net
        );
      });

      rows.push([]);
      rows.push(["Factures"]);
      rows.push([
        "Reference",
        "Client",
        "Type client",
        "Statut",
        "Montant (EUR)",
        "Date emission",
        "Emission estimee",
        "Echeance client",
        "Date scenario",
        "Factorisee",
        "Avance factor (jours)",
        "Conditions paiement",
        "Commande",
        "Lot de facturation",
        "Projet / iteration",
        "Lignes",
      ]);
      if (invoicesForCsv.length) {
        invoicesForCsv.forEach((invoice) => {
          rows.push([
            invoice.invoiceReference || invoice.title || `Facture ${invoice.id}`,
            invoice.clientName || "",
            CLIENT_TYPE_SEGMENTS.find((segment) => segment.id === normalizeClientType(invoice.clientType))?.label || "",
            invoice.status || "",
            Number(invoice.amount || 0),
            invoice.issueDateKey || "",
            invoice.issueDateIsEstimated ? "oui" : "non",
            invoice.originalPaymentDateKey || "",
            invoice.scenarioPaymentDateKey || "",
            invoice.isFactored ? "oui" : "non",
            Number(invoice.acceleratedDays || 0),
            invoice.paymentTerms || "",
            invoice.batchReference || "",
            invoice.batchName || "",
            invoice.projectLabel || "",
            (invoice.allocations || [])
              .map((allocation) =>
                `${allocation.label || "Ligne"}: ${formatCsvNumber(allocation.amount || 0)} EUR`
              )
              .join(" | "),
          ]);
        });
      } else {
        rows.push(["Aucune facture sur l'annee"]);
      }

      const csvContent = rows.map(buildCsvRow).join("\r\n");
      const filename = `pilotage_${year}_${sanitizeFilenamePart(scenarioLabel)}.csv`;
      downloadTextFile(filename, csvContent);
      setSaveState({
        type: failures.length ? "info" : "success",
        text: failures.length
          ? `CSV exporte, heures partielles pour ${failures.length} employe(s).`
          : "CSV exporte",
      });
    } catch (err) {
      console.error("Erreur export CSV pilotage:", err);
      setSaveState({ type: "error", text: "Erreur export CSV" });
    } finally {
      setExportingCsv(false);
      setTimeout(() => setSaveState(null), 2500);
    }
  }, [
    activeScenarioId,
    cashFlowByMonth,
    getEmployeeHoursForExport,
    invoiceTimelineData.entries,
    metricsByMonth,
    monthRanges,
    productionCapacityProjection.monthlyData,
    productionProgress.monthlyData,
    projectMonthlyMetrics,
    scenarioName,
    year,
  ]);

  const activeScenarioSavedConfig = useMemo(() => {
    if (activeScenarioId === SCENARIO_BASE_ID) {
      return createDefaultScenarioConfig();
    }
    return (
      scenarioResolutionMap.get(String(activeScenarioId))?.effectiveConfig ||
      createDefaultScenarioConfig()
    );
  }, [activeScenarioId, scenarioResolutionMap]);

  const activeScenarioSavedRecord = useMemo(() => {
    if (activeScenarioId === SCENARIO_BASE_ID) {
      return {
        year: currentYear,
        scenarioName: "",
      };
    }
    const resolution = scenarioResolutionMap.get(String(activeScenarioId));
    return {
      year: Number(resolution?.scenario?.year || currentYear),
      scenarioName: resolution?.scenario?.name || "",
    };
  }, [activeScenarioId, currentYear, scenarioResolutionMap]);

  const activeScenarioSavedSnapshot = useMemo(
    () =>
      buildEditorSnapshot(
        {
          year: activeScenarioSavedRecord.year,
          scenarioName: activeScenarioSavedRecord.scenarioName,
          scenarioConfig: activeScenarioSavedConfig,
        },
        currentYear
      ),
    [activeScenarioSavedConfig, activeScenarioSavedRecord, currentYear]
  );

  const currentEditorSnapshot = useMemo(
    () =>
      buildEditorSnapshot(
        {
          year,
          scenarioName,
          scenarioConfig,
        },
        currentYear
      ),
    [currentYear, scenarioConfig, scenarioName, year]
  );

  const isDirty =
    serializeEditorSnapshot(currentEditorSnapshot, currentYear) !==
    serializeEditorSnapshot(activeScenarioSavedSnapshot, currentYear);

  const canUndo = scenarioPast.length > 0;
  const canRedo = scenarioFuture.length > 0;

  const undoScenarioChange = useCallback(() => {
    setScenarioPast((prevPast) => {
      if (!prevPast.length) return prevPast;
      const previousSnapshot = prevPast[prevPast.length - 1];
      setScenarioFuture((prevFuture) => [getCurrentEditorSnapshot(), ...prevFuture]);
      applyEditorSnapshot(previousSnapshot);
      return prevPast.slice(0, -1);
    });
  }, [applyEditorSnapshot, getCurrentEditorSnapshot]);

  const redoScenarioChange = useCallback(() => {
    setScenarioFuture((prevFuture) => {
      if (!prevFuture.length) return prevFuture;
      const [nextSnapshot, ...remainingFuture] = prevFuture;
      setScenarioPast((prevPast) => [
        ...prevPast.slice(-(EDITOR_HISTORY_LIMIT - 1)),
        getCurrentEditorSnapshot(),
      ]);
      applyEditorSnapshot(nextSnapshot);
      return remainingFuture;
    });
  }, [applyEditorSnapshot, getCurrentEditorSnapshot]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") {
        if (isEditableElement(document.activeElement)) return;
        if (event.shiftKey) {
          if (!canRedo) return;
          event.preventDefault();
          redoScenarioChange();
          return;
        }
        if (!canUndo) return;
        event.preventDefault();
        undoScenarioChange();
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "y") {
        if (isEditableElement(document.activeElement) || !canRedo) return;
        event.preventDefault();
        redoScenarioChange();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [canRedo, canUndo, redoScenarioChange, undoScenarioChange]);

  const handleChartClick = (event) => {
    const payload = event?.activePayload?.[0]?.payload;
    if (payload?.key) {
      setSelectedMonthKey(payload.key);
    }
  };

  const renderMonthlyChargeTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const entry = payload[0]?.payload || {};
    const projectBreakdown = projectSeries
      .map((project) => {
        const projectKey = String(project.id);
        const actual = Number(entry[`project_${projectKey}_actual`] || 0);
        const forecast = Number(entry[`project_${projectKey}_forecast`] || 0);
        return {
          ...project,
          actual,
          forecast,
          total: actual + forecast,
        };
      })
      .filter((project) => project.total > 0)
      .sort((a, b) => b.total - a.total);
    const clientTypeBreakdown = CLIENT_TYPE_SEGMENTS.map((segment) => {
      const actual = Number(entry[`${segment.id}Actual`] || 0);
      const forecast = Number(entry[`${segment.id}Forecast`] || 0);
      return {
        ...segment,
        actual,
        forecast,
        total: actual + forecast,
      };
    }).filter((segment) => segment.total > 0);

    const actual =
      entry.actual !== undefined
        ? Number(entry.actual || 0)
        : projectBreakdown.reduce((sum, project) => sum + project.actual, 0);
    const forecast =
      entry.forecast !== undefined
        ? Number(entry.forecast || 0)
        : projectBreakdown.reduce((sum, project) => sum + project.forecast, 0);
    const total =
      entry.total !== undefined ? Number(entry.total || 0) : actual + forecast;
    const capacity = Number(entry.capacity || 0);
    const remaining = capacity - total;
    const events = Array.isArray(entry.events) ? entry.events : [];

    const rows = [
      ["Charge acquise", actual],
      ["Charge prev.", forecast],
      ["Charge totale", total],
      ["Capacite dispo", capacity],
    ];

    return (
      <div className="max-w-sm rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 shadow-lg">
        <div className="font-semibold text-slate-900">{label}</div>
        <div className="mt-2 space-y-1">
          {rows.map(([rowLabel, value]) => (
            <div key={rowLabel} className="flex items-center justify-between gap-6">
              <span>{rowLabel}</span>
              <span className="font-semibold text-slate-900">
                {formatHours(value)} h
              </span>
            </div>
          ))}
          <div className="flex items-center justify-between gap-6 border-t border-slate-100 pt-1">
            <span>{remaining >= 0 ? "Reste dispo" : "Surcharge"}</span>
            <span
              className={`font-semibold ${
                remaining >= 0 ? "text-emerald-700" : "text-rose-700"
              }`}
            >
              {formatHours(Math.abs(remaining))} h
            </span>
          </div>
        </div>
        {projectBreakdown.length > 0 && (
          <div className="mt-2 border-t border-slate-100 pt-2">
            <div className="mb-1 font-semibold text-slate-700">Projets</div>
            <div className="max-h-40 space-y-1 overflow-y-auto pr-1">
              {projectBreakdown.map((project) => (
                <div key={project.id} className="grid grid-cols-[1fr_auto] gap-3">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: project.color }}
                    />
                    <span className="truncate" title={project.name}>
                      {project.name}
                    </span>
                  </div>
                  <span className="font-semibold text-slate-900">
                    {formatHours(project.total)} h
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {separateInternalInvestment && clientTypeBreakdown.length > 0 && (
          <div className="mt-2 border-t border-slate-100 pt-2">
            <div className="mb-1 font-semibold text-slate-700">Segments client</div>
            <div className="space-y-1">
              {clientTypeBreakdown.map((segment) => (
                <div key={segment.id} className="grid grid-cols-[1fr_auto] gap-3">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: segment.color }}
                    />
                    <span className="truncate">{segment.label}</span>
                  </div>
                  <span className="font-semibold text-slate-900">
                    {formatHours(segment.total)} h
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {events.length > 0 && (
          <div className="mt-2 border-t border-slate-100 pt-2">
            <div className="mb-1 font-semibold text-slate-700">Evenements</div>
            <div className="space-y-1">
              {events.map((event, index) => {
                const meta = eventTypeMeta[event.type] || {
                  color: "#0f172a",
                  label: "Evenement",
                };
                return (
                  <div
                    key={event.id || `${event.type}-${index}`}
                    className="flex items-center gap-1.5"
                  >
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: meta.color }}
                    />
                    <span>
                      {meta.label}:{" "}
                      <span className="font-semibold text-slate-900">
                        {event.name || "Sans nom"}
                      </span>
                      {event.dateLabel ? ` (${event.dateLabel})` : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderProjectChargeTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const project = payload[0]?.payload;
    if (!project) return null;
    return (
      <div className="max-w-xs rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 shadow-lg">
        <div className="font-semibold text-slate-900">{project.name}</div>
        <div className="mt-2 space-y-1">
          <div className="flex items-center justify-between gap-6">
            <span>Charge acquise</span>
            <span className="font-semibold text-slate-900">
              {formatHours(project.chargeActual)} h
            </span>
          </div>
          <div className="flex items-center justify-between gap-6">
            <span>Charge prev.</span>
            <span className="font-semibold text-slate-900">
              {formatHours(project.chargeForecast)} h
            </span>
          </div>
          <div className="flex items-center justify-between gap-6 border-t border-slate-100 pt-1">
            <span>Charge totale</span>
            <span className="font-semibold text-slate-900">
              {formatHours(project.chargeTotal)} h
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderProjectCaTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const project = payload[0]?.payload;
    if (!project) return null;
    return (
      <div className="max-w-xs rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 shadow-lg">
        <div className="font-semibold text-slate-900">{project.name}</div>
        <div className="mt-2 space-y-1">
          <div className="flex items-center justify-between gap-6">
            <span>CA acquis</span>
            <span className="font-semibold text-slate-900">
              {formatCurrency(project.caActual)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-6">
            <span>CA prev.</span>
            <span className="font-semibold text-slate-900">
              {formatCurrency(project.caForecast)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-6 border-t border-slate-100 pt-1">
            <span>CA total</span>
            <span className="font-semibold text-slate-900">
              {formatCurrency(project.caTotal)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderContributionTooltip = ({ active, payload }, valueKey, formatter, total) => {
    if (!active || !payload?.length) return null;
    const project = payload[0]?.payload;
    if (!project) return null;
    const value = Number(project[valueKey] || 0);
    const pct = total > 0 ? (value / total) * 100 : 0;
    return (
      <div className="max-w-xs rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 shadow-lg">
        <div className="font-semibold text-slate-900">{project.name}</div>
        <div className="mt-2 flex items-center justify-between gap-6">
          <span>Contribution</span>
          <span className="font-semibold text-slate-900">{formatter(value)}</span>
        </div>
        <div className="mt-1 flex items-center justify-between gap-6">
          <span>Part annuelle</span>
          <span className="font-semibold text-slate-900">{formatPercent(pct)}</span>
        </div>
      </div>
    );
  };

  const renderInvoiceTimelineTooltip = () => {
    if (!hoveredInvoiceTimelineItem || typeof document === "undefined") return null;
    const { invoice, x, y } = hoveredInvoiceTimelineItem;
    if (!invoice) return null;
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
    const clampedX = Math.min(Math.max(x, 180), Math.max(180, viewportWidth - 180));
    const alternativeDate = invoice.isFactored
      ? invoice.originalPaymentDateKey
      : invoice.issueDateKey;
    const currentLabel = invoice.isFactored ? "Factor" : "Client";
    const alternativeLabel = invoice.isFactored ? "Sans factor" : "Avec factor";

    return ReactDOM.createPortal(
      <div
        className="pointer-events-none z-50 w-80 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 shadow-xl"
        style={{
          position: "fixed",
          left: clampedX,
          top: y,
          transform: "translate(-50%, -100%) translateY(-10px)",
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate font-semibold text-slate-950">{invoice.title}</div>
            <div className="mt-0.5 truncate text-slate-500">
              {invoice.subtitle || invoice.projectLabel || "Commande non rattachee"}
            </div>
          </div>
          <div className="shrink-0 font-semibold text-slate-950">
            {formatCurrency(invoice.amount)}
          </div>
        </div>
        <div className="mt-2 grid grid-cols-[105px_minmax(0,1fr)] gap-y-1">
          <span className="text-slate-400">
            {invoice.issueDateIsEstimated ? "Emission est." : "Emission"}
          </span>
          <span>{formatDateShort(invoice.issueDateKey)}</span>
          <span className="text-slate-400">Echeance client</span>
          <span>{formatDateShort(invoice.originalPaymentDateKey)}</span>
          <span className="text-slate-400">Scenario</span>
          <span>
            {currentLabel}: {formatDateShort(invoice.scenarioPaymentDateKey)}
          </span>
          <span className="text-slate-400">Alternative</span>
          <span>
            {invoice.canFactor
              ? `${alternativeLabel}: ${formatDateShort(alternativeDate)}`
              : "Non factorisable"}
          </span>
          <span className="text-slate-400">Condition</span>
          <span>{invoice.paymentTerms || "-"}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span
            className={`rounded-full px-2 py-0.5 font-semibold ${
              invoice.isPlanned
                ? "bg-amber-100 text-amber-700"
                : "bg-emerald-100 text-emerald-700"
            }`}
          >
            {invoice.status}
          </span>
          {invoice.canFactor && (
            <span className="rounded-full bg-blue-50 px-2 py-0.5 font-semibold text-blue-700">
              {invoice.isFactored ? "Factorisee" : `${invoice.delayDays || 0} j client`}
            </span>
          )}
        </div>
      </div>,
      document.body
    );
  };

  const updateScenarioConfig = (updates) =>
    setScenarioConfig((prev) => ({ ...prev, ...updates }));

  const setInvoiceFactored = (invoiceId, enabled) => {
    setScenarioConfig((prev) => {
      const nextIds = new Set((prev.factoredInvoiceIds || []).map(keyOf));
      const invoiceKey = keyOf(invoiceId);
      if (enabled) {
        nextIds.add(invoiceKey);
      } else {
        nextIds.delete(invoiceKey);
      }
      return {
        ...prev,
        factoredInvoiceIds: Array.from(nextIds),
      };
    });
  };

  const showInvoiceTimelineTooltip = (event, invoice) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHoveredInvoiceTimelineItem({
      invoice,
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  };

  const setProjectIncluded = (projectId, include) => {
    setScenarioConfig((prev) => {
      const overrides = (prev.projectOverrides || []).filter(
        (item) => keyOf(item.project_id) !== keyOf(projectId)
      );
      if (!include) {
        overrides.push({ project_id: projectId, include: false });
      }
      return { ...prev, projectOverrides: overrides };
    });
  };

  const includeAllProjects = () => {
    setScenarioConfig((prev) => ({ ...prev, projectOverrides: [] }));
  };

  const excludeAllProjects = () => {
    setScenarioConfig((prev) => ({
      ...prev,
      projectOverrides: availableProjects.map((project) => ({
        project_id: project.id,
        include: false,
      })),
    }));
  };

  const setEmployeeOverride = (employeeId, updates) => {
    setScenarioConfig((prev) => {
      const overrides = [...(prev.employeeOverrides || [])];
      const index = overrides.findIndex((item) => item.employee_id === employeeId);
      if (!updates) {
        if (index >= 0) overrides.splice(index, 1);
        return { ...prev, employeeOverrides: overrides };
      }
      const next = { ...(index >= 0 ? overrides[index] : { employee_id: employeeId }), ...updates };
      if (
        next.availability_percent === null ||
        next.availability_percent === undefined ||
        next.availability_percent === ""
      ) {
        if (index >= 0) overrides.splice(index, 1);
      } else if (index >= 0) {
        overrides[index] = next;
      } else {
        overrides.push(next);
      }
      return { ...prev, employeeOverrides: overrides };
    });
  };

  const addEmployeeAddition = () => {
    setScenarioConfig((prev) => ({
      ...prev,
      employeeAdditions: [
        ...(prev.employeeAdditions || []),
        {
          local_id: `extra-${Date.now()}`,
          name: "",
          start_date: `${year}-01-01`,
          end_date: "",
          availability_percent: DEFAULT_SCENARIO_AVAILABILITY_PERCENT,
          production_rate: 100,
          weekly_hours: DEFAULT_EMPLOYEE_WEEKLY_HOURS,
        },
      ],
    }));
  };

  const updateEmployeeAddition = (index, updates) => {
    setScenarioConfig((prev) => {
      const additions = [...(prev.employeeAdditions || [])];
      additions[index] = { ...additions[index], ...updates };
      return { ...prev, employeeAdditions: additions };
    });
  };

  const removeEmployeeAddition = (index) => {
    setScenarioConfig((prev) => {
      const additions = [...(prev.employeeAdditions || [])];
      additions.splice(index, 1);
      return { ...prev, employeeAdditions: additions };
    });
  };

  const addTimeOff = () => {
    const fallbackEmployeeId = employees[0]?.id || null;
    setScenarioConfig((prev) => ({
      ...prev,
      employeeTimeOffs: [
        ...(prev.employeeTimeOffs || []),
        {
          local_id: `timeoff-${Date.now()}`,
          employee_id: fallbackEmployeeId,
          start_date: `${year}-01-01`,
          end_date: `${year}-01-01`,
          label: "",
        },
      ],
    }));
  };

  const updateTimeOff = (index, updates) => {
    setScenarioConfig((prev) => {
      const timeOffs = [...(prev.employeeTimeOffs || [])];
      timeOffs[index] = { ...timeOffs[index], ...updates };
      return { ...prev, employeeTimeOffs: timeOffs };
    });
  };

  const removeTimeOff = (index) => {
    setScenarioConfig((prev) => {
      const timeOffs = [...(prev.employeeTimeOffs || [])];
      timeOffs.splice(index, 1);
      return { ...prev, employeeTimeOffs: timeOffs };
    });
  };

  const resetRoadmapDraft = (updates = {}) => {
    setRoadmapDraft(buildRoadmapDraft(year, updates));
  };

  const updateScenarioIterationOverride = (iterationId, updates) => {
    setScenarioConfig((prev) => {
      const overrides = [...(prev.iterationOverrides || [])];
      const index = overrides.findIndex(
        (item) => String(item.iteration_id) === String(iterationId)
      );
      const next = {
        ...(index >= 0 ? overrides[index] : { iteration_id: iterationId }),
        ...updates,
      };
      if (index >= 0) {
        overrides[index] = next;
      } else {
        overrides.push(next);
      }
      return { ...prev, iterationOverrides: overrides };
    });
  };

  const resetScenarioIterationOverride = (iterationId) => {
    setScenarioConfig((prev) => {
      const overrides = (prev.iterationOverrides || []).filter(
        (item) => String(item.iteration_id) !== String(iterationId)
      );
      return { ...prev, iterationOverrides: overrides };
    });
  };

  const updateScenarioIterationAddition = (localId, updates) => {
    setScenarioConfig((prev) => {
      const additions = [...(prev.iterationAdditions || [])];
      const index = additions.findIndex(
        (item) => String(item.local_id || item.id) === String(localId)
      );
      if (index >= 0) {
        additions[index] = { ...additions[index], ...updates };
      }
      return { ...prev, iterationAdditions: additions };
    });
  };

  const removeScenarioIterationAddition = (localId) => {
    setScenarioConfig((prev) => {
      const additions = (prev.iterationAdditions || []).filter(
        (item) => String(item.local_id || item.id) !== String(localId)
      );
      return { ...prev, iterationAdditions: additions };
    });
  };

  const openRoadmapCreate = () => {
    setRoadmapDraftError(null);
    resetRoadmapDraft();
    setRoadmapEditorOpen(true);
  };

  const openRoadmapEdit = (iteration) => {
    if (!iteration) return;
    setRoadmapDraftError(null);
    if (isRoadmapMilestoneItem(iteration)) {
      resetRoadmapDraft({
        id: iteration.objective_id || iteration.id,
        source: "milestone",
        item_type: "milestone",
        objective_id: iteration.objective_id || iteration.id,
        project_id: iteration.project_id || "",
        project_mode: "existing",
        new_project_name: "",
        new_project_number: "",
        name: iteration.name || "",
        description: iteration.description || "",
        start_date: iteration.milestone_date || iteration.start_date || `${year}-01-01`,
        end_date: iteration.milestone_date || iteration.start_date || `${year}-01-01`,
        forecast_ca_eur: "",
        forecast_purchase_eur: "",
        forecast_time_hours: "",
      });
      setRoadmapEditorOpen(true);
      return;
    }
    resetRoadmapDraft({
      id: iteration.id,
      source: iteration.__source || "base",
      item_type: "iteration",
      project_id: iteration.project_id || "",
      project_mode: "existing",
      new_project_name: "",
      new_project_number: "",
      name: iteration.name || "",
      description: iteration.description || "",
      start_date: iteration.start_date || `${year}-01-01`,
      end_date: iteration.end_date || iteration.start_date || `${year}-01-15`,
      forecast_ca_eur:
        iteration.forecast_ca_eur !== null && iteration.forecast_ca_eur !== undefined
          ? iteration.forecast_ca_eur
          : "",
      forecast_purchase_eur:
        iteration.forecast_purchase_eur !== null && iteration.forecast_purchase_eur !== undefined
          ? iteration.forecast_purchase_eur
          : "",
      forecast_time_hours:
        iteration.forecast_time_hours !== null && iteration.forecast_time_hours !== undefined
          ? iteration.forecast_time_hours
          : "",
    });
    setRoadmapEditorOpen(true);
  };

  const handleRoadmapIterationUpdate = async (iterationId, changes) => {
    const target = roadmapIterations.find((iteration) => String(iteration.id) === String(iterationId));
    if (!target) return;
    if (isRoadmapMilestoneItem(target)) {
      const nextDate = changes.start_date || changes.end_date || target.milestone_date || target.start_date;
      const updatedObjective = await apiService.patch(
        `/projects/${target.project_id}/objectives/${target.objective_id}`,
        {
          is_milestone: true,
          milestone_date: nextDate,
          sprint_id: null,
        }
      );
      setMilestoneObjectives((prev) =>
        prev.map((objective) =>
          String(objective.id) === String(updatedObjective.id) ? updatedObjective : objective
        )
      );
      return;
    }
    const payload = {
      start_date: changes.start_date,
      end_date: changes.end_date,
    };
    if (target.__source === "addition") {
      updateScenarioIterationAddition(target.local_id || target.id, payload);
    } else {
      updateScenarioIterationOverride(target.id, payload);
    }
  };

  const deleteRoadmapMilestone = async () => {
    if (roadmapDraft.source !== "milestone" || !roadmapDraft.objective_id) return;
    try {
      await apiService.delete(
        `/projects/${roadmapDraft.project_id}/objectives/${roadmapDraft.objective_id}`
      );
      setMilestoneObjectives((prev) =>
        prev.filter((objective) => String(objective.id) !== String(roadmapDraft.objective_id))
      );
      setRoadmapEditorOpen(false);
    } catch (err) {
      console.error("Erreur suppression jalon:", err);
      setRoadmapDraftError(err.message || "Impossible de supprimer le jalon.");
    }
  };

  const saveRoadmapDraft = async () => {
    const trimmedName = String(roadmapDraft.name || "").trim();
    const draftType =
      roadmapDraft.source === "milestone"
        ? "milestone"
        : roadmapDraft.item_type || "iteration";
    const isMilestoneDraft = draftType === "milestone";
    const isCreatingScenarioProject =
      !isMilestoneDraft && roadmapDraft.source === "addition" && roadmapDraft.project_mode === "new";
    const newProjectName = String(roadmapDraft.new_project_name || "").trim();
    const newProjectNumber = String(roadmapDraft.new_project_number || "").trim();
    let projectId = roadmapDraft.project_id || null;
    if (isCreatingScenarioProject) {
      if (!newProjectName) {
        setRoadmapDraftError("Nom du projet requis.");
        return;
      }
      projectId = `scenario-project-${Date.now()}`;
    }
    if (!projectId) {
      setRoadmapDraftError("Selectionnez un projet.");
      return;
    }

    if (isMilestoneDraft) {
      if (!trimmedName) {
        setRoadmapDraftError("Nom du jalon requis.");
        return;
      }
      if (!roadmapDraft.start_date) {
        setRoadmapDraftError("Date du jalon requise.");
        return;
      }
      const numericProjectId = Number(projectId);
      if (!Number.isInteger(numericProjectId) || numericProjectId <= 0) {
        setRoadmapDraftError("Un jalon doit etre rattache a un projet existant.");
        return;
      }
      const payload = {
        project_id: numericProjectId,
        name: trimmedName,
        description: roadmapDraft.description || null,
        sprint_id: null,
        effort: 0,
        owner_id: null,
        status: "not_started",
        blocked_by: [],
        comment: null,
        is_milestone: true,
        milestone_date: roadmapDraft.start_date,
      };

      try {
        if (roadmapDraft.source === "milestone" && roadmapDraft.objective_id) {
          const updatedObjective = await apiService.patch(
            `/projects/${numericProjectId}/objectives/${roadmapDraft.objective_id}`,
            {
              name: payload.name,
              description: payload.description,
              sprint_id: null,
              effort: 0,
              is_milestone: true,
              milestone_date: payload.milestone_date,
            }
          );
          setMilestoneObjectives((prev) =>
            prev.map((objective) =>
              String(objective.id) === String(updatedObjective.id) ? updatedObjective : objective
            )
          );
        } else {
          const createdObjective = await apiService.post(
            `/projects/${numericProjectId}/objectives`,
            payload
          );
          setMilestoneObjectives((prev) => [...prev, createdObjective]);
        }
        setRoadmapEditorOpen(false);
      } catch (err) {
        console.error("Erreur enregistrement jalon:", err);
        setRoadmapDraftError(err.message || "Impossible d'enregistrer le jalon.");
      }
      return;
    }

    if (roadmapDraft.source === "addition" && !trimmedName) {
      setRoadmapDraftError("Nom de l'iteration requis.");
      return;
    }
    if (!roadmapDraft.start_date || !roadmapDraft.end_date) {
      setRoadmapDraftError("Dates de debut et fin requises.");
      return;
    }
    const draftStart = parseDate(roadmapDraft.start_date);
    const draftEnd = parseDate(roadmapDraft.end_date);
    if (draftStart && draftEnd && draftStart > draftEnd) {
      setRoadmapDraftError("La date de fin doit etre posterieure au debut.");
      return;
    }

    const payload = {
      project_id: projectId,
      name: trimmedName || roadmapDraft.name,
      start_date: roadmapDraft.start_date,
      end_date: roadmapDraft.end_date,
      forecast_ca_eur: toOptionalNumber(roadmapDraft.forecast_ca_eur),
      forecast_purchase_eur: toOptionalNumber(roadmapDraft.forecast_purchase_eur),
      forecast_time_hours: toOptionalNumber(roadmapDraft.forecast_time_hours),
    };

    if (roadmapDraft.source === "addition") {
      const localId = roadmapDraft.id || `iter-${Date.now()}`;
      setScenarioConfig((prev) => {
        const nextProjectAdditions = [...(prev.projectAdditions || [])];
        if (isCreatingScenarioProject) {
          nextProjectAdditions.push({
            local_id: projectId,
            name: newProjectName,
            project_number: newProjectNumber || null,
            status: "proposition",
          });
        }

        const nextIterationAdditions = [...(prev.iterationAdditions || [])];
        const index = nextIterationAdditions.findIndex(
          (item) => String(item.local_id || item.id) === String(localId)
        );
        const nextIteration = { local_id: localId, ...payload };
        if (index >= 0) {
          nextIterationAdditions[index] = {
            ...nextIterationAdditions[index],
            ...nextIteration,
          };
        } else {
          nextIterationAdditions.push(nextIteration);
        }

        return {
          ...prev,
          projectAdditions: nextProjectAdditions,
          iterationAdditions: nextIterationAdditions,
        };
      });
    } else {
      updateScenarioIterationOverride(roadmapDraft.id, payload);
    }

    setRoadmapEditorOpen(false);
  };

  const duplicateScenario = async () => {
    if (activeScenarioId === SCENARIO_BASE_ID) return;
    try {
      setSaveState({ type: "info", text: "Duplication du scenario..." });
      const created = await apiService.post("/pilotage/scenarios", {
        name: `${scenarioName || "Scenario"} - copie`,
        year,
        payload: buildScenarioPayload(
          scenarioConfig,
          createStandaloneScenarioMeta()
        ),
      });
      setScenarios((prev) => [created, ...prev]);
      setActiveScenarioId(created.id);
      setSaveState({ type: "success", text: "Scenario duplique" });
    } catch (err) {
      console.error("Erreur duplication scenario:", err);
      setSaveState({ type: "error", text: "Erreur duplication scenario" });
    } finally {
      setTimeout(() => setSaveState(null), 2500);
    }
  };

  const saveScenario = async () => {
    try {
      setSaveState({ type: "info", text: "Enregistrement en cours..." });
      const payload = buildScenarioPayload(scenarioConfig, createStandaloneScenarioMeta());
      if (activeScenarioId === SCENARIO_BASE_ID) {
        if (!scenarioName.trim()) {
          setSaveState({ type: "error", text: "Nom du scenario requis" });
          return;
        }
        const created = await apiService.post("/pilotage/scenarios", {
          name: scenarioName.trim(),
          year,
          payload,
        });
        setScenarios((prev) => [created, ...prev]);
        setActiveScenarioId(created.id);
        setSaveState({ type: "success", text: "Scenario enregistre" });
      } else {
        const updated = await apiService.put(`/pilotage/scenarios/${activeScenarioId}`, {
          name: scenarioName.trim() || scenarioName,
          year,
          payload,
        });
        setScenarios((prev) =>
          prev.map((item) => (String(item.id) === String(activeScenarioId) ? updated : item))
        );
        setSaveState({ type: "success", text: "Scenario mis a jour" });
      }
    } catch (err) {
      console.error("Erreur sauvegarde scenario:", err);
      setSaveState({ type: "error", text: "Erreur sauvegarde scenario" });
    } finally {
      setTimeout(() => setSaveState(null), 2500);
    }
  };

  const deleteScenario = async () => {
    if (activeScenarioId === SCENARIO_BASE_ID) return;
    try {
      await apiService.delete(`/pilotage/scenarios/${activeScenarioId}`);
      setScenarios((prev) => prev.filter((item) => String(item.id) !== String(activeScenarioId)));
      setActiveScenarioId(SCENARIO_BASE_ID);
      setSaveState({ type: "success", text: "Scenario supprime" });
    } catch (err) {
      console.error("Erreur suppression scenario:", err);
      setSaveState({ type: "error", text: "Erreur suppression scenario" });
    } finally {
      setTimeout(() => setSaveState(null), 2500);
    }
  };

  const handleScenarioSelectionChange = (nextScenarioId) => {
    if (String(nextScenarioId) === String(activeScenarioId)) return;
    if (
      isDirty &&
      !window.confirm("Des modifications non sauvegardees seront perdues. Continuer ?")
    ) {
      return;
    }
    setActiveScenarioId(nextScenarioId);
  };

  const resetEditorToSavedState = () => {
    pushEditorSnapshot(() => activeScenarioSavedSnapshot);
  };

  if (loading) {
    return (
      <div className="py-10 text-center text-gray-500">
        Chargement des donnees de pilotage...
      </div>
    );
  }

  const roadmapDraftType =
    roadmapDraft.source === "milestone" ? "milestone" : roadmapDraft.item_type || "iteration";
  const isRoadmapMilestoneDraft = roadmapDraftType === "milestone";
  const roadmapEditorTitle = isRoadmapMilestoneDraft
    ? roadmapDraft.source === "milestone"
      ? "Jalon roadmap"
      : "Nouveau jalon"
    : roadmapDraft.source === "addition"
      ? "Nouvelle iteration"
      : "Iteration scenario";

  return (
    <div className="py-6 space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Pilotage entreprise</h1>
            <p className="text-sm text-gray-600">
              Vue annuelle charge, CA, previsionnel et ressources. Ajustez vos
              hypotheses pour simuler des scenarios.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500">Annee</label>
              <input
                type="number"
                min="2000"
                max="2100"
                value={year}
                onChange={(e) => setYear(Number(e.target.value || currentYear))}
                className="border rounded px-2 py-1 w-24"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500">Scenario</label>
              <select
                className="border rounded px-2 py-1 min-w-[180px]"
                value={activeScenarioId}
                onChange={(e) => handleScenarioSelectionChange(e.target.value)}
              >
                {scenarioSelectOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500">Nom</label>
              <input
                type="text"
                value={scenarioName}
                onChange={(e) => setScenarioName(e.target.value)}
                className="border rounded px-2 py-1 w-52"
                placeholder="Nom du scenario"
              />
            </div>
            <button
              onClick={undoScenarioChange}
              disabled={!canUndo}
              className={`inline-flex items-center px-3 py-2 rounded border ${
                canUndo
                  ? "text-gray-600 border-gray-300 hover:bg-gray-100"
                  : "text-gray-400 border-gray-200 cursor-not-allowed"
              }`}
              title="Annuler la derniere modification"
            >
              <Undo2 className="h-4 w-4 mr-2" />
              Undo
            </button>
            <button
              onClick={redoScenarioChange}
              disabled={!canRedo}
              className={`inline-flex items-center px-3 py-2 rounded border ${
                canRedo
                  ? "text-gray-600 border-gray-300 hover:bg-gray-100"
                  : "text-gray-400 border-gray-200 cursor-not-allowed"
              }`}
              title="Retablir la derniere modification annulee"
            >
              <Redo2 className="h-4 w-4 mr-2" />
              Redo
            </button>
            <button
              onClick={saveScenario}
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Save className="h-4 w-4 mr-2" />
              Enregistrer
            </button>
            <button
              type="button"
              onClick={exportPilotageCsv}
              disabled={exportingCsv}
              className={`inline-flex items-center px-3 py-2 rounded border ${
                exportingCsv
                  ? "text-gray-400 border-gray-200 cursor-wait"
                  : "text-gray-600 border-gray-300 hover:bg-gray-100"
              }`}
            >
              <FileText className="h-4 w-4 mr-2" />
              {exportingCsv ? "Export..." : "Exporter CSV"}
            </button>
            {activeScenarioId !== SCENARIO_BASE_ID && (
              <button
                onClick={duplicateScenario}
                className="inline-flex items-center px-3 py-2 rounded border text-gray-600 border-gray-300 hover:bg-gray-100"
              >
                <Layers className="h-4 w-4 mr-2" />
                Dupliquer le scenario
              </button>
            )}
            <button
              onClick={deleteScenario}
              disabled={activeScenarioId === SCENARIO_BASE_ID}
              className={`inline-flex items-center px-3 py-2 rounded border ${
                activeScenarioId === SCENARIO_BASE_ID
                  ? "text-gray-400 border-gray-200 cursor-not-allowed"
                  : "text-gray-600 border-gray-300 hover:bg-gray-100"
              }`}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer
            </button>
            <button
              onClick={resetEditorToSavedState}
              disabled={!isDirty}
              className={`inline-flex items-center px-3 py-2 rounded border ${
                isDirty
                  ? "text-gray-600 border-gray-300 hover:bg-gray-100"
                  : "text-gray-400 border-gray-200 cursor-not-allowed"
              }`}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </button>
          </div>
        </div>
        {saveState && (
          <div
            className={`mt-4 text-sm ${
              saveState.type === "success"
                ? "text-green-600"
                : saveState.type === "error"
                ? "text-red-600"
                : "text-gray-600"
            }`}
          >
            {saveState.text}
          </div>
        )}
        <div className="mt-2 text-xs text-gray-500">
          Raccourcis: Ctrl/Cmd+Z pour annuler, Ctrl/Cmd+Shift+Z ou Ctrl/Cmd+Y pour retablir.
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-sm">
          <button
            onClick={() => setActiveModal("availability")}
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
          >
            <Users className="h-4 w-4 text-gray-400" />
            Disponibilites ({scenarioStats.overrides})
          </button>
          <button
            onClick={() => setActiveModal("reinforcements")}
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
          >
            <Users className="h-4 w-4 text-gray-400" />
            Renforts ({scenarioStats.additions})
          </button>
          <button
            onClick={() => setActiveModal("timeOffs")}
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
          >
            <CalendarClock className="h-4 w-4 text-gray-400" />
            Conges ({scenarioStats.timeOffs})
          </button>
          <button
            onClick={() => setActiveModal("projects")}
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
          >
            <Layers className="h-4 w-4 text-gray-400" />
            Projets filtres ({scenarioStats.excludedProjects})
          </button>
        </div>
        <div className="mt-5 border-t border-gray-100 pt-4">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-6">
            {PILOTAGE_TABS.map((tab) => {
              const Icon = tab.icon;
              const active = activePilotageTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActivePilotageTab(tab.id)}
                  className={`flex min-h-[68px] items-center gap-3 rounded-lg border px-3 py-2 text-left transition ${
                    active
                      ? "border-blue-500 bg-blue-50 text-blue-900"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <Icon className={`h-5 w-5 shrink-0 ${active ? "text-blue-600" : "text-gray-400"}`} />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">{tab.label}</span>
                    <span className="block text-xs text-gray-500">{tab.description}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {["production", "accounting"].includes(activePilotageTab) && (
        <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4">
        {activePilotageTab === "production" ? (
          <>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">Capacite totale (h)</div>
          <div className="text-lg font-semibold">{formatHours(totals.capacityHours)}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">Charge planifiee (h)</div>
          <div className="text-lg font-semibold">{formatHours(totals.chargeHours)}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">Charge / capacite</div>
          <div className="text-lg font-semibold">
            {totals.capacityHours > 0
              ? `${Math.round((totals.chargeHours / totals.capacityHours) * 100)}%`
              : "-"}
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">CA considere fait</div>
          <div className="text-lg font-semibold">{formatCurrency(productionProgress.totals.done)}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">CA futur restant</div>
          <div className="text-lg font-semibold">{formatCurrency(productionProgress.totals.remaining)}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">Progression CA</div>
          <div className="text-lg font-semibold">{formatPercent(productionProgress.totals.progressPct)}</div>
          <div className="mt-2 h-1.5 w-full rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-emerald-500"
              style={{ width: `${Math.min(100, productionProgress.totals.progressPct)}%` }}
            />
          </div>
        </div>
          </>
        ) : (
          <>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">CA produit total (EUR)</div>
          <div className="text-lg font-semibold">
            {formatCurrency(totals.caActualEur + totals.caForecastEur)}
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">Depenses prev. (EUR)</div>
          <div className="text-lg font-semibold">{formatCurrency(totals.expensesForecastEur)}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">Encaissements (EUR)</div>
          <div className="text-lg font-semibold">{formatCurrency(cashFlowTotals.inflow)}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">Decaissements (EUR)</div>
          <div className="text-lg font-semibold">{formatCurrency(cashFlowTotals.outflow)}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">Tresorerie nette (EUR)</div>
          <div className="text-lg font-semibold">{formatCurrency(cashFlowTotals.net)}</div>
        </div>
          </>
        )}
      </div>

      {activePilotageTab === "production" && separateInternalInvestment && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {CLIENT_TYPE_SEGMENTS.map((segment) => {
            const segmentTotals = productionProgress.totals.byClientType?.[segment.id] || {};
            return (
              <div key={`segment-kpi-${segment.id}`} className="rounded-lg border border-slate-200 bg-white p-4 shadow">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase text-slate-500">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: segment.color }} />
                  {segment.label}
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-slate-500">Cible</div>
                    <div className="font-semibold text-slate-900">{formatCurrency(segmentTotals.target || 0)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Fait</div>
                    <div className="font-semibold text-slate-900">{formatCurrency(segmentTotals.done || 0)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Reste</div>
                    <div className="font-semibold text-slate-900">{formatCurrency(segmentTotals.remaining || 0)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6 space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">
              {activePilotageTab === "production" ? "Production annuelle" : "Compta et tresorerie"}
            </h2>
            <p className="text-sm text-gray-500">
              {activePilotageTab === "production"
                ? "Charge, capacite et CA produit, sans melanger les flux de tresorerie."
                : "Encaissements, decaissements et tresorerie nette, separes du CA produit."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {activePilotageTab === "production" ? (
              <>
            <button
              type="button"
              onClick={() => setShowDetailedImpact((prev) => !prev)}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-600 hover:border-gray-300 hover:bg-gray-50"
            >
              {showDetailedImpact ? "Vue synthese" : "Vue detaillee"}
            </button>
            <button
              type="button"
              onClick={() => setShowAcquiredForecastSplit((prev) => !prev)}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-600 hover:border-gray-300 hover:bg-gray-50"
            >
              {showAcquiredForecastSplit ? "Vue globale" : "Vue acquis + prev."}
            </button>
            <label
              className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1 text-xs transition ${
                separateInternalInvestment
                  ? "border-amber-500 bg-amber-50 text-amber-800"
                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <input
                type="checkbox"
                checked={separateInternalInvestment}
                onChange={(event) => setSeparateInternalInvestment(event.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              Invest. interne separe
            </label>
              </>
            ) : (
              <>
                <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600">
                  {(scenarioConfig.factoredInvoiceIds || []).length} facture(s) factorisee(s)
                </span>
                <label
                  className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1 text-xs transition ${
                    separateInternalInvestment
                      ? "border-amber-500 bg-amber-50 text-amber-800"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={separateInternalInvestment}
                    onChange={(event) => setSeparateInternalInvestment(event.target.checked)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  Invest. interne separe
                </label>
                <button
                  type="button"
                  onClick={() => setShowExpenseChart((prev) => !prev)}
                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                >
                  {showExpenseChart ? "Masquer depenses" : "Afficher depenses"}
                </button>
              </>
            )}
          </div>
        </div>
        <div className="space-y-6">
          {activePilotageTab === "production" && (
            <>
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <div className="text-sm font-medium text-gray-900">Charge cumulative (h)</div>
            <div className="mt-3 h-96">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={
                    separateInternalInvestment
                      ? cumulativeChargeClientTypeData
                      : showDetailedImpact
                      ? cumulativeChargeDetailData
                      : showAcquiredForecastSplit
                      ? cumulativeChargeSplitData
                      : cumulativeChargeData
                  }
                  onClick={handleChartClick}
                  margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tickMargin={8} />
                  <YAxis tickFormatter={formatHours} />
                  <Tooltip formatter={(value, name) => [`${formatHours(value)} h`, name]} />
                  <defs>
                    <pattern
                      id="charge-forecast-pattern"
                      patternUnits="userSpaceOnUse"
                      width="8"
                      height="8"
                      patternTransform="rotate(45)"
                    >
                      <line x1="0" y1="0" x2="0" y2="8" stroke="#2563eb" strokeWidth="2" />
                    </pattern>
                    {projectSeries.map((project) => (
                      <pattern
                        key={`charge-forecast-${project.id}`}
                        id={`charge-forecast-${project.id}`}
                        patternUnits="userSpaceOnUse"
                        width="8"
                        height="8"
                        patternTransform="rotate(45)"
                      >
                        <line x1="0" y1="0" x2="0" y2="8" stroke={project.color} strokeWidth="2" />
                      </pattern>
                    ))}
                    {CLIENT_TYPE_SEGMENTS.map((segment) => (
                      <pattern
                        key={`charge-forecast-${segment.id}`}
                        id={`charge-forecast-${segment.id}`}
                        patternUnits="userSpaceOnUse"
                        width="8"
                        height="8"
                        patternTransform="rotate(45)"
                      >
                        <line x1="0" y1="0" x2="0" y2="8" stroke={segment.color} strokeWidth="2" />
                      </pattern>
                    ))}
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="capacityTotal"
                    name="Capacite cumulative"
                    stroke="#0f172a"
                    fill="rgba(15, 23, 42, 0.08)"
                    fillOpacity={0.18}
                    strokeWidth={2}
                  />
                  {separateInternalInvestment ? (
                    <>
                      {CLIENT_TYPE_SEGMENTS.map((segment) => (
                        <Area
                          key={`${segment.id}-actual`}
                          type="monotone"
                          dataKey={`${segment.id}Actual`}
                          name={`${segment.label} (acquis)`}
                          stroke={segment.color}
                          fill={segment.color}
                          fillOpacity={0.2}
                          strokeWidth={1.5}
                          stackId="charge-stack"
                        />
                      ))}
                      {CLIENT_TYPE_SEGMENTS.map((segment) => (
                        <Area
                          key={`${segment.id}-forecast`}
                          type="monotone"
                          dataKey={`${segment.id}Forecast`}
                          name={`${segment.label} (prev.)`}
                          stroke={segment.color}
                          fill={`url(#charge-forecast-${segment.id})`}
                          fillOpacity={0.35}
                          strokeWidth={1.5}
                          strokeDasharray="4 3"
                          stackId="charge-stack"
                        />
                      ))}
                    </>
                  ) : showDetailedImpact ? (
                    <>
                      {projectSeries.map((project) => (
                        <Area
                          key={`${project.id}-actual`}
                          type="monotone"
                          dataKey={`project_${project.id}_actual`}
                          name={`${project.name} (reel)`}
                          stroke={project.color}
                          fill={project.color}
                          fillOpacity={0.2}
                          strokeWidth={1.5}
                          stackId="charge-stack"
                        />
                      ))}
                      {projectSeries.map((project) => (
                        <Area
                          key={`${project.id}-forecast`}
                          type="monotone"
                          dataKey={`project_${project.id}_forecast`}
                          name={`${project.name} (prev.)`}
                          stroke={project.color}
                          fill={`url(#charge-forecast-${project.id})`}
                          fillOpacity={0.35}
                          strokeWidth={1.5}
                          strokeDasharray="4 3"
                          stackId="charge-stack"
                        />
                      ))}
                    </>
                  ) : showAcquiredForecastSplit ? (
                    <>
                      <Area
                        type="monotone"
                        dataKey="actual"
                        name="Charge acquise"
                        stroke="#2563eb"
                        fill="#2563eb"
                        fillOpacity={0.2}
                        strokeWidth={2}
                        stackId="charge-stack"
                      />
                      <Area
                        type="monotone"
                        dataKey="forecast"
                        name="Charge prev."
                        stroke="#2563eb"
                        fill="url(#charge-forecast-pattern)"
                        fillOpacity={0.35}
                        strokeWidth={2}
                        strokeDasharray="4 3"
                        stackId="charge-stack"
                      />
                    </>
                  ) : (
                    <Area
                      type="monotone"
                      dataKey="total"
                      name="Charge totale"
                      stroke="#2563eb"
                      fill="#2563eb"
                      fillOpacity={0.2}
                      strokeWidth={2}
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <div className="text-sm font-medium text-gray-900">Charge par mois (h)</div>
            <div className="mt-3 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={
                    separateInternalInvestment
                      ? monthlyChargeClientTypeData
                      : showDetailedImpact
                      ? monthlyChargeDetailData
                      : monthlyChargeData
                  }
                  onClick={handleChartClick}
                  margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tickMargin={8} />
                  <YAxis tickFormatter={formatHours} />
                  <Tooltip content={renderMonthlyChargeTooltip} />
                  <defs>
                    <pattern
                      id="charge-month-forecast-pattern"
                      patternUnits="userSpaceOnUse"
                      width="8"
                      height="8"
                      patternTransform="rotate(45)"
                    >
                      <line x1="0" y1="0" x2="0" y2="8" stroke="#2563eb" strokeWidth="2" />
                    </pattern>
                    {projectSeries.map((project) => (
                      <pattern
                        key={`charge-month-forecast-${project.id}`}
                        id={`charge-month-forecast-${project.id}`}
                        patternUnits="userSpaceOnUse"
                        width="8"
                        height="8"
                        patternTransform="rotate(45)"
                      >
                      <line x1="0" y1="0" x2="0" y2="8" stroke={project.color} strokeWidth="2" />
                    </pattern>
                  ))}
                    {CLIENT_TYPE_SEGMENTS.map((segment) => (
                      <pattern
                        key={`charge-month-forecast-${segment.id}`}
                        id={`charge-month-forecast-${segment.id}`}
                        patternUnits="userSpaceOnUse"
                        width="8"
                        height="8"
                        patternTransform="rotate(45)"
                      >
                        <line x1="0" y1="0" x2="0" y2="8" stroke={segment.color} strokeWidth="2" />
                      </pattern>
                    ))}
                </defs>
                  {separateInternalInvestment ? (
                    <>
                      {CLIENT_TYPE_SEGMENTS.map((segment) => (
                        <Bar
                          key={`${segment.id}-actual`}
                          dataKey={`${segment.id}Actual`}
                          name={`${segment.label} (acquis)`}
                          stackId="month-charge"
                          fill={segment.color}
                          radius={[4, 4, 0, 0]}
                        />
                      ))}
                      {CLIENT_TYPE_SEGMENTS.map((segment) => (
                        <Bar
                          key={`${segment.id}-forecast`}
                          dataKey={`${segment.id}Forecast`}
                          name={`${segment.label} (prev.)`}
                          stackId="month-charge"
                          fill={`url(#charge-month-forecast-${segment.id})`}
                          radius={[4, 4, 0, 0]}
                        />
                      ))}
                    </>
                  ) : showDetailedImpact ? (
                    <>
                      {projectSeries.map((project) => (
                        <Bar
                          key={`${project.id}-actual`}
                          dataKey={`project_${project.id}_actual`}
                          name={`${project.name} (reel)`}
                          stackId="month-charge"
                          fill={project.color}
                          radius={[4, 4, 0, 0]}
                        />
                      ))}
                      {projectSeries.map((project) => (
                        <Bar
                          key={`${project.id}-forecast`}
                          dataKey={`project_${project.id}_forecast`}
                          name={`${project.name} (prev.)`}
                          stackId="month-charge"
                          fill={`url(#charge-month-forecast-${project.id})`}
                          radius={[4, 4, 0, 0]}
                        />
                      ))}
                    </>
                  ) : showAcquiredForecastSplit ? (
                    <>
                      <Bar
                        dataKey="actual"
                        name="Charge acquise"
                        stackId="month-charge"
                        fill="#2563eb"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="forecast"
                        name="Charge prev."
                        stackId="month-charge"
                        fill="url(#charge-month-forecast-pattern)"
                        radius={[4, 4, 0, 0]}
                      />
                    </>
                  ) : (
                    <Bar
                      dataKey="total"
                      name="Charge totale"
                      fill="#2563eb"
                      radius={[4, 4, 0, 0]}
                    />
                  )}
                  <Customized component={MonthlyCapacityTicks} />
                  <Customized component={MonthlyEventMarkers} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-500">
              <span className="font-medium text-gray-500">Evenements:</span>
              <span className="inline-flex items-center gap-2">
                <span
                  className="inline-flex h-2 w-2 rounded-full"
                  style={{ backgroundColor: eventTypeMeta.employee_arrival.color }}
                />
                {eventTypeMeta.employee_arrival.label}
              </span>
              <span className="inline-flex items-center gap-2">
                <span
                  className="inline-flex h-2 w-2 rounded-full"
                  style={{ backgroundColor: eventTypeMeta.employee_departure.color }}
                />
                {eventTypeMeta.employee_departure.label}
              </span>
              <span className="inline-flex items-center gap-2">
                <span
                  className="inline-flex h-2 w-2 rounded-full"
                  style={{ backgroundColor: eventTypeMeta.reinforcement_arrival.color }}
                />
                {eventTypeMeta.reinforcement_arrival.label}
              </span>
              <span className="inline-flex items-center gap-2">
                <span
                  className="inline-flex h-2 w-2 rounded-full"
                  style={{ backgroundColor: eventTypeMeta.reinforcement_departure.color }}
                />
                {eventTypeMeta.reinforcement_departure.label}
              </span>
            </div>
          </div>
            </>
          )}
          {activePilotageTab === "accounting" && showExpenseChart && (
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <div className="text-sm font-medium text-gray-900">Depenses cumulatives (EUR)</div>
              <div className="mt-3 h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={cumulativeExpenseData}
                    onClick={handleChartClick}
                    margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tickMargin={8} />
                    <YAxis tickFormatter={formatCurrencyShort} />
                    <Tooltip formatter={(value, name) => [formatCurrency(value), name]} />
                    {projectSeries.map((project) => (
                      <Area
                        key={project.id}
                        type="monotone"
                        dataKey={`project_${project.id}`}
                        name={project.name}
                        stroke={project.color}
                        fill={project.color}
                        fillOpacity={0.18}
                        strokeWidth={1.5}
                        stackId="expense-stack"
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {activePilotageTab === "production" && (
            <>
          {showCapacityRevenueProjection && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    CA realisable selon capacite
                  </div>
                  <div className="text-xs text-gray-500">
                    Le CA futur est ajuste au prorata des mois ou la charge depasse la capacite.
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  Surcharge {formatHours(productionCapacityProjection.totals.overloadHours)} h
                </div>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-lg border border-slate-100 bg-white px-4 py-3">
                  <div className="text-xs text-slate-500">CA produit</div>
                  <div className="mt-1 text-lg font-semibold text-emerald-700">
                    {formatCurrency(productionCapacityProjection.totals.producedCa)}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-100 bg-white px-4 py-3">
                  <div className="text-xs text-slate-500">Futur productible</div>
                  <div className="mt-1 text-lg font-semibold text-blue-700">
                    {formatCurrency(productionCapacityProjection.totals.productibleFutureCa)}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">
                    sur {formatCurrency(productionCapacityProjection.totals.plannedFutureCa)} a produire
                  </div>
                </div>
                <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-3">
                  <div className="text-xs text-rose-700">CA perdu capacite</div>
                  <div className="mt-1 text-lg font-semibold text-rose-700">
                    {formatCurrency(productionCapacityProjection.totals.lostCa)}
                  </div>
                  <div className="mt-1 text-[11px] text-rose-700">
                    {formatPercent(productionCapacityProjection.totals.lossPct)} du futur planifie
                  </div>
                </div>
                <div className="rounded-lg border border-slate-100 bg-white px-4 py-3">
                  <div className="text-xs text-slate-500">CA fin d'annee</div>
                  <div className="mt-1 text-lg font-semibold text-slate-950">
                    {formatCurrency(productionCapacityProjection.totals.endOfYearCa)}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">
                    plan {formatCurrency(productionCapacityProjection.totals.plannedEndOfYearCa)}
                  </div>
                </div>
              </div>
              {separateInternalInvestment && (
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  {CLIENT_TYPE_SEGMENTS.map((segment) => {
                    const segmentTotals =
                      productionCapacityProjection.totals.byClientType?.[segment.id] || {};
                    return (
                      <div
                        key={`capacity-summary-${segment.id}`}
                        className="rounded-lg border border-slate-100 bg-white px-4 py-3"
                      >
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase text-slate-500">
                          <span
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: segment.color }}
                          />
                          {segment.label}
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <div className="text-slate-500">Produit</div>
                            <div className="font-semibold text-slate-900">
                              {formatCurrency(segmentTotals.producedCa || 0)}
                            </div>
                          </div>
                          <div>
                            <div className="text-slate-500">Productible</div>
                            <div className="font-semibold text-slate-900">
                              {formatCurrency(segmentTotals.productibleFutureCa || 0)}
                            </div>
                          </div>
                          <div>
                            <div className="text-slate-500">Perdu</div>
                            <div className="font-semibold text-rose-700">
                              {formatCurrency(segmentTotals.lostCa || 0)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-4 h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={productionCapacityProjection.cumulativeData}
                    onClick={handleChartClick}
                    margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tickMargin={8} />
                    <YAxis tickFormatter={formatCurrencyShort} />
                    <Tooltip formatter={(value, name) => [formatCurrency(value), name]} />
                    {separateInternalInvestment ? (
                      <>
                        {CLIENT_TYPE_SEGMENTS.map((segment) => (
                          <Area
                            key={`${segment.id}-produced`}
                            type="monotone"
                            dataKey={`${segment.id}Produced`}
                            name={`${segment.label} produit`}
                            stroke={segment.color}
                            fill={segment.color}
                            fillOpacity={0.22}
                            strokeWidth={2}
                            stackId="capacity-revenue"
                          />
                        ))}
                        {CLIENT_TYPE_SEGMENTS.map((segment) => (
                          <Area
                            key={`${segment.id}-future`}
                            type="monotone"
                            dataKey={`${segment.id}FutureProductible`}
                            name={`${segment.label} productible`}
                            stroke={segment.forecastColor}
                            fill={segment.forecastColor}
                            fillOpacity={0.2}
                            strokeWidth={2}
                            stackId="capacity-revenue"
                          />
                        ))}
                        {CLIENT_TYPE_SEGMENTS.map((segment) => (
                          <Area
                            key={`${segment.id}-lost`}
                            type="monotone"
                            dataKey={`${segment.id}LostCa`}
                            name={`${segment.label} perdu`}
                            stroke="#e11d48"
                            fill="#e11d48"
                            fillOpacity={segment.id === CLIENT_TYPE_INTERNAL_INVESTMENT ? 0.2 : 0.12}
                            strokeWidth={2}
                            stackId="capacity-revenue"
                          />
                        ))}
                      </>
                    ) : (
                      <>
                        <Area
                          type="monotone"
                          dataKey="produced"
                          name="CA produit"
                          stroke="#059669"
                          fill="#059669"
                          fillOpacity={0.24}
                          strokeWidth={2}
                          stackId="capacity-revenue"
                        />
                        <Area
                          type="monotone"
                          dataKey="futureProductible"
                          name="Futur productible"
                          stroke="#2563eb"
                          fill="#2563eb"
                          fillOpacity={0.18}
                          strokeWidth={2}
                          stackId="capacity-revenue"
                        />
                        <Area
                          type="monotone"
                          dataKey="lostCa"
                          name="CA perdu capacite"
                          stroke="#e11d48"
                          fill="#e11d48"
                          fillOpacity={0.16}
                          strokeWidth={2}
                          stackId="capacity-revenue"
                        />
                      </>
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
              <div className="text-sm font-medium text-gray-900">
                CA planifie cumulatif (fait / reste)
              </div>
              <div className="text-xs text-gray-500">
                Cible {formatCurrency(productionProgress.totals.target)} - fait {formatCurrency(productionProgress.totals.done)}
              </div>
            </div>
            <div className="mt-3 h-96">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={productionProgress.cumulativeData}
                  onClick={handleChartClick}
                  margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tickMargin={8} />
                  <YAxis tickFormatter={formatCurrencyShort} />
                  <Tooltip formatter={(value, name) => [formatCurrency(value), name]} />
                  {separateInternalInvestment ? (
                    <>
                      {CLIENT_TYPE_SEGMENTS.map((segment) => (
                        <Area
                          key={`${segment.id}-done`}
                          type="monotone"
                          dataKey={`${segment.id}Done`}
                          name={`${segment.label} fait`}
                          stroke={segment.color}
                          fill={segment.color}
                          fillOpacity={0.24}
                          strokeWidth={2}
                          stackId="production-progress"
                        />
                      ))}
                      {CLIENT_TYPE_SEGMENTS.map((segment) => (
                        <Area
                          key={`${segment.id}-remaining`}
                          type="monotone"
                          dataKey={`${segment.id}Remaining`}
                          name={`${segment.label} restant`}
                          stroke={segment.forecastColor}
                          fill={segment.forecastColor}
                          fillOpacity={0.18}
                          strokeWidth={2}
                          stackId="production-progress"
                        />
                      ))}
                    </>
                  ) : (
                    <>
                      <Area
                        type="monotone"
                        dataKey="done"
                        name="CA considere fait"
                        stroke="#059669"
                        fill="#059669"
                        fillOpacity={0.26}
                        strokeWidth={2}
                        stackId="production-progress"
                      />
                      <Area
                        type="monotone"
                        dataKey="remaining"
                        name="CA futur restant"
                        stroke="#f59e0b"
                        fill="#f59e0b"
                        fillOpacity={0.18}
                        strokeWidth={2}
                        stackId="production-progress"
                      />
                    </>
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
              <div className="text-sm font-medium text-gray-900">
                Contribution annuelle par projet
              </div>
              <div className="text-xs text-gray-500">
                Charge {formatHours(projectAnnualContribution.totals.charge)} h - CA {formatCurrency(projectAnnualContribution.totals.ca)}
              </div>
            </div>
            <div className="mt-4 grid gap-4 xl:grid-cols-2">
              <div className="rounded-lg border border-slate-100 bg-white p-3">
                <div className="text-xs font-semibold uppercase text-gray-500">
                  Charge projet
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={projectAnnualContribution.chargeData}
                          dataKey="charge"
                          nameKey="name"
                          innerRadius={58}
                          outerRadius={88}
                          paddingAngle={1}
                        >
                          {projectAnnualContribution.chargeData.map((project) => (
                            <Cell key={`charge-pie-${project.id}`} fill={project.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          content={(props) =>
                            renderContributionTooltip(
                              props,
                              "charge",
                              (value) => `${formatHours(value)} h`,
                              projectAnnualContribution.totals.charge
                            )
                          }
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                    {projectAnnualContribution.chargeData.map((project) => (
                      <div key={`charge-row-${project.id}`} className="grid grid-cols-[1fr_auto] gap-3 text-xs">
                        <div className="flex min-w-0 items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: project.color }}
                          />
                          <span className="truncate text-slate-700" title={project.name}>
                            {project.name}
                          </span>
                        </div>
                        <div className="text-right font-semibold text-slate-900">
                          <div>{formatPercent(project.chargePct)}</div>
                          <div className="text-[11px] font-medium text-slate-500">
                            {formatHours(project.charge)} h
                          </div>
                        </div>
                      </div>
                    ))}
                    {!projectAnnualContribution.chargeData.length && (
                      <div className="rounded border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-400">
                        Aucune charge projet sur l'annee.
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-slate-100 bg-white p-3">
                <div className="text-xs font-semibold uppercase text-gray-500">
                  CA de production
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={projectAnnualContribution.caData}
                          dataKey="ca"
                          nameKey="name"
                          innerRadius={58}
                          outerRadius={88}
                          paddingAngle={1}
                        >
                          {projectAnnualContribution.caData.map((project) => (
                            <Cell key={`ca-pie-${project.id}`} fill={project.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          content={(props) =>
                            renderContributionTooltip(
                              props,
                              "ca",
                              formatCurrency,
                              projectAnnualContribution.totals.ca
                            )
                          }
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                    {projectAnnualContribution.caData.map((project) => (
                      <div key={`ca-row-${project.id}`} className="grid grid-cols-[1fr_auto] gap-3 text-xs">
                        <div className="flex min-w-0 items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: project.color }}
                          />
                          <span className="truncate text-slate-700" title={project.name}>
                            {project.name}
                          </span>
                        </div>
                        <div className="text-right font-semibold text-slate-900">
                          <div>{formatPercent(project.caPct)}</div>
                          <div className="text-[11px] font-medium text-slate-500">
                            {formatCurrencyShort(project.ca)}
                          </div>
                        </div>
                      </div>
                    ))}
                    {!projectAnnualContribution.caData.length && (
                      <div className="rounded border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-400">
                        Aucun CA projet sur l'annee.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-5 border-t border-slate-200 pt-4">
              <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                <div className="text-sm font-medium text-gray-900">
                  Contribution annuelle par client
                </div>
                <div className="text-xs text-gray-500">
                  Projets regroupes par client
                </div>
              </div>
              <div className="mt-4 grid gap-4 xl:grid-cols-2">
                <div className="rounded-lg border border-slate-100 bg-white p-3">
                  <div className="text-xs font-semibold uppercase text-gray-500">
                    Charge client
                  </div>
                  <div className="mt-3 grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={projectAnnualContribution.clientChargeData}
                            dataKey="charge"
                            nameKey="name"
                            innerRadius={58}
                            outerRadius={88}
                            paddingAngle={1}
                          >
                            {projectAnnualContribution.clientChargeData.map((client) => (
                              <Cell key={`client-charge-pie-${client.id}`} fill={client.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            content={(props) =>
                              renderContributionTooltip(
                                props,
                                "charge",
                                (value) => `${formatHours(value)} h`,
                                projectAnnualContribution.totals.charge
                              )
                            }
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                      {projectAnnualContribution.clientChargeData.map((client) => (
                        <div key={`client-charge-row-${client.id}`} className="grid grid-cols-[1fr_auto] gap-3 text-xs">
                          <div className="flex min-w-0 items-center gap-2">
                            <span
                              className="h-2.5 w-2.5 shrink-0 rounded-full"
                              style={{ backgroundColor: client.color }}
                            />
                            <span className="truncate text-slate-700" title={client.name}>
                              {client.name}
                            </span>
                            {client.clientType === CLIENT_TYPE_INTERNAL_INVESTMENT && (
                              <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                                Interne
                              </span>
                            )}
                          </div>
                          <div className="text-right font-semibold text-slate-900">
                            <div>{formatPercent(client.chargePct)}</div>
                            <div className="text-[11px] font-medium text-slate-500">
                              {formatHours(client.charge)} h
                            </div>
                          </div>
                        </div>
                      ))}
                      {!projectAnnualContribution.clientChargeData.length && (
                        <div className="rounded border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-400">
                          Aucune charge client sur l'annee.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-100 bg-white p-3">
                  <div className="text-xs font-semibold uppercase text-gray-500">
                    CA client
                  </div>
                  <div className="mt-3 grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={projectAnnualContribution.clientCaData}
                            dataKey="ca"
                            nameKey="name"
                            innerRadius={58}
                            outerRadius={88}
                            paddingAngle={1}
                          >
                            {projectAnnualContribution.clientCaData.map((client) => (
                              <Cell key={`client-ca-pie-${client.id}`} fill={client.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            content={(props) =>
                              renderContributionTooltip(
                                props,
                                "ca",
                                formatCurrency,
                                projectAnnualContribution.totals.ca
                              )
                            }
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                      {projectAnnualContribution.clientCaData.map((client) => (
                        <div key={`client-ca-row-${client.id}`} className="grid grid-cols-[1fr_auto] gap-3 text-xs">
                          <div className="flex min-w-0 items-center gap-2">
                            <span
                              className="h-2.5 w-2.5 shrink-0 rounded-full"
                              style={{ backgroundColor: client.color }}
                            />
                            <span className="truncate text-slate-700" title={client.name}>
                              {client.name}
                            </span>
                            {client.clientType === CLIENT_TYPE_INTERNAL_INVESTMENT && (
                              <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                                Interne
                              </span>
                            )}
                          </div>
                          <div className="text-right font-semibold text-slate-900">
                            <div>{formatPercent(client.caPct)}</div>
                            <div className="text-[11px] font-medium text-slate-500">
                              {formatCurrencyShort(client.ca)}
                            </div>
                          </div>
                        </div>
                      ))}
                      {!projectAnnualContribution.clientCaData.length && (
                        <div className="rounded border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-400">
                          Aucun CA client sur l'annee.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
            </>
          )}
          {activePilotageTab === "accounting" && (
            <>
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
              <div className="text-sm font-medium text-gray-900">Tresorerie cumulative (EUR)</div>
              <div className="text-xs text-gray-500">
                Encaissement a la fin d'iteration + conditions client. Decaissement au debut.
              </div>
            </div>
            <div className="mt-3 h-96">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={cumulativeCashFlowData}
                  onClick={handleChartClick}
                  margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tickMargin={8} />
                  <YAxis tickFormatter={formatCurrencyShort} />
                  <Tooltip formatter={(value, name) => [formatCurrency(value), name]} />
                  {separateInternalInvestment ? (
                    <>
                      {CLIENT_TYPE_SEGMENTS.map((segment) => (
                        <Area
                          key={`${segment.id}-net-cumulative`}
                          type="monotone"
                          dataKey={`${segment.id}NetCumulative`}
                          name={`Net cumule - ${segment.label}`}
                          stroke={segment.color}
                          fill={segment.color}
                          fillOpacity={0.16}
                          strokeWidth={2}
                        />
                      ))}
                      <Area
                        type="monotone"
                        dataKey="netCumulative"
                        name="Tresorerie nette totale"
                        stroke="#0f172a"
                        fill="#0f172a"
                        fillOpacity={0.05}
                        strokeWidth={2.5}
                      />
                    </>
                  ) : (
                    <>
                      <Area
                        type="monotone"
                        dataKey="inflowCumulative"
                        name="Encaissements cumules"
                        stroke="#16a34a"
                        fill="#16a34a"
                        fillOpacity={0.14}
                        strokeWidth={2}
                      />
                      <Area
                        type="monotone"
                        dataKey="outflowCumulative"
                        name="Decaissements cumules"
                        stroke="#e11d48"
                        fill="#e11d48"
                        fillOpacity={0.12}
                        strokeWidth={2}
                      />
                      <Area
                        type="monotone"
                        dataKey="netCumulative"
                        name="Tresorerie nette cumulee"
                        stroke="#0f172a"
                        fill="#0f172a"
                        fillOpacity={0.08}
                        strokeWidth={2.5}
                      />
                    </>
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <div className="text-sm font-medium text-gray-900">Flux de tresorerie par mois (EUR)</div>
            <div className="mt-3 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={cashFlowByMonth}
                  onClick={handleChartClick}
                  margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tickMargin={8} />
                  <YAxis tickFormatter={formatCurrencyShort} />
                  <Tooltip formatter={(value, name) => [formatCurrency(value), name]} />
                  {separateInternalInvestment ? (
                    CLIENT_TYPE_SEGMENTS.map((segment) => (
                      <Bar
                        key={`${segment.id}-net`}
                        dataKey={`${segment.id}Net`}
                        name={`Net - ${segment.label}`}
                        fill={segment.color}
                        radius={[4, 4, 0, 0]}
                      />
                    ))
                  ) : (
                    <>
                      <Bar
                        dataKey="inflow"
                        name="Encaissements"
                        fill="#16a34a"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="outflow"
                        name="Decaissements"
                        fill="#e11d48"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="net"
                        name="Net"
                        fill="#334155"
                        radius={[4, 4, 0, 0]}
                      />
                    </>
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
            </>
          )}
        </div>
        {activePilotageTab && (
        <div className="space-y-4">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {metricsByMonth.map((month) => {
              const ratio =
                month.capacityHours > 0 ? (month.chargeHours / month.capacityHours) * 100 : 0;
              const isActive = month.key === selectedMonthKey;
              return (
                <button
                  key={month.key}
                  onClick={() => setSelectedMonthKey(month.key)}
                  className={`min-w-[96px] rounded-lg border px-3 py-2 text-left transition ${
                    isActive
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <div className="text-[11px] uppercase text-gray-500">{month.label}</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {activePilotageTab === "production"
                      ? `${formatHours(month.chargeHours)}h`
                      : formatCurrency(cashFlowByMonth.find((item) => item.key === month.key)?.net || 0)}
                  </div>
                  {activePilotageTab === "production" && (
                  <div className="mt-2 h-1.5 w-full rounded-full bg-gray-100">
                    <div
                      className={`h-full rounded-full ${
                        ratio > 100 ? "bg-rose-500" : "bg-emerald-500"
                      }`}
                      style={{ width: `${Math.min(100, ratio)}%` }}
                    />
                  </div>
                  )}
                </button>
              );
            })}
          </div>
          {selectedMonth && (
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase text-slate-500">
                    Mois selectionne
                  </div>
                  <div className="mt-1 text-xl font-semibold text-slate-950">
                    {selectedMonth.label}
                  </div>
                </div>
                <div className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600">
                  {activePilotageTab === "production"
                    ? "Lecture production"
                    : "Lecture compta"}
                </div>
              </div>

              {activePilotageTab === "production" ? (
                <>
                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-6">
                  <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                    <div className="text-xs text-slate-500">Capacite disponible</div>
                    <div className="mt-1 text-lg font-semibold text-slate-950">
                      {formatHours(selectedMonth.capacityHours)} h
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                    <div className="text-xs text-slate-500">Charge planifiee</div>
                    <div className="mt-1 text-lg font-semibold text-slate-950">
                      {formatHours(selectedMonth.chargeHours)} h
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                    <div className="text-xs text-slate-500">Charge / capacite</div>
                    <div
                      className={`mt-1 text-lg font-semibold ${
                        selectedRatio > 100 ? "text-rose-600" : "text-emerald-700"
                      }`}
                    >
                      {Number.isFinite(selectedRatio) ? `${Math.round(selectedRatio)}%` : "-"}
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-slate-200">
                      <div
                        className={`h-full rounded-full ${
                          selectedRatio > 100 ? "bg-rose-500" : "bg-emerald-500"
                        }`}
                        style={{ width: `${Math.min(100, selectedRatio)}%` }}
                      />
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                    <div className="text-xs text-slate-500">CA produit acquis</div>
                    <div className="mt-1 text-lg font-semibold text-slate-950">
                      {formatCurrency(selectedMonth.caActualEur)}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                    <div className="text-xs text-slate-500">CA produit prev.</div>
                    <div className="mt-1 text-lg font-semibold text-slate-950">
                      {formatCurrency(selectedMonth.caForecastEur)}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                    <div className="text-xs text-slate-500">Reste futur cumule</div>
                    <div className="mt-1 text-lg font-semibold text-amber-700">
                      {formatCurrency(selectedProductionProgressMonth?.remaining || 0)}
                    </div>
                  </div>
                </div>
                {showCapacityRevenueProjection && selectedCapacityProjectionMonth && (
                  <div className="mt-3 grid gap-3 md:grid-cols-3">
                    <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
                      <div className="text-xs text-blue-700">Futur productible du mois</div>
                      <div className="mt-1 text-lg font-semibold text-blue-700">
                        {formatCurrency(selectedCapacityProjectionMonth.futureProductible)}
                      </div>
                    </div>
                    <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-3">
                      <div className="text-xs text-rose-700">CA perdu du mois</div>
                      <div className="mt-1 text-lg font-semibold text-rose-700">
                        {formatCurrency(selectedCapacityProjectionMonth.lostCa)}
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                      <div className="text-xs text-slate-500">Surcharge du mois</div>
                      <div className="mt-1 text-lg font-semibold text-slate-950">
                        {formatHours(selectedCapacityProjectionMonth.overloadHours)} h
                      </div>
                    </div>
                  </div>
                )}
                </>
              ) : (
                <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_1.2fr]">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                      <div className="text-xs text-slate-500">CA produit du mois</div>
                      <div className="mt-1 text-lg font-semibold text-slate-950">
                        {formatCurrency(selectedMonth.caActualEur + selectedMonth.caForecastEur)}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        Hors date d'encaissement
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                      <div className="text-xs text-slate-500">Depenses prevues</div>
                      <div className="mt-1 text-lg font-semibold text-slate-950">
                        {formatCurrency(selectedMonth.expensesForecastEur)}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        Affectees au mois de production
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                    <div className="grid gap-3 md:grid-cols-3">
                      <div>
                        <div className="text-xs text-slate-500">Encaissements</div>
                        <div className="mt-1 text-lg font-semibold text-emerald-700">
                          {formatCurrency(selectedCashFlowMonth?.inflow || 0)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Decaissements</div>
                        <div className="mt-1 text-lg font-semibold text-rose-600">
                          {formatCurrency(selectedCashFlowMonth?.outflow || 0)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Tresorerie nette</div>
                        <div
                          className={`mt-1 text-lg font-semibold ${
                            (selectedCashFlowMonth?.net || 0) < 0
                              ? "text-rose-600"
                              : "text-slate-950"
                          }`}
                        >
                          {formatCurrency(selectedCashFlowMonth?.net || 0)}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 grid gap-2 text-xs text-slate-500 sm:grid-cols-2">
                      <div className="rounded border border-emerald-100 bg-white px-3 py-2">
                        Encaissements = paiements attendus selon les conditions client.
                      </div>
                      <div className="rounded border border-rose-100 bg-white px-3 py-2">
                        Decaissements = sorties prevues au debut des iterations.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        )}
        {activePilotageTab === "production" && selectedMonth && (
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-sm font-medium text-gray-900">
                  Focus mois: {selectedMonth.label}
                </div>
                <div className="text-xs text-gray-500">
                  Detail charge, CA produit et repartition par projet.
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {formatHours(selectedMonth.chargeHours)}h charge ·{" "}
                {formatHours(selectedMonth.capacityHours)}h dispo
              </div>
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-3">
              <div className="rounded-lg border border-gray-100 bg-white p-3">
                <div className="text-xs font-semibold uppercase text-gray-500">
                  Projets actifs
                </div>
                <div className="mt-3 max-h-96 space-y-2 overflow-y-auto pr-1">
                  {selectedMonthProjects.map((project) => (
                    <div
                      key={project.id}
                      className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: project.color }}
                        />
                        <div className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">
                          {project.name}
                        </div>
                        {project.clientType === CLIENT_TYPE_INTERNAL_INVESTMENT && (
                          <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                            Interne
                          </span>
                        )}
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-600">
                        <div>
                          <span className="block text-slate-400">Charge</span>
                          <span className="font-semibold text-slate-900">
                            {formatHours(project.chargeTotal)} h
                          </span>
                        </div>
                        <div>
                          <span className="block text-slate-400">CA produit</span>
                          <span className="font-semibold text-slate-900">
                            {formatCurrency(project.caTotal)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {!selectedMonthProjects.length && (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-400">
                      Aucun projet actif sur ce mois.
                    </div>
                  )}
                </div>
              </div>
              <div className="lg:col-span-2 space-y-4">
                <div className="rounded-lg border border-gray-100 bg-white p-3">
                  <div className="text-xs font-semibold uppercase text-gray-500">
                    Repartition charge par projet
                  </div>
                  <div
                    className="mt-3"
                    style={{ height: selectedMonthProjectChartHeight }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={selectedMonthProjects}
                        layout="vertical"
                        margin={{ top: 0, right: 10, left: 16, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={formatHours} />
                        <YAxis
                          dataKey="name"
                          type="category"
                          width={220}
                          interval={0}
                          tick={<ProjectAxisTick />}
                        />
                        <Tooltip content={renderProjectChargeTooltip} />
                        <defs>
                          {selectedMonthProjects.map((project) => (
                            <pattern
                              key={`focus-charge-${project.id}`}
                              id={`focus-charge-${project.id}`}
                              patternUnits="userSpaceOnUse"
                              width="8"
                              height="8"
                              patternTransform="rotate(45)"
                            >
                              <line
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="8"
                                stroke={project.color}
                                strokeWidth="2"
                              />
                            </pattern>
                          ))}
                        </defs>
                        <Bar
                          dataKey="chargeActual"
                          name="Charge acquise"
                          stackId="focus-charge"
                        >
                          {selectedMonthProjects.map((project) => (
                            <Cell key={`charge-actual-${project.id}`} fill={project.color} />
                          ))}
                        </Bar>
                        <Bar
                          dataKey="chargeForecast"
                          name="Charge prev."
                          stackId="focus-charge"
                        >
                          {selectedMonthProjects.map((project) => (
                            <Cell
                              key={`charge-forecast-${project.id}`}
                              fill={`url(#focus-charge-${project.id})`}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="rounded-lg border border-gray-100 bg-white p-3">
                  <div className="text-xs font-semibold uppercase text-gray-500">
                    Repartition CA par projet
                  </div>
                  <div
                    className="mt-3"
                    style={{ height: selectedMonthProjectChartHeight }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={selectedMonthProjects}
                        layout="vertical"
                        margin={{ top: 0, right: 10, left: 16, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={formatCurrencyShort} />
                        <YAxis
                          dataKey="name"
                          type="category"
                          width={220}
                          interval={0}
                          tick={<ProjectAxisTick />}
                        />
                        <Tooltip content={renderProjectCaTooltip} />
                        <defs>
                          {selectedMonthProjects.map((project) => (
                            <pattern
                              key={`focus-ca-${project.id}`}
                              id={`focus-ca-${project.id}`}
                              patternUnits="userSpaceOnUse"
                              width="8"
                              height="8"
                              patternTransform="rotate(45)"
                            >
                              <line
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="8"
                                stroke={project.color}
                                strokeWidth="2"
                              />
                            </pattern>
                          ))}
                        </defs>
                        <Bar dataKey="caActual" name="CA acquis" stackId="focus-ca">
                          {selectedMonthProjects.map((project) => (
                            <Cell key={`ca-actual-${project.id}`} fill={project.color} />
                          ))}
                        </Bar>
                        <Bar dataKey="caForecast" name="CA prev." stackId="focus-ca">
                          {selectedMonthProjects.map((project) => (
                            <Cell
                              key={`ca-forecast-${project.id}`}
                              fill={`url(#focus-ca-${project.id})`}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
            {!selectedMonthProjects.length && (
              <div className="mt-4 text-sm text-gray-400">
                Aucun projet sur ce mois.
              </div>
            )}
          </div>
        )}
        {activePilotageTab === "accounting" && selectedMonth && (
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-sm font-medium text-gray-900">
                  Detail compta: {selectedMonth.label}
                </div>
                <div className="text-xs text-gray-500">
                  Le CA produit explique l'activite du mois. Les flux cash suivent les dates
                  d'encaissement et de decaissement.
                </div>
              </div>
              <div
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  (selectedCashFlowMonth?.net || 0) < 0
                    ? "bg-rose-100 text-rose-700"
                    : "bg-emerald-100 text-emerald-700"
                }`}
              >
                Net cash {formatCurrency(selectedCashFlowMonth?.net || 0)}
              </div>
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="rounded-lg border border-slate-100 bg-white p-3">
                <div className="text-xs font-semibold uppercase text-gray-500">
                  Flux de tresorerie du mois
                </div>
                <div className="mt-3 space-y-3 text-sm">
                  <div className="flex items-center justify-between rounded-lg bg-emerald-50 px-3 py-2">
                    <span className="text-emerald-800">Encaissements</span>
                    <span className="font-semibold text-emerald-800">
                      {formatCurrency(selectedCashFlowMonth?.inflow || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-rose-50 px-3 py-2">
                    <span className="text-rose-800">Decaissements</span>
                    <span className="font-semibold text-rose-800">
                      {formatCurrency(selectedCashFlowMonth?.outflow || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                    <span className="text-slate-700">Tresorerie nette</span>
                    <span className="font-semibold text-slate-950">
                      {formatCurrency(selectedCashFlowMonth?.net || 0)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-slate-100 bg-white p-3">
                <div className="text-xs font-semibold uppercase text-gray-500">
                  Commandes et echeances du mois
                </div>
                <div className="mt-3 max-h-96 space-y-2 overflow-y-auto pr-1">
                  {selectedCashFlowEntries.map((entry) => (
                    <div
                      key={entry.id}
                      className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex min-w-0 items-center gap-2">
                            <span
                              className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                entry.direction === "outflow"
                                  ? "bg-rose-100 text-rose-700"
                                  : "bg-emerald-100 text-emerald-700"
                              }`}
                            >
                              {entry.direction === "outflow" ? "Decaissement" : "Encaissement"}
                            </span>
                            <span className="truncate font-semibold text-slate-900">
                              {entry.title}
                            </span>
                          </div>
                          <div className="mt-1 truncate text-xs text-slate-500">
                            {entry.subtitle || "Commande non rattachee"}
                          </div>
                        </div>
                        <div
                          className={`shrink-0 text-right font-semibold ${
                            entry.direction === "outflow" ? "text-rose-700" : "text-emerald-700"
                          }`}
                        >
                          {formatCurrency(entry.signedAmount)}
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-slate-500">
                        <span className="rounded border border-slate-200 bg-white px-2 py-0.5">
                          {formatDateShort(entry.date)}
                        </span>
                        {entry.isEstimatedDate && (
                          <span className="rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700">
                            Date estimee
                          </span>
                        )}
                        {entry.eventType && (
                          <span className="rounded border border-slate-200 bg-white px-2 py-0.5">
                            {entry.eventType}
                          </span>
                        )}
                        {entry.status && (
                          <span className="rounded border border-slate-200 bg-white px-2 py-0.5">
                            {entry.status}
                          </span>
                        )}
                        {entry.invoiceReference && (
                          <span className="rounded border border-slate-200 bg-white px-2 py-0.5">
                            Facture {entry.invoiceReference}
                          </span>
                        )}
                      </div>
                      {entry.allocations?.length > 0 && (
                        <div className="mt-2 space-y-1 border-t border-slate-200 pt-2 text-xs text-slate-600">
                          {entry.allocations.slice(0, 4).map((allocation, index) => (
                            <div
                              key={`${entry.id}-allocation-${index}`}
                              className="flex items-center justify-between gap-3"
                            >
                              <span className="truncate">{allocation.label}</span>
                              {allocation.amount > 0 && (
                                <span className="shrink-0 font-medium text-slate-800">
                                  {formatCurrency(allocation.amount)}
                                </span>
                              )}
                            </div>
                          ))}
                          {entry.allocations.length > 4 && (
                            <div className="text-slate-400">
                              + {entry.allocations.length - 4} lignes
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {!selectedCashFlowEntries.length && (
                    <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-8 text-center text-sm text-slate-400">
                      Aucun flux compta sur ce mois.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
        </>
      )}

      {activePilotageTab === "statistics" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-xs text-gray-500">CA produit a date</div>
              <div className="text-lg font-semibold">
                {formatCurrency(statisticsData.totals.producedCaToDate)}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                {statisticsData.hasElapsed
                  ? `Au ${formatDateShort(statisticsData.elapsedEnd)}`
                  : "Annee future"}
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-xs text-gray-500">Capacite prod. utilisee</div>
              <div className="text-lg font-semibold">
                {formatHours(statisticsData.totals.productiveToDate)} h
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Presence x dispo x taux prod
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-xs text-gray-500">CA / h disponible</div>
              <div className="text-lg font-semibold">
                {formatCurrencyPerHour(statisticsData.totals.currentCaPerProductiveHour)}
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-xs text-gray-500">CA / h saisie x prod</div>
              <div className="text-lg font-semibold">
                {formatCurrencyPerHour(statisticsData.totals.currentCaPerEnteredProductionHour)}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Hors {statisticsData.nonProductionProjectName}
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-xs text-gray-500">CA futur restant</div>
              <div className="text-lg font-semibold">
                {formatCurrency(statisticsData.totals.remainingCa)}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Capacite future {formatHours(statisticsData.totals.productiveFuture)} h
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-xs text-gray-500">CA / h requis futur</div>
              <div
                className={`text-lg font-semibold ${
                  Number.isFinite(statisticsData.totals.caPerHourGap) &&
                  statisticsData.totals.caPerHourGap > 0
                    ? "text-amber-700"
                    : "text-emerald-700"
                }`}
              >
                {formatCurrencyPerHour(statisticsData.totals.requiredFutureCaPerHour)}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Ecart {formatCurrencyPerHour(statisticsData.totals.caPerHourGap)}
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6 space-y-6">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Statistiques annuelles</h2>
                <p className="text-sm text-gray-500">
                  Lecture a date des heures saisies ponderees prod, de la capacite productive et du CA/h a tenir.
                </p>
              </div>
              <div className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600">
                Projet hors production: {statisticsData.nonProductionProjectName}
              </div>
            </div>

            {(employeeHoursLoading || employeeHoursError) && (
              <div
                className={`rounded-lg border px-4 py-3 text-sm ${
                  employeeHoursError
                    ? "border-amber-200 bg-amber-50 text-amber-800"
                    : "border-blue-100 bg-blue-50 text-blue-800"
                }`}
              >
                {employeeHoursError || "Chargement des heures saisies..."}
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="text-xs text-slate-500">Heures attendues prod.</div>
                <div className="mt-1 text-lg font-semibold text-slate-950">
                  {formatHours(statisticsData.totals.trackedExpectedToDate)} h
                </div>
              </div>
              <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="text-xs text-slate-500">Heures saisies x prod</div>
                <div className="mt-1 text-lg font-semibold text-slate-950">
                  {formatHours(statisticsData.totals.actualToDate)} h
                </div>
              </div>
              <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="text-xs text-slate-500">Taux de saisie</div>
                <div
                  className={`mt-1 text-lg font-semibold ${
                    statisticsData.totals.entryRate < 90
                      ? "text-amber-700"
                      : "text-emerald-700"
                  }`}
                >
                  {formatPercent(statisticsData.totals.entryRate)}
                </div>
              </div>
              <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="text-xs text-slate-500">Taux hors prod reel</div>
                <div className="mt-1 text-lg font-semibold text-slate-950">
                  {formatOptionalPercent(statisticsData.totals.realNonProductionPct)}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {formatHours(statisticsData.totals.rawNonProductionHours)} h hors prod /
                  {" "}
                  {formatHours(statisticsData.totals.rawProductionProjectHours)} h projets
                </div>
              </div>
              <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="text-xs text-slate-500">Charge future / capacite</div>
                <div
                  className={`mt-1 text-lg font-semibold ${
                    statisticsData.totals.futureLoadPct > 100
                      ? "text-rose-700"
                      : "text-slate-950"
                  }`}
                >
                  {formatPercent(statisticsData.totals.futureLoadPct)}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {formatHours(statisticsData.totals.futurePlannedCharge)} h planifiees
                </div>
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-[0.8fr_1.2fr]">
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <div className="text-sm font-medium text-gray-900">
                  CA/h actuel vs requis
                </div>
                <div className="mt-3 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={statisticsData.efficiencyBars}
                      layout="vertical"
                      margin={{ top: 10, right: 24, left: 8, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={formatCurrencyShort} />
                      <YAxis dataKey="label" type="category" width={120} />
                      <Tooltip formatter={(value) => [formatCurrencyPerHour(value), ""]} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {statisticsData.efficiencyBars.map((bar, index) => (
                          <Cell
                            key={bar.label}
                            fill={index === 0 ? "#2563eb" : "#f59e0b"}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                  <div className="text-sm font-medium text-gray-900">
                    Heures prod. attendues vs saisies
                  </div>
                  <div className="text-xs text-gray-500">
                    Donnees arretees au {statisticsData.hasElapsed ? formatDateShort(statisticsData.elapsedEnd) : "-"}
                  </div>
                </div>
                <div className="mt-3 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={statisticsData.monthlyData}
                      margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tickMargin={8} />
                      <YAxis tickFormatter={formatHours} />
                      <Tooltip formatter={(value, name) => [`${formatHours(value)} h`, name]} />
                      <Bar
                        dataKey="expectedHours"
                        name="Heures attendues prod."
                        fill="#cbd5e1"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="productionActualHours"
                        name="Heures saisies prod. x taux"
                        stackId="actual-hours"
                        fill="#2563eb"
                      />
                      <Bar
                        dataKey="nonProductionHours"
                        name="Heures hors prod. x taux"
                        stackId="actual-hours"
                        fill="#f59e0b"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-100 bg-white">
              <div className="flex flex-col gap-1 border-b border-slate-100 px-4 py-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase text-gray-500">
                    Saisie par employe
                  </div>
                  <div className="text-sm text-slate-500">
                    Rouge = moins de 80% des heures productives attendues sur le mois.
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  {statisticsData.employeeRows.length} ligne(s)
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[1120px] text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Employe</th>
                      <th className="px-3 py-2 text-right">Attendu prod.</th>
                      <th className="px-3 py-2 text-right">Saisi x prod</th>
                      <th className="px-3 py-2 text-right">Ecart</th>
                      <th className="px-3 py-2 text-right">Hors prod reel</th>
                      <th className="px-3 py-2 text-left">Timeline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statisticsData.employeeRows.map((row) => (
                      <tr key={row.key} className="border-b border-slate-100">
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-900">{row.name}</div>
                          <div className="text-xs text-slate-500">
                            {row.source === "addition" ? "Renfort scenario" : "Employe"}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {formatHours(row.expectedToDate)} h
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="font-medium text-slate-900">
                            {formatHours(row.actualToDate)} h
                          </div>
                          <div className="text-xs text-slate-500">
                            {formatPercent(row.enteredPct)}
                          </div>
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-semibold ${
                            row.gapHours < -4
                              ? "text-rose-700"
                              : row.gapHours < 0
                              ? "text-amber-700"
                              : "text-emerald-700"
                          }`}
                        >
                          {formatSignedHours(row.gapHours)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="font-medium text-slate-900">
                            {formatOptionalPercent(row.realNonProductionPct)}
                          </div>
                          <div className="text-xs text-slate-500">
                            {formatHours(row.rawNonProductionHours)} h /{" "}
                            {formatHours(row.rawProductionProjectHours)} h
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="grid min-w-[440px] grid-cols-12 gap-1">
                            {row.months.map((month) => {
                              const fillPct = Math.min(100, Math.max(0, month.enteredPct));
                              const isMissing = month.expectedHours > 0 && month.enteredPct < 80;
                              const isLow = month.expectedHours > 0 && month.enteredPct < 95;
                              const hasExtra = month.enteredPct > 115;
                              const colorClass = isMissing
                                ? "bg-rose-500"
                                : isLow
                                ? "bg-amber-500"
                                : hasExtra
                                ? "bg-blue-500"
                                : "bg-emerald-500";
                              const title = `${month.label}: attendu prod ${formatHours(
                                month.expectedHours
                              )} h, saisi x prod ${formatHours(month.actualHours)} h, ecart ${formatSignedHours(
                                month.gapHours
                              )}`;
                              return (
                                <div key={month.key} className="min-w-0" title={title}>
                                  <div className="mb-1 truncate text-center text-[10px] text-slate-400">
                                    {month.label}
                                  </div>
                                  <div className="h-6 rounded bg-slate-100">
                                    {(month.expectedHours > 0 || month.actualHours > 0) && (
                                      <div
                                        className={`h-full rounded ${colorClass}`}
                                        style={{ width: `${fillPct}%` }}
                                      />
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!statisticsData.employeeRows.length && (
                      <tr>
                        <td colSpan={6} className="px-3 py-6 text-center text-sm text-slate-400">
                          Aucune donnee employe disponible pour cette annee.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {activePilotageTab === "invoices" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-xs text-gray-500">Factures</div>
              <div className="text-lg font-semibold">{invoiceTimelineData.totals.count}</div>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-xs text-gray-500">Montant total</div>
              <div className="text-lg font-semibold">
                {formatCurrency(invoiceTimelineData.totals.amount)}
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-xs text-gray-500">A facturer / prevu</div>
              <div className="text-lg font-semibold text-amber-700">
                {formatCurrency(invoiceTimelineData.totals.planned)}
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-xs text-gray-500">Eligibles factor</div>
              <div className="text-lg font-semibold text-blue-700">
                {invoiceTimelineData.totals.factorable}
              </div>
              <div className="text-xs text-gray-500">
                {formatCurrency(invoiceTimelineData.totals.factorableAmount)}
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <div className="text-xs text-gray-500">Factorisees</div>
              <div className="text-lg font-semibold text-blue-700">
                {invoiceTimelineData.totals.factored}
              </div>
              <div className="text-xs text-gray-500">
                {invoiceTimelineData.totals.acceleratedDays} j avances
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">
                  Timeline des factures {year}
                </h2>
                <p className="text-sm text-gray-500">
                  Encaissements attendus selon les conditions client et les overrides factor du scenario.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
                  Emission
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  Echeance client
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-slate-900" />
                  Scenario factor
                </span>
              </div>
            </div>

            <div className="mt-5 overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <div className="min-w-[1040px] p-4">
                <div
                  className="relative"
                  style={{ height: `${invoiceTimelineData.timelineHeight}px` }}
                >
                  <div className="absolute inset-x-0 top-0 grid grid-cols-12 border-b border-slate-200 pb-2 text-center text-xs font-medium uppercase text-slate-500">
                    {monthRanges.map((month) => (
                      <div key={month.key}>{month.label}</div>
                    ))}
                  </div>

                  <div className="absolute inset-x-0 top-9 bottom-0 grid grid-cols-12 rounded-lg bg-slate-50/70">
                    {monthRanges.map((month) => (
                      <div key={month.key} className="border-l border-slate-200 first:border-l-0" />
                    ))}
                  </div>

                  {invoiceTimelineData.entries.map((invoice) => {
                    const markerClasses = invoice.isFactored
                      ? "bg-slate-900 ring-slate-300"
                      : invoice.isPlanned
                        ? "bg-amber-400 ring-amber-100"
                        : "bg-emerald-500 ring-emerald-100";
                    return (
                      <React.Fragment key={invoice.id}>
                        {invoice.issueDateKey && (
                          <>
                            <span
                              className="absolute z-10 h-px bg-slate-300"
                              style={{
                                left: `${invoice.cashLineLeftPct}%`,
                                top: `${invoice.laneTop}px`,
                                width: `${invoice.cashLineWidthPct}%`,
                              }}
                            />
                            <span
                              className="absolute z-20 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-blue-500 shadow"
                              style={{
                                left: `${invoice.issuePct}%`,
                                top: `${invoice.laneTop}px`,
                              }}
                            />
                          </>
                        )}
                        {invoice.isFactored && invoice.originalPaymentDateKey && (
                          <span
                            className="absolute z-20 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-emerald-500 bg-white shadow"
                            style={{
                              left: `${invoice.originalPaymentPct}%`,
                              top: `${invoice.laneTop}px`,
                            }}
                          />
                        )}
                        <button
                          type="button"
                          aria-disabled={!invoice.canFactor}
                          aria-label={
                            invoice.canFactor
                              ? invoice.isFactored
                                ? `Defactoriser ${invoice.title}`
                                : `Factoriser ${invoice.title}`
                              : `${invoice.title} non factorisable`
                          }
                          title={invoice.title}
                          onClick={() => {
                            if (!invoice.canFactor) return;
                            setInvoiceFactored(invoice.id, !invoice.isFactored);
                          }}
                          onMouseEnter={(event) => showInvoiceTimelineTooltip(event, invoice)}
                          onMouseLeave={() => setHoveredInvoiceTimelineItem(null)}
                          onFocus={(event) => showInvoiceTimelineTooltip(event, invoice)}
                          onBlur={() => setHoveredInvoiceTimelineItem(null)}
                          className={`absolute z-30 flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white shadow ring-2 transition hover:scale-125 ${
                            invoice.canFactor ? "cursor-pointer" : "cursor-default"
                          } ${markerClasses}`}
                          style={{
                            left: `${invoice.scenarioPaymentPct}%`,
                            top: `${invoice.laneTop}px`,
                          }}
                        >
                          <span className="sr-only">{formatCurrencyShort(invoice.amount)}</span>
                        </button>
                      </React.Fragment>
                    );
                  })}

                  {!invoiceTimelineData.entries.length && (
                    <div className="absolute inset-x-0 top-16 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-400">
                      Aucune facture sur la periode selectionnee.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-3 text-xs text-slate-500 md:grid-cols-3">
              <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                Une facture factorisee tombe a sa date d'emission dans le scenario.
              </div>
              <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                Une facture non factorisee tombe a son echeance client.
              </div>
              <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                Les factures a reception ne sont pas factorisables.
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">
                  Detail mensuel des factures
                </h2>
                <p className="text-sm text-gray-500">
                  Liste groupee par mois d'encaissement scenario, apres application du factor.
                </p>
              </div>
              <div className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600">
                {invoiceTimelineData.totals.count} facture
                {invoiceTimelineData.totals.count > 1 ? "s" : ""}
              </div>
            </div>

            <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-[1100px] text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Facture</th>
                    <th className="px-3 py-2 text-left">Statut</th>
                    <th className="px-3 py-2 text-left">Emission</th>
                    <th className="px-3 py-2 text-left">Echeance client</th>
                    <th className="px-3 py-2 text-left">Scenario</th>
                    <th className="px-3 py-2 text-right">Montant</th>
                    <th className="px-3 py-2 text-left">Reglement</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoiceTimelineEntriesByMonth.map((group) => (
                    <React.Fragment key={group.key}>
                      <tr className="bg-slate-100/80">
                        <td colSpan={8} className="px-3 py-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="font-semibold text-slate-900">{group.label}</div>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                              <span>{group.entries.length} facture{group.entries.length > 1 ? "s" : ""}</span>
                              <span className="rounded-full bg-white px-2 py-0.5 font-semibold text-slate-900">
                                {formatCurrency(group.amount)}
                              </span>
                              {group.factored > 0 && (
                                <span className="rounded-full bg-blue-50 px-2 py-0.5 font-semibold text-blue-700">
                                  {group.factored} factorisee{group.factored > 1 ? "s" : ""}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                      {group.entries.map((invoice) => (
                        <tr key={`invoice-detail-${invoice.id}`} className="bg-white hover:bg-slate-50">
                          <td className="px-3 py-2 align-top">
                            <div className="max-w-[280px]">
                              <div className="truncate font-semibold text-slate-900" title={invoice.title}>
                                {invoice.title}
                              </div>
                              <div className="mt-0.5 truncate text-xs text-slate-500" title={invoice.subtitle}>
                                {invoice.subtitle || "Commande non rattachee"}
                              </div>
                              {invoice.projectLabel && (
                                <div className="mt-0.5 truncate text-[11px] text-slate-400" title={invoice.projectLabel}>
                                  {invoice.projectLabel}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 align-top">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                                invoice.isPlanned
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-emerald-100 text-emerald-700"
                              }`}
                            >
                              {invoice.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 align-top text-slate-700">
                            <div>{formatDateShort(invoice.issueDateKey)}</div>
                            {invoice.issueDateIsEstimated && (
                              <div className="text-xs text-slate-400">estimee</div>
                            )}
                          </td>
                          <td className="px-3 py-2 align-top text-slate-700">
                            <div>{formatDateShort(invoice.originalPaymentDateKey)}</div>
                            {Number.isFinite(invoice.delayDays) && invoice.delayDays > 0 && (
                              <div className="text-xs text-slate-400">+{invoice.delayDays} j</div>
                            )}
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className={invoice.isFactored ? "font-semibold text-slate-950" : "text-slate-700"}>
                              {formatDateShort(invoice.scenarioPaymentDateKey)}
                            </div>
                            <div className="text-xs text-slate-400">
                              {invoice.isFactored ? "Factor" : "Client"}
                            </div>
                          </td>
                          <td className="px-3 py-2 align-top text-right font-semibold text-slate-900">
                            {formatCurrency(invoice.amount)}
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className="max-w-[180px] truncate text-slate-700" title={invoice.paymentTerms || ""}>
                              {invoice.paymentTerms || "-"}
                            </div>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {invoice.canFactor ? (
                                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                                  Factorisable
                                </span>
                              ) : (
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
                                  A reception
                                </span>
                              )}
                              {invoice.isFactored && (
                                <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-semibold text-white">
                                  Override factor
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 align-top text-right">
                            <button
                              type="button"
                              disabled={!invoice.canFactor}
                              onClick={() => setInvoiceFactored(invoice.id, !invoice.isFactored)}
                              className={`rounded-full border px-3 py-1 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${
                                invoice.isFactored
                                  ? "border-slate-900 bg-slate-900 text-white hover:bg-slate-700"
                                  : "border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:text-blue-700"
                              }`}
                            >
                              {invoice.isFactored ? "Defactoriser" : "Factoriser"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {!invoiceTimelineEntriesByMonth.length && (
                    <tr>
                      <td colSpan={8} className="px-3 py-10 text-center text-sm text-slate-400">
                        Aucune facture sur la periode selectionnee.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {renderInvoiceTimelineTooltip()}

      {activePilotageTab === "projects" && (
      <div className="space-y-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-2xl">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Pilotage projets
              </div>
              <h2 className="mt-1 text-lg font-semibold text-slate-900">
                Roadmap annuelle {year}
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Fenetre fixe du 01/01 au 31/12. Les iterations longues sont recadrees sur
                l'annee au lieu de decaler toute la timeline.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5 xl:min-w-[520px]">
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Projets</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">{roadmapStats.projects}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Iterations</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">{roadmapStats.iterations}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Jalons</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">{roadmapStats.milestones}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Prev.</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">{roadmapStats.forecastOnly}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Hors annee</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">
                  {roadmapStats.outsideWindow}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
            <span className="font-medium text-slate-600">Evenements</span>
            {[eventTypeMeta.employee_arrival, eventTypeMeta.employee_departure, eventTypeMeta.reinforcement_arrival, eventTypeMeta.reinforcement_departure].map((meta) => (
              <span key={meta.label} className="inline-flex items-center gap-2">
                <span className="inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: meta.color }} />
                {meta.label}
              </span>
            ))}
            <span className="ml-1 font-medium text-slate-600">Iterations</span>
            <span className="inline-flex items-center gap-2">
              <span
                className="inline-flex h-3 w-7 rounded border border-slate-300"
                style={{
                  backgroundColor: "#dbeafe",
                  backgroundImage:
                    "repeating-linear-gradient(45deg, rgba(255,255,255,0.75) 0, rgba(255,255,255,0.75) 6px, rgba(255,255,255,0) 6px, rgba(255,255,255,0) 12px)",
                  backgroundSize: "12px 12px",
                }}
              />
              Prevision
            </span>
            <span className="inline-flex items-center gap-2">
              <span
                className="inline-flex h-3 w-7 rounded border-2 border-dashed border-slate-500"
                style={{
                  backgroundColor: "#dbeafe",
                  backgroundImage:
                    "repeating-linear-gradient(135deg, rgba(15,23,42,0.16) 0, rgba(15,23,42,0.16) 4px, rgba(255,255,255,0) 4px, rgba(255,255,255,0) 10px)",
                  backgroundSize: "10px 10px",
                }}
              />
              Ajout scenario
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="inline-flex h-4 w-7 rounded border-x-2 border-dashed border-slate-700 bg-slate-100" />
              Continue hors annee
            </span>
            <span className="inline-flex items-center gap-2">
              <Flag className="h-3.5 w-3.5 text-rose-600" />
              Jalon objectif
            </span>
          </div>
        </div>

        <div className="h-[72vh] min-h-[620px] overflow-y-auto pr-2">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(220px,280px)_minmax(0,1fr)]">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Projets
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    Selection et alignement des lignes
                  </div>
                </div>
                <div className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600">
                  {roadmapProjectRows.length}
                </div>
              </div>
              <div
                className="mt-4 pr-1 text-sm"
                style={{ paddingTop: roadmapTopOffset + roadmapHeaderOffset }}
              >
                <div className="flex flex-col" style={{ gap: roadmapLaneGap }}>
                  {roadmapProjectRows.map((project) => {
                    const included = !projectExclusions.has(keyOf(project.id));
                    const projectColor = `hsl(${((projectColorMap.get(project.id) ?? 0) * 47) % 360} 70% 45%)`;
                    return (
                      <label
                        key={project.id}
                        className={`group flex cursor-pointer items-start gap-2 rounded-lg border px-2.5 py-3 transition ${
                          included
                            ? "border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50"
                            : "border-slate-100 bg-white/60 text-slate-400"
                        }`}
                        style={{
                          height: project.visualHeight,
                          borderLeftColor: included ? projectColor : "#e2e8f0",
                          borderLeftWidth: 4,
                        }}
                      >
                        <input
                          className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          type="checkbox"
                          checked={included}
                          onChange={(e) => setProjectIncluded(project.id, e.target.checked)}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-flex h-2.5 w-2.5 shrink-0 rounded-full"
                              style={{ backgroundColor: projectColor }}
                            />
                            <span className="truncate text-xs font-semibold">{project.name}</span>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                            {project.project_number && (
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-500">
                                {project.project_number}
                              </span>
                            )}
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-500">
                              {project.iterationCount} iteration
                              {project.iterationCount > 1 ? "s" : ""}
                            </span>
                            {project.milestoneCount > 0 && (
                              <span className="rounded-full bg-rose-50 px-2 py-0.5 text-rose-700">
                                {project.milestoneCount} jalon
                                {project.milestoneCount > 1 ? "s" : ""}
                              </span>
                            )}
                            {project.laneCount > 1 && (
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-700">
                                {project.laneCount} pistes
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                  {!roadmapProjectRows.length && (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-white px-4 py-6 text-sm text-slate-400">
                      Aucun projet
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="min-w-0 overflow-x-auto">
              {roadmapIterations.length ? (
                <div className="min-w-[820px]">
                  <InteractiveTimelineView
                    iterations={roadmapIterations}
                    slots={slots}
                    setIterations={setRoadmapIterations}
                    onIterationUpdate={handleRoadmapIterationUpdate}
                    onIterationReset={resetScenarioIterationOverride}
                    onIterationAdd={openRoadmapCreate}
                    onIterationSelect={openRoadmapEdit}
                    showIterationToggle={false}
                    showSprints={false}
                    showProjectLabel={false}
                    iterationRowHeight={roadmapRowHeight}
                    laneGap={roadmapLaneGap}
                    events={timelineEvents}
                    timelineStartDate={roadmapTimelineBounds.startDate}
                    timelineEndDate={roadmapTimelineBounds.endDate}
                    title={`Roadmap ${year}`}
                    subtitle={`${formatDateShort(roadmapTimelineBounds.startDate)} - ${formatDateShort(roadmapTimelineBounds.endDate)}`}
                    variant="roadmap"
                  />
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-400">
                  Aucun element a afficher pour cette annee.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {activePilotageTab === "scenario" && (
        <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">Capacite totale (h)</div>
          <div className="text-lg font-semibold">{formatHours(totals.capacityHours)}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">Charge planifiee (h)</div>
          <div className="text-lg font-semibold">{formatHours(totals.chargeHours)}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">Charge / capacite</div>
          <div className="text-lg font-semibold">
            {totals.capacityHours > 0
              ? `${Math.round((totals.chargeHours / totals.capacityHours) * 100)}%`
              : "-"}
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">CA produit acquis (EUR)</div>
          <div className="text-lg font-semibold">{formatCurrency(totals.caActualEur)}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="text-xs text-gray-500">CA produit prev. (EUR)</div>
          <div className="text-lg font-semibold">{formatCurrency(totals.caForecastEur)}</div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6 space-y-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Hypotheses globales</h2>
          <p className="mt-1 text-sm text-gray-500">
            La capacite theorique vient des horaires, du taux de prod et des absences. Le facteur
            d'efficacite transforme cette capacite theorique en capacite scenario.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Efficacite scenario (%)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={scenarioConfig.efficiencyPercent ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                updateScenarioConfig({
                  efficiencyPercent: value === "" ? null : Number(value),
                });
              }}
              className="border rounded px-3 py-2 w-full text-lg font-medium"
            />
            <div className="mt-1 text-xs text-slate-500">
              Actif: {formatPercent(statisticsData.totals.scenarioEfficiencyPct)}
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Capacite scenario: {formatHours(totals.capacityHours)} h
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
                <div className="text-xs text-slate-500">Efficacite observee</div>
                <div className="mt-1 font-semibold text-slate-900">
                  {formatOptionalPercent(statisticsData.totals.observedEfficiencyPct)}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  {formatHours(statisticsData.totals.elapsedPlannedCharge)} h budgetees /
                  {" "}{formatHours(statisticsData.totals.nominalProductiveToDate)} h nominales
                </div>
                <button
                  type="button"
                  disabled={!Number.isFinite(statisticsData.totals.observedEfficiencyPct)}
                  onClick={() =>
                    updateScenarioConfig({
                      efficiencyPercent: Math.round(statisticsData.totals.observedEfficiencyPct),
                    })
                  }
                  className="mt-2 rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 transition hover:border-blue-200 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Utiliser
                </button>
              </div>
              <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
                <div className="text-xs text-slate-500">Reference nominale</div>
                <div className="mt-1 font-semibold text-slate-900">
                  {formatPercent(DEFAULT_SCENARIO_EFFICIENCY_PERCENT)}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  Capacite theorique sans correction d'efficacite.
                </div>
                <button
                  type="button"
                  onClick={() =>
                    updateScenarioConfig({
                      efficiencyPercent: DEFAULT_SCENARIO_EFFICIENCY_PERCENT,
                    })
                  }
                  className="mt-2 rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 transition hover:border-blue-200 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Reinitialiser
                </button>
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Cette hypothese ajuste uniquement la capacite en heures, pas les budgets des
              iterations.
            </div>
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          <div className="font-medium text-slate-900">
            Factures factorisees: {(scenarioConfig.factoredInvoiceIds || []).length}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Le choix se fait facture par facture dans l'onglet Factures.
          </div>
        </div>
      </div>

      <details className="bg-white shadow rounded-lg p-6">
        <summary className="cursor-pointer">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-lg font-medium text-gray-900">Editeur detaille</div>
              <div className="text-sm text-gray-500">
                Overrides de disponibilite, renforts et absences.
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {scenarioStats.overrides} overrides / {scenarioStats.additions} renforts /{" "}
              {scenarioStats.timeOffs} absences
            </div>
          </div>
        </summary>
        <div className="mt-6 space-y-6">
          <section>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-base font-medium text-gray-900">Disponibilite par employe</h3>
                <p className="text-sm text-gray-500">
                  Vide = 100% par defaut, puis le taux de prod employe est applique.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActiveModal("availability")}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Ouvrir en grand
              </button>
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Employe</th>
                    <th className="px-3 py-2 text-left">Arrivee</th>
                    <th className="px-3 py-2 text-left">Depart</th>
                    <th className="px-3 py-2 text-left">Dispo (%)</th>
                    <th className="px-3 py-2 text-left">Debut</th>
                    <th className="px-3 py-2 text-left">Fin</th>
                    <th className="px-3 py-2 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((employee) => {
                    const override = (scenarioConfig.employeeOverrides || []).find(
                      (item) => item.employee_id === employee.id
                    );
                    return (
                      <tr key={employee.id} className="border-t border-slate-100">
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">{employee.name}</div>
                          <div className="text-xs text-gray-500">
                            Taux de prod: {employee.production_rate ?? 100}%
                          </div>
                        </td>
                        <td className="px-3 py-2">{employee.arrival_date || "-"}</td>
                        <td className="px-3 py-2">{employee.departure_date || "-"}</td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={override?.availability_percent ?? ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              const numeric = value === "" ? null : Number(value);
                              setEmployeeOverride(employee.id, { availability_percent: numeric });
                            }}
                            className="border rounded px-2 py-1 w-24"
                            placeholder="Auto"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="date"
                            value={override?.start_date || ""}
                            onChange={(e) =>
                              setEmployeeOverride(employee.id, {
                                start_date: e.target.value || null,
                              })
                            }
                            disabled={!override}
                            className="border rounded px-2 py-1 disabled:bg-slate-50"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="date"
                            value={override?.end_date || ""}
                            onChange={(e) =>
                              setEmployeeOverride(employee.id, {
                                end_date: e.target.value || null,
                              })
                            }
                            disabled={!override}
                            className="border rounded px-2 py-1 disabled:bg-slate-50"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => setEmployeeOverride(employee.id, null)}
                            className="text-xs text-gray-500 hover:text-gray-800"
                          >
                            Reset
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {!employees.length && (
                    <tr>
                      <td colSpan={7} className="px-3 py-4 text-gray-400 text-center">
                        Aucun employe
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-base font-medium text-gray-900">Renforts scenario</h3>
                <p className="text-sm text-gray-500">
                  Renforts temporaires avec leur propre capacite.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setActiveModal("reinforcements")}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Ouvrir en grand
                </button>
                <button
                  type="button"
                  onClick={addEmployeeAddition}
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Ajouter
                </button>
              </div>
            </div>
            <div className="space-y-3">
              {(scenarioConfig.employeeAdditions || []).map((item, index) => (
                <div
                  key={item.local_id || index}
                  className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3"
                >
                  <input
                    type="text"
                    value={item.name || ""}
                    onChange={(e) => updateEmployeeAddition(index, { name: e.target.value })}
                    placeholder="Nom renfort"
                    className="border rounded px-2 py-1"
                  />
                  <input
                    type="date"
                    value={item.start_date || ""}
                    onChange={(e) => updateEmployeeAddition(index, { start_date: e.target.value })}
                    className="border rounded px-2 py-1"
                  />
                  <input
                    type="date"
                    value={item.end_date || ""}
                    onChange={(e) => updateEmployeeAddition(index, { end_date: e.target.value })}
                    className="border rounded px-2 py-1"
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={item.availability_percent ?? ""}
                    onChange={(e) =>
                      updateEmployeeAddition(index, {
                        availability_percent: Number(e.target.value || 0),
                      })
                    }
                    placeholder="% dispo"
                    className="border rounded px-2 py-1"
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={item.production_rate ?? 100}
                    onChange={(e) =>
                      updateEmployeeAddition(index, {
                        production_rate: Number(e.target.value || 0),
                      })
                    }
                    placeholder="% prod"
                    className="border rounded px-2 py-1"
                  />
                  <input
                    type="number"
                    min="1"
                    max="80"
                    step="0.5"
                    value={item.weekly_hours ?? DEFAULT_EMPLOYEE_WEEKLY_HOURS}
                    onChange={(e) =>
                      updateEmployeeAddition(index, {
                        weekly_hours: Number(
                          e.target.value || DEFAULT_EMPLOYEE_WEEKLY_HOURS
                        ),
                      })
                    }
                    placeholder="h / semaine"
                    className="border rounded px-2 py-1"
                  />
                  <button
                    type="button"
                    onClick={() => removeEmployeeAddition(index)}
                    className="text-sm text-gray-500 hover:text-gray-800"
                  >
                    Supprimer
                  </button>
                </div>
              ))}
              {!scenarioConfig.employeeAdditions?.length && (
                <div className="text-sm text-gray-400">Aucun renfort ajoute</div>
              )}
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-base font-medium text-gray-900">Conges et indisponibilites</h3>
                <p className="text-sm text-gray-500">
                  Jours retires du calcul de capacite.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setActiveModal("timeOffs")}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Ouvrir en grand
                </button>
                <button
                  type="button"
                  onClick={addTimeOff}
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Ajouter
                </button>
              </div>
            </div>
            <div className="space-y-3">
              {(scenarioConfig.employeeTimeOffs || []).map((item, index) => (
                <div
                  key={item.local_id || index}
                  className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3"
                >
                  <select
                    value={item.employee_id || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      updateTimeOff(index, { employee_id: value ? Number(value) : null });
                    }}
                    className="border rounded px-2 py-1"
                  >
                    <option value="">Employe</option>
                    {employees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="date"
                    value={item.start_date || ""}
                    onChange={(e) => updateTimeOff(index, { start_date: e.target.value })}
                    className="border rounded px-2 py-1"
                  />
                  <input
                    type="date"
                    value={item.end_date || ""}
                    onChange={(e) => updateTimeOff(index, { end_date: e.target.value })}
                    className="border rounded px-2 py-1"
                  />
                  <input
                    type="text"
                    value={item.label || ""}
                    onChange={(e) => updateTimeOff(index, { label: e.target.value })}
                    placeholder="Note"
                    className="border rounded px-2 py-1"
                  />
                  <button
                    type="button"
                    onClick={() => removeTimeOff(index)}
                    className="text-sm text-gray-500 hover:text-gray-800"
                  >
                    Supprimer
                  </button>
                </div>
              ))}
              {!scenarioConfig.employeeTimeOffs?.length && (
                <div className="text-sm text-gray-400">Aucun conge defini</div>
              )}
            </div>
          </section>
        </div>
      </details>
        </>
      )}

      {activePilotageTab === "accounting" && (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Vue comptable mensuelle</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Mois</th>
                <th className="px-3 py-2 text-right">CA produit</th>
                <th className="px-3 py-2 text-right">Depenses prev.</th>
                <th className="px-3 py-2 text-right">Encaissements</th>
                <th className="px-3 py-2 text-right">Decaissements</th>
                <th className="px-3 py-2 text-right">Net treso</th>
              </tr>
            </thead>
            <tbody>
              {metricsByMonth.map((month, index) => {
                const cashMonth = cashFlowByMonth[index] || { inflow: 0, outflow: 0, net: 0 };
                return (
                  <tr key={month.key} className="border-b">
                    <td className="px-3 py-2 text-left font-medium text-gray-900">{month.label}</td>
                    <td className="px-3 py-2 text-right">
                      {formatCurrency(month.caActualEur + month.caForecastEur)}
                    </td>
                    <td className="px-3 py-2 text-right">{formatCurrency(month.expensesForecastEur)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(cashMonth.inflow)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(cashMonth.outflow)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(cashMonth.net)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td className="px-3 py-2 font-semibold text-gray-900">Total</td>
                <td className="px-3 py-2 text-right font-semibold">
                  {formatCurrency(totals.caActualEur + totals.caForecastEur)}
                </td>
                <td className="px-3 py-2 text-right font-semibold">
                  {formatCurrency(totals.expensesForecastEur)}
                </td>
                <td className="px-3 py-2 text-right font-semibold">{formatCurrency(cashFlowTotals.inflow)}</td>
                <td className="px-3 py-2 text-right font-semibold">{formatCurrency(cashFlowTotals.outflow)}</td>
                <td className="px-3 py-2 text-right font-semibold">{formatCurrency(cashFlowTotals.net)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="mt-6 space-y-4 border-t border-gray-100 pt-6">
          <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-base font-medium text-gray-900">
                Couverture factures / commandes vs projets
              </h3>
              <p className="text-sm text-gray-500">
                Compare le CA de production annuel aux factures et commandes rattachees a des projets.
              </p>
            </div>
            <div className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600">
              {formatPercent(accountingCoverage.totals.coveragePct)} du CA couvert par des factures rattachees
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
              <div className="text-xs text-slate-500">CA production annuel</div>
              <div className="mt-1 text-lg font-semibold text-slate-950">
                {formatCurrency(accountingCoverage.totals.production)}
              </div>
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
              <div className="text-xs text-slate-500">Factures / commandes</div>
              <div className="mt-1 text-lg font-semibold text-slate-950">
                {formatCurrency(accountingCoverage.totals.invoices)}
              </div>
            </div>
            <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3">
              <div className="text-xs text-emerald-700">Rattache projets</div>
              <div className="mt-1 text-lg font-semibold text-emerald-800">
                {formatCurrency(accountingCoverage.totals.linkedInvoices)}
              </div>
              <div className="mt-1 text-xs text-emerald-700">
                {formatPercent(accountingCoverage.totals.linkedPct)} des factures
              </div>
            </div>
            <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3">
              <div className="text-xs text-amber-700">Non rattache</div>
              <div className="mt-1 text-lg font-semibold text-amber-800">
                {formatCurrency(accountingCoverage.totals.unlinkedInvoices)}
              </div>
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
              <div className="text-xs text-slate-500">Ecart CA vs factures</div>
              <div
                className={`mt-1 text-lg font-semibold ${
                  accountingCoverage.totals.gapVsInvoices > 0
                    ? "text-amber-700"
                    : accountingCoverage.totals.gapVsInvoices < 0
                    ? "text-rose-700"
                    : "text-emerald-700"
                }`}
              >
                {formatCurrency(accountingCoverage.totals.gapVsInvoices)}
              </div>
            </div>
          </div>
          <div className="grid gap-4 xl:grid-cols-[1.35fr_0.65fr]">
            <div className="rounded-lg border border-slate-100 bg-white">
              <div className="border-b border-slate-100 px-4 py-3">
                <div className="text-xs font-semibold uppercase text-gray-500">
                  Ecarts par projet
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Projet</th>
                      <th className="px-3 py-2 text-right">CA prod.</th>
                      <th className="px-3 py-2 text-right">Factures liees</th>
                      <th className="px-3 py-2 text-right">Ecart</th>
                      <th className="px-3 py-2 text-left">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accountingCoverage.rows.slice(0, 14).map((row) => {
                      const gapAbs = Math.abs(row.gap);
                      const statusLabel =
                        gapAbs < 1
                          ? "OK"
                          : row.gap > 0
                          ? "A facturer / rattacher"
                          : "Surfacturation ou mauvais rattachement";
                      return (
                        <tr key={row.id} className="border-b border-slate-100">
                          <td className="px-3 py-2">
                            <div className="flex min-w-0 items-center gap-2">
                              <span
                                className="h-2.5 w-2.5 shrink-0 rounded-full"
                                style={{ backgroundColor: row.color }}
                              />
                              <span className="truncate font-medium text-slate-900" title={row.name}>
                                {row.name}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">{formatCurrency(row.production)}</td>
                          <td className="px-3 py-2 text-right">
                            <div className="font-medium text-slate-900">{formatCurrency(row.invoices)}</div>
                            <div className="text-xs text-slate-400">
                              {formatCurrency(row.invoiced)} facture - {formatCurrency(row.planned)} prevu
                            </div>
                          </td>
                          <td
                            className={`px-3 py-2 text-right font-semibold ${
                              row.gap > 0 ? "text-amber-700" : row.gap < 0 ? "text-rose-700" : "text-emerald-700"
                            }`}
                          >
                            {formatCurrency(row.gap)}
                          </td>
                          <td className="px-3 py-2 text-xs text-slate-600">{statusLabel}</td>
                        </tr>
                      );
                    })}
                    {!accountingCoverage.rows.length && (
                      <tr>
                        <td colSpan={5} className="px-3 py-6 text-center text-sm text-slate-400">
                          Aucun projet ou facture sur l'annee.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="rounded-lg border border-amber-100 bg-amber-50 p-4">
              <div className="text-xs font-semibold uppercase text-amber-700">
                Factures non rattachees
              </div>
              <div className="mt-3 space-y-2">
                {accountingCoverage.unlinkedEntries.slice(0, 8).map((entry) => (
                  <div key={entry.id} className="rounded-md border border-amber-100 bg-white px-3 py-2 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-slate-900" title={entry.label}>
                          {entry.label}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-slate-500" title={entry.subtitle}>
                          {entry.subtitle || "Commande non rattachee"}
                        </div>
                      </div>
                      <div className="shrink-0 font-semibold text-amber-800">
                        {formatCurrency(entry.amount)}
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-slate-500">
                      <span className="rounded border border-slate-200 bg-white px-2 py-0.5">
                        {formatDateShort(entry.date)}
                      </span>
                      <span className="rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700">
                        {entry.status}
                      </span>
                    </div>
                  </div>
                ))}
                {!accountingCoverage.unlinkedEntries.length && (
                  <div className="rounded-md border border-dashed border-amber-200 bg-white/70 px-3 py-6 text-center text-sm text-amber-700">
                    Toutes les factures de l'annee sont rattachees a un projet.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      <Modal
        open={activeModal === "availability"}
        title="Disponibilite par employe"
        onClose={() => setActiveModal(null)}
        maxWidth="max-w-5xl"
      >
        <div className="text-sm text-gray-500 mb-4">
          Ajustez les disponibilites individuelles pour un intervalle donne.
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Employe</th>
                <th className="px-3 py-2 text-left">Arrivee</th>
                <th className="px-3 py-2 text-left">Depart</th>
                <th className="px-3 py-2 text-left">Disponibilite (%)</th>
                <th className="px-3 py-2 text-left">Debut</th>
                <th className="px-3 py-2 text-left">Fin</th>
                <th className="px-3 py-2 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((employee) => {
                const override = (scenarioConfig.employeeOverrides || []).find(
                  (item) => item.employee_id === employee.id
                );
                return (
                  <tr key={employee.id} className="border-b">
                    <td className="px-3 py-2">{employee.name}</td>
                    <td className="px-3 py-2">{employee.arrival_date || "-"}</td>
                    <td className="px-3 py-2">{employee.departure_date || "-"}</td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={override?.availability_percent ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          const numeric = value === "" ? null : Number(value);
                          setEmployeeOverride(employee.id, { availability_percent: numeric });
                        }}
                        className="border rounded px-2 py-1 w-24"
                        placeholder="Auto"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="date"
                        value={override?.start_date || ""}
                        onChange={(e) =>
                          setEmployeeOverride(employee.id, {
                            start_date: e.target.value || null,
                          })
                        }
                        disabled={!override}
                        className="border rounded px-2 py-1"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="date"
                        value={override?.end_date || ""}
                        onChange={(e) =>
                          setEmployeeOverride(employee.id, {
                            end_date: e.target.value || null,
                          })
                        }
                        disabled={!override}
                        className="border rounded px-2 py-1"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setEmployeeOverride(employee.id, null)}
                        className="text-xs text-gray-500 hover:text-gray-800"
                      >
                        Reset
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!employees.length && (
                <tr>
                  <td colSpan={7} className="px-3 py-2 text-gray-400">
                    Aucun employe
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Modal>

      <Modal
        open={activeModal === "reinforcements"}
        title="Renforts scenario"
        onClose={() => setActiveModal(null)}
        maxWidth="max-w-5xl"
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-gray-500">
            Ajoutez des renforts temporaires et leur disponibilite.
          </p>
          <button
            onClick={addEmployeeAddition}
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
          >
            <Plus className="h-4 w-4 mr-1" />
            Ajouter
          </button>
        </div>
        <div className="space-y-3">
          {(scenarioConfig.employeeAdditions || []).map((item, index) => (
            <div key={item.local_id || index} className="grid grid-cols-1 md:grid-cols-7 gap-3">
              <input
                type="text"
                value={item.name || ""}
                onChange={(e) => updateEmployeeAddition(index, { name: e.target.value })}
                placeholder="Nom renfort"
                className="border rounded px-2 py-1"
              />
              <input
                type="date"
                value={item.start_date || ""}
                onChange={(e) => updateEmployeeAddition(index, { start_date: e.target.value })}
                className="border rounded px-2 py-1"
              />
              <input
                type="date"
                value={item.end_date || ""}
                onChange={(e) => updateEmployeeAddition(index, { end_date: e.target.value })}
                className="border rounded px-2 py-1"
              />
              <input
                type="number"
                min="0"
                max="100"
                value={item.availability_percent ?? ""}
                onChange={(e) =>
                  updateEmployeeAddition(index, { availability_percent: Number(e.target.value || 0) })
                }
                placeholder="% dispo"
                className="border rounded px-2 py-1"
              />
              <input
                type="number"
                min="0"
                max="100"
                value={item.production_rate ?? 100}
                onChange={(e) =>
                  updateEmployeeAddition(index, { production_rate: Number(e.target.value || 0) })
                }
                placeholder="% prod"
                className="border rounded px-2 py-1"
              />
              <input
                type="number"
                min="1"
                max="80"
                step="0.5"
                value={item.weekly_hours ?? DEFAULT_EMPLOYEE_WEEKLY_HOURS}
                onChange={(e) =>
                  updateEmployeeAddition(index, { weekly_hours: Number(e.target.value || DEFAULT_EMPLOYEE_WEEKLY_HOURS) })
                }
                placeholder="h / semaine"
                className="border rounded px-2 py-1"
              />
              <button
                onClick={() => removeEmployeeAddition(index)}
                className="text-sm text-gray-500 hover:text-gray-800"
              >
                Supprimer
              </button>
            </div>
          ))}
          {!scenarioConfig.employeeAdditions?.length && (
            <div className="text-sm text-gray-400">Aucun renfort ajoute</div>
          )}
        </div>
      </Modal>

      <Modal
        open={activeModal === "timeOffs"}
        title="Conges et indisponibilites"
        onClose={() => setActiveModal(null)}
        maxWidth="max-w-4xl"
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-gray-500">
            Planifiez les absences ou conges a integrer au scenario.
          </p>
          <button
            onClick={addTimeOff}
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
          >
            <Plus className="h-4 w-4 mr-1" />
            Ajouter
          </button>
        </div>
        <div className="space-y-3">
          {(scenarioConfig.employeeTimeOffs || []).map((item, index) => (
            <div key={item.local_id || index} className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <select
                value={item.employee_id || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  updateTimeOff(index, { employee_id: value ? Number(value) : null });
                }}
                className="border rounded px-2 py-1"
              >
                <option value="">Employe</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={item.start_date || ""}
                onChange={(e) => updateTimeOff(index, { start_date: e.target.value })}
                className="border rounded px-2 py-1"
              />
              <input
                type="date"
                value={item.end_date || ""}
                onChange={(e) => updateTimeOff(index, { end_date: e.target.value })}
                className="border rounded px-2 py-1"
              />
              <input
                type="text"
                value={item.label || ""}
                onChange={(e) => updateTimeOff(index, { label: e.target.value })}
                placeholder="Note"
                className="border rounded px-2 py-1"
              />
              <button
                onClick={() => removeTimeOff(index)}
                className="text-sm text-gray-500 hover:text-gray-800"
              >
                Supprimer
              </button>
            </div>
          ))}
          {!scenarioConfig.employeeTimeOffs?.length && (
            <div className="text-sm text-gray-400">Aucun conge defini</div>
          )}
        </div>
      </Modal>

      <Modal
        open={activeModal === "projects"}
        title="Selection des projets"
        onClose={() => setActiveModal(null)}
        maxWidth="max-w-4xl"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-500">
            Incluez ou excluez les projets pris en compte dans la timeline.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={includeAllProjects}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Tout inclure
            </button>
            <button
              onClick={excludeAllProjects}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Tout exclure
            </button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {availableProjects.map((project) => {
            const included = !projectExclusions.has(keyOf(project.id));
            return (
              <label
                key={project.id}
                className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm text-gray-700"
              >
                <input
                  type="checkbox"
                  checked={included}
                  onChange={(e) => setProjectIncluded(project.id, e.target.checked)}
                />
                <span>{formatProjectOptionLabel(project)}</span>
              </label>
            );
          })}
          {!availableProjects.length && <div className="text-sm text-gray-400">Aucun projet</div>}
        </div>
      </Modal>

      <Modal
        open={roadmapEditorOpen}
        title={roadmapEditorTitle}
        onClose={() => setRoadmapEditorOpen(false)}
        maxWidth="max-w-3xl"
      >
        <div className="space-y-4">
          {roadmapDraft.source === "addition" && (
            <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1 text-xs font-medium">
              <button
                type="button"
                onClick={() =>
                  setRoadmapDraft((prev) => ({
                    ...prev,
                    item_type: "iteration",
                    end_date: prev.end_date || prev.start_date,
                  }))
                }
                className={`rounded-full px-3 py-1 ${
                  !isRoadmapMilestoneDraft
                    ? "bg-blue-600 text-white"
                    : "text-slate-600 hover:bg-white"
                }`}
              >
                Iteration
              </button>
              <button
                type="button"
                onClick={() =>
                  setRoadmapDraft((prev) => ({
                    ...prev,
                    item_type: "milestone",
                    project_mode: "existing",
                    new_project_name: "",
                    new_project_number: "",
                    end_date: prev.start_date,
                    forecast_ca_eur: "",
                    forecast_purchase_eur: "",
                    forecast_time_hours: "",
                  }))
                }
                className={`rounded-full px-3 py-1 ${
                  isRoadmapMilestoneDraft
                    ? "bg-blue-600 text-white"
                    : "text-slate-600 hover:bg-white"
                }`}
              >
                Jalon
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              {roadmapDraft.source === "addition" && !isRoadmapMilestoneDraft && (
                <div className="mb-2 flex items-center gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() =>
                      setRoadmapDraft((prev) => ({
                        ...prev,
                        project_mode: "existing",
                        new_project_name: "",
                        new_project_number: "",
                      }))
                    }
                    className={`rounded-full px-3 py-1 ${
                      roadmapDraft.project_mode === "existing"
                        ? "bg-blue-600 text-white"
                        : "border border-gray-200 text-gray-600"
                    }`}
                  >
                    Projet existant
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setRoadmapDraft((prev) => ({
                        ...prev,
                        project_mode: "new",
                        project_id: "",
                      }))
                    }
                    className={`rounded-full px-3 py-1 ${
                      roadmapDraft.project_mode === "new"
                        ? "bg-blue-600 text-white"
                        : "border border-gray-200 text-gray-600"
                    }`}
                  >
                    Nouveau projet
                  </button>
                </div>
              )}
              <label className="block text-xs text-gray-500">Projet</label>
              {roadmapDraft.source === "addition" && !isRoadmapMilestoneDraft && roadmapDraft.project_mode === "new" ? (
                <div className="space-y-3 rounded border border-dashed border-gray-200 p-3">
                  <div>
                    <label className="block text-xs text-gray-500">Nom du projet</label>
                    <input
                      type="text"
                      value={roadmapDraft.new_project_name || ""}
                      onChange={(e) =>
                        setRoadmapDraft((prev) => ({ ...prev, new_project_name: e.target.value }))
                      }
                      className="border rounded px-2 py-1 w-full"
                      placeholder="Nom du projet"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500">Numero du projet</label>
                    <input
                      type="text"
                      value={roadmapDraft.new_project_number || ""}
                      onChange={(e) =>
                        setRoadmapDraft((prev) => ({
                          ...prev,
                          new_project_number: e.target.value,
                        }))
                      }
                      className="border rounded px-2 py-1 w-full"
                      placeholder="Optionnel"
                    />
                  </div>
                </div>
              ) : (
                <select
                  value={roadmapDraft.project_id || ""}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    const selectedProject = availableProjects.find(
                      (project) => String(project.id) === String(selectedValue)
                    );
                    setRoadmapDraft((prev) => ({
                      ...prev,
                      project_id: selectedProject ? selectedProject.id : "",
                    }));
                  }}
                  disabled={roadmapDraft.source !== "addition"}
                  className="border rounded px-2 py-1 w-full"
                >
                  <option value="">Selectionner</option>
                  {availableProjects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {formatProjectOptionLabel(project)}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-xs text-gray-500">
                {isRoadmapMilestoneDraft ? "Nom du jalon" : "Nom"}
              </label>
              <input
                type="text"
                value={roadmapDraft.name || ""}
                onChange={(e) => setRoadmapDraft((prev) => ({ ...prev, name: e.target.value }))}
                disabled={roadmapDraft.source !== "addition" && roadmapDraft.source !== "milestone"}
                className="border rounded px-2 py-1 w-full"
                placeholder={isRoadmapMilestoneDraft ? "Nom du jalon" : "Nom de l'iteration"}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500">
                {isRoadmapMilestoneDraft ? "Date" : "Debut"}
              </label>
              <input
                type="date"
                value={roadmapDraft.start_date || ""}
                onChange={(e) =>
                  setRoadmapDraft((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                    ...(isRoadmapMilestoneDraft ? { end_date: e.target.value } : {}),
                  }))
                }
                className="border rounded px-2 py-1 w-full"
              />
            </div>
            {!isRoadmapMilestoneDraft && (
              <>
                <div>
                  <label className="block text-xs text-gray-500">Fin</label>
                  <input
                    type="date"
                    value={roadmapDraft.end_date || ""}
                    onChange={(e) => setRoadmapDraft((prev) => ({ ...prev, end_date: e.target.value }))}
                    className="border rounded px-2 py-1 w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">CA estime (EUR)</label>
                  <input
                    type="number"
                    min="0"
                    value={roadmapDraft.forecast_ca_eur ?? ""}
                    onChange={(e) =>
                      setRoadmapDraft((prev) => ({ ...prev, forecast_ca_eur: e.target.value }))
                    }
                    className="border rounded px-2 py-1 w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">Depense estimee (EUR)</label>
                  <input
                    type="number"
                    min="0"
                    value={roadmapDraft.forecast_purchase_eur ?? ""}
                    onChange={(e) =>
                      setRoadmapDraft((prev) => ({ ...prev, forecast_purchase_eur: e.target.value }))
                    }
                    className="border rounded px-2 py-1 w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">Charge prev. (h)</label>
                  <input
                    type="number"
                    min="0"
                    value={roadmapDraft.forecast_time_hours ?? ""}
                    onChange={(e) =>
                      setRoadmapDraft((prev) => ({ ...prev, forecast_time_hours: e.target.value }))
                    }
                    className="border rounded px-2 py-1 w-full"
                  />
                </div>
              </>
            )}
          </div>
          {roadmapDraftError && (
            <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-600">
              {roadmapDraftError}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3">
            {roadmapDraft.source === "milestone" && roadmapDraft.objective_id ? (
              <button
                type="button"
                onClick={deleteRoadmapMilestone}
                className="text-sm text-rose-600 hover:text-rose-700"
              >
                Supprimer le jalon
              </button>
            ) : roadmapDraft.source === "addition" && roadmapDraft.id && !isRoadmapMilestoneDraft ? (
              <button
                type="button"
                onClick={() => {
                  removeScenarioIterationAddition(roadmapDraft.id);
                  setRoadmapEditorOpen(false);
                }}
                className="text-sm text-rose-600 hover:text-rose-700"
              >
                Supprimer l'iteration
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setRoadmapEditorOpen(false)}
                className="rounded border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={saveRoadmapDraft}
                className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                {isRoadmapMilestoneDraft ? "Enregistrer le jalon" : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {isDirty && (
        <div className="fixed bottom-4 left-4 right-4 z-40 sm:left-auto sm:right-6 sm:max-w-xl">
          <div className="ml-auto flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-amber-800">Modifications non sauvegardees</div>
              <div className="text-xs text-amber-700">
                Pense a enregistrer avant de quitter cette page.
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button
                onClick={undoScenarioChange}
                disabled={!canUndo}
                className={`inline-flex items-center rounded-full border px-3 py-1.5 text-sm ${
                  canUndo
                    ? "border-gray-300 text-gray-600 hover:bg-gray-100"
                    : "cursor-not-allowed border-gray-200 text-gray-400"
                }`}
              >
                <Undo2 className="mr-2 h-4 w-4" />
                Undo
              </button>
              <button
                onClick={resetEditorToSavedState}
                className="inline-flex items-center rounded-full border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Reset
              </button>
              <button
                onClick={saveScenario}
                className="inline-flex items-center rounded-full bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
              >
                <Save className="mr-2 h-4 w-4" />
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PilotagePage;
